// Custom hook for external recording functionality
import { useState, useRef, useEffect } from "react";
import recordGif from "../../../assets/recordGif.gif";
import premiumRecordingGif from "../../../assets/premium_3.gif";
import { getLabel } from "../../../config/roles";
import {
  BACKUP_INTERVAL_MS as DEFAULT_BACKUP_INTERVAL_MS,
  RECORDING_CHUNK_TIMESLICE,
  MAX_AUDIO_UPLOAD_BYTES,
} from "../Constants";
import {
  BASE_URLS,
  supabaseServiceKey as envSupabaseServiceKey,
  supabaseUrl as envSupabaseUrl,
  tableName as envTableName,
  auto_copilot,
} from "../../../components/env";
import { supabaseSchema } from "../../../components/supabaseClient";
import { tableForPatientId } from "../../medstudent/chartSource";
import {
  normalizeSchemaName,
  recorderSchemaCandidates,
  recorderStateRow,
  resolveRecorderSchema,
} from "../../../utils/recorderSchema";
import {
  getClinicalResultsTabButtonsHtml,
  getClinicalResultsTabPanelsHtml,
} from "../components/ExternalRecordingPage/clinicalResultsTabsMarkup";
import { copilotUpgradeBannerHtml } from "../../../utils/copilotUpgrade";
import { OPEN_HELP_DESK_POST_MESSAGE_TYPE } from "../../../utils/helpDesk";
import {
  VOSK_SERVICE_ORIGIN,
  VOSK_JS_URL,
  VOSK_MANIFEST_URL,
  voskModelUrl,
  fetchVoskManifest,
} from "../../../services/vosk/config.js";

export const useExternalRecording = ({
  state,
  refId,
  patientId,
  patientName,
  ehrId,
  liveTranscription = false,
  liveFactExtraction = false,
  userRole,
  preferredMicDeviceId = "",
  aiRecapDefault = true,
  copilotEnabled = false,
  audioBackupIntervalMs = DEFAULT_BACKUP_INTERVAL_MS,
  supabaseServiceKey = null,
  /** Project API URL (https://xxx.supabase.co). Required for direct upload in about:blank popup. */
  supabaseUrl: supabaseUrlProp = null,
  audioBucketName = "audio",
  tableName = envTableName || "audio_notes",
  dbSchemaName = "public",
  /**
   * Schema the OPEN CHART writes everything else with (MedStudentClinicPage's
   * `chartDocSchema`). Outranks every other source — it is the row's `_schema`
   * after the provider allow-list gate, so a revoked link still collapses to
   * the viewer's home schema.
   */
  chartSchema = null,
  audioNumber = 1,
  userId = "",
  userJwtToken = "",
}) => {
  const [recordingState, setRecordingState] = useState("prompt");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timer, setTimer] = useState({ minutes: 0, seconds: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [liveTranscriptionEnabled, setLiveTranscriptionEnabled] =
    useState(liveTranscription);
  const [includeSystemAudioEnabled, setIncludeSystemAudioEnabled] = useState(
    () => {
      const saved = localStorage.getItem("includeSystemAudio");
      return saved !== null ? JSON.parse(saved) : false;
    },
  );

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const generateExternalHTML = () => {
    const resolvedUserRole = userRole || state?.role || "";
    const scheduledDate = state?.date_ || "";
    const saveTargetName = getLabel(resolvedUserRole, 'saveTarget');
    const injectedMicDeviceId = preferredMicDeviceId || "";
    const injectedAiRecapDefault = aiRecapDefault ? "true" : "false";
    const roleTokens = String(resolvedUserRole || "")
      .toLowerCase()
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean);
    const injectedCopilotEnabled = copilotEnabled ? "true" : "false";
    const hideResultsTab = injectedCopilotEnabled !== "true";
    const isPremiumCopilotMode = injectedCopilotEnabled === "true";
    const copilotUpgradeBannerMarkup = copilotUpgradeBannerHtml();
    const recorderGifSrc = isPremiumCopilotMode
      ? premiumRecordingGif
      : recordGif;
    const premiumDarkModeClass = isPremiumCopilotMode
      ? "premium-dark-mode"
      : "";
    console.log("🎤 [COPILOT-DEBUG] generateExternalHTML flags:", {
      userRole,
      roleTokens,
      copilotEnabledProp: !!copilotEnabled,
      injectedCopilotEnabled,
    });
    const injectedEhrId = ehrId || "";
    const clampedBackupIntervalMs = Math.round(
      Math.min(
        600000,
        Math.max(
          5000,
          Number(audioBackupIntervalMs) || DEFAULT_BACKUP_INTERVAL_MS,
        ),
      ),
    );
    const injectedRecorderTimesliceMs = Math.round(
      Math.min(60000, Math.max(250, Number(RECORDING_CHUNK_TIMESLICE) || 2000)),
    );

    /** Injected popup hard cap matches main app AudioRecorder/constants. */
    const injectedMaxAudioUploadBytes = MAX_AUDIO_UPLOAD_BYTES;
    const injectedAutoSaveMarginBytes = Math.min(
      512 * 1024 * 1024,
      Math.floor(injectedMaxAudioUploadBytes * 0.02),
    );
    const injectedMaxGbForLabel =
      injectedMaxAudioUploadBytes / (1024 * 1024 * 1024);
    const injectedMaxUploadLabelText =
      injectedMaxGbForLabel % 1 === 0
        ? `${Math.floor(injectedMaxGbForLabel)} GB`
        : `${injectedMaxGbForLabel.toFixed(1)} GB`;

    // Store patient info in global variables so embedded JavaScript can access it
    // Use unique names to avoid conflicts
    const recordingPatientId = patientId || "";
    const recordingPatientName = patientName || "";
    const recordingUserRole = resolvedUserRole || "";
    const injectedScheduledDate = scheduledDate || "";
    const recordingAudioNumber = audioNumber || 1;
    // Roster charts: the popup reads/writes more_audios + transcripts on the
    // CURRENT patient's row via raw REST, so the injected table must resolve
    // per patient id — patients_with_ehrid for registered roster ids, the
    // caller's/env table for everything else (byte-identical for visits).
    const rosterTable =
      recordingPatientId &&
      tableForPatientId(recordingPatientId) === "patients_with_ehrid"
        ? "patients_with_ehrid"
        : null;
    const injectedTableName = (rosterTable || tableName || envTableName || "audio_notes")
      .replace(/\\/g, "\\\\")
      .replace(/'/g, "\\'");
    // Determine the effective schema — critical for multi-schema environments.
    //
    // The popup PATCHes `?id=eq.<uuid>` and uploads into
    // `{schema}/{year}/{date_}/`, so this must be the schema the OPEN CHART ROW
    // lives in, NOT the viewer's home schema. Those differ whenever a chart is
    // opened through the cross-schema Providers dropdown; sending the write to
    // the home schema matched zero rows there and the save failed outright with
    // "Patient record not found". See utils/recorderSchema for the full order.
    const rawDbSchemaName = resolveRecorderSchema({
      chartSchema,
      state,
      dbSchemaName,
      homeSchema: supabaseSchema,
    });
    const injectedSchemaName = rawDbSchemaName
      ? String(rawDbSchemaName).replace(/\\/g, "\\\\").replace(/'/g, "\\'")
      : "";
    const effectiveSchema = injectedSchemaName;
    // Where else the row may live — the popup looks the id up before it
    // writes (see locateRowSchema in the script). JSON, with `<` escaped so a
    // value can never close the <script> tag.
    const injectedSchemaCandidatesJson = JSON.stringify(
      recorderSchemaCandidates({
        chartSchema,
        state,
        dbSchemaName,
        homeSchema: supabaseSchema,
      }),
    ).replace(/</g, "\\u003c");

    // Nothing resolved: the popup will fall back to "public". That is legitimate
    // for a genuine public-schema user and a real problem for anyone else, so
    // shout only when a caller did hand us a tenant schema we failed to use.
    const wasExplicitDbSchemaProvided =
      normalizeSchemaName(chartSchema) || normalizeSchemaName(dbSchemaName);
    const schemaSources = {
      chartSchema,
      dbSchemaName,
      rowSchema: recorderStateRow(state)?._schema,
      stateSchema: recorderStateRow(state)?.schema,
      supabaseSchema,
    };
    if (!effectiveSchema) {
      if (wasExplicitDbSchemaProvided) {
        console.error(
          "🎤 [ERROR] Schema resolution failed — falling back to public:",
          schemaSources,
        );
      } else {
        console.warn(
          '🎤 [WARN] Schema resolution using "public":',
          schemaSources,
        );
      }
    } else {
      console.log(
        "🎤 [DEBUG] Schema resolution - effectiveSchema:",
        effectiveSchema,
        schemaSources,
      );
    }

    // Supabase credentials for independent audio upload - read directly from env.js
    // This ensures we get the latest value after loadConfig() has been called
    const injectedSupabaseServiceKey =
      envSupabaseServiceKey || supabaseServiceKey || "";
    const injectedAudioBucketName = audioBucketName || "audio";
    const rawSupabaseUrl = (supabaseUrlProp || envSupabaseUrl || "").trim();
    const injectedSupabaseUrl = rawSupabaseUrl
      .replace(/\\/g, "\\\\")
      .replace(/'/g, "\\'");
    const runtimeHostname =
      typeof window !== "undefined" ? window.location.hostname : "";
    let runtimeBackendBaseUrl =
      runtimeHostname === "localhost" || runtimeHostname === "127.0.0.1"
        ? BASE_URLS?.local?.backend
        : BASE_URLS?.render?.backend;
    if (!runtimeBackendBaseUrl || !String(runtimeBackendBaseUrl).trim()) {
      if (runtimeHostname === "localhost" || runtimeHostname === "127.0.0.1") {
        runtimeBackendBaseUrl =
          process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
      }
    }
    const injectedBackendBaseUrl = String(runtimeBackendBaseUrl || "")
      .replace(/\\/g, "\\\\")
      .replace(/'/g, "\\'");

    const injectedVoskServiceOrigin = String(VOSK_SERVICE_ORIGIN || "")
      .replace(/\\/g, "\\\\")
      .replace(/'/g, "\\'");
    const injectedVoskJsUrl = String(VOSK_JS_URL || "")
      .replace(/\\/g, "\\\\")
      .replace(/'/g, "\\'");
    const injectedVoskManifestUrl = String(VOSK_MANIFEST_URL || "")
      .replace(/\\/g, "\\\\")
      .replace(/'/g, "\\'");

    return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Copilot - ${patientName || "Unknown " + getLabel(userRole, 'entity')}</title>
        <style>
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            
            :root {
                --surface-bg: #f3f6fb;
                --surface-card: rgba(255,255,255,0.42);
                --surface-card-strong: rgba(255,255,255,0.62);
                --surface-card-border: rgba(255,255,255,0.58);
                --text-primary: #000000;
                --text-muted: rgba(0, 0, 0, 0.65);
                --radius-md: 12px;
                --radius-lg: 16px;
            }

            html {
                /* Global text scale for the external recorder UI */
                font-size: 24px;
            }
            
            body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                color: var(--text-primary);
                min-height: 100vh;
                width: 100vw;
                max-width: 100vw;
                display: flex;
                flex-direction: column;
                padding: clamp(0.75rem, 2vw, 1.25rem);
                overflow-x: hidden;
                box-sizing: border-box;
                background: var(--surface-bg);
                line-height: 1.45;
                align-items: center;
                gap: clamp(0.5rem, 1.5vh, 0.9rem);
            }

            body, button, input, select, textarea {
                font-size: inherit;
            }
            
            .header {
                text-align: center;
                flex-shrink: 0;
                width: min(100%, 980px);
                overflow: hidden;
                position: relative;
                background: var(--surface-card);
                border: 1px solid var(--surface-card-border);
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
                border-radius: var(--radius-lg);
                padding: clamp(0.8rem, 1.8vw, 1rem);
                backdrop-filter: blur(14px);
            }
            
            .resize-btn {
                position: absolute;
                top: 0.55rem;
                right: 0.55rem;
                background: var(--surface-card-strong);
                border: 1px solid rgba(255,255,255,0.85);
                border-radius: 9px;
                color: var(--text-primary);
                font-size: 0.75rem;
                font-weight: 600;
                padding: 0.32rem 0.65rem;
                cursor: pointer;
                transition: all 0.2s ease;
                z-index: 1000;
                min-width: 58px;
                text-align: center;
            }
            
            .resize-btn:hover {
                background: #ffffff;
                border-color: rgba(255,255,255,0.95);
                transform: translateY(-1px);
            }
            
            .compact-mode .patient-info {
                display: none;
            }
            
            .compact-mode .live-panel {
                display: none;
            }
            
            .compact-mode .checkbox-container {
                display: none;
            }
            
            .compact-mode .recording-display {
                min-height: clamp(205px, 32vh, 250px);
                margin-bottom: clamp(0.3rem, 1vh, 0.5rem);
            }
            
            .compact-mode .record-gif {
                width: clamp(104px, 24vw, 156px);
                height: clamp(91px, 22vw, 130px);
            }
            
            .compact-mode .timer {
                font-size: clamp(1.2rem, 3vw, 1.5rem);
                margin-bottom: 0.5rem;
            }
            
            .compact-mode .status {
                margin-bottom: 0.8rem;
                font-size: clamp(0.9rem, 2.5vw, 1rem);
            }
            
            /* Settings Button */
            .settings-btn {
                position: absolute;
                top: -3.5rem;
                right: 0;
                background: var(--surface-card-strong);
                border: 1px solid rgba(255,255,255,0.8);
                border-radius: var(--radius-md);
                color: #1f2937;
                font-size: 1rem;
                font-weight: 600;
                padding: clamp(0.45rem, 1.2vw, 0.6rem) clamp(0.55rem, 1.5vw, 0.75rem);
                cursor: pointer;
                transition: all 0.2s ease;
                z-index: 1000;
                min-width: 42px;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
                backdrop-filter: blur(10px);
            }
            
            .settings-btn:hover {
                background: #ffffff;
                border-color: rgba(255,255,255,0.95);
                transform: translateY(-1px);
                box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
            }

            .controls-settings-cluster {
                position: absolute;
                top: -3.5rem;
                right: 0;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.35rem;
                z-index: 1000;
            }

            .controls-settings-cluster .settings-btn {
                position: static;
                top: auto;
                right: auto;
            }

            .settings-btn .settings-btn-label {
                margin-left: 0.38rem;
                font-size: 0.78rem;
                font-weight: 700;
                line-height: 1;
                white-space: nowrap;
            }
            
            /* Settings Modal */
            .settings-modal {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0,0,0,0.5);
                z-index: 2000;
                justify-content: center;
                align-items: center;
            }
            
            .settings-modal.active {
                display: flex;
            }
            
            .settings-modal-content {
                background: white;
                border-radius: 12px;
                padding: clamp(1rem, 3vw, 1.5rem);
                max-width: 400px;
                width: 90%;
                color: black;
                box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            }

            .patient-audio-modal-content {
                max-width: min(52rem, 92vw);
                max-height: 88vh;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }

            .patient-audio-list {
                overflow-y: auto;
                flex: 1;
                min-height: 120px;
                max-height: 48vh;
                margin: 0.65rem 0 0.75rem;
                padding-right: 0.15rem;
            }

            .patient-audio-accordion-wrap {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            .patient-audio-acc {
                border: 1px solid #e2e8f0;
                border-radius: 0.375rem;
                background: #ffffff;
                overflow: hidden;
            }

            .patient-audio-acc > summary {
                list-style: none;
                cursor: pointer;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.5rem;
                padding: 0.5rem 0.65rem;
                font-size: 0.875rem;
                user-select: none;
                transition: background 0.15s ease;
            }

            .patient-audio-acc > summary::-webkit-details-marker {
                display: none;
            }

            .patient-audio-acc > summary:hover {
                background: #f7fafc;
            }

            .pam-badge {
                font-size: 0.6875rem;
                font-weight: 600;
                line-height: 1;
                padding: 0.2rem 0.45rem;
                border-radius: 0.25rem;
                white-space: nowrap;
                flex-shrink: 0;
            }

            .pam-badge-green {
                background: #c6f6d5;
                color: #22543d;
            }

            .pam-badge-gray {
                background: #edf2f7;
                color: #4a5568;
            }

            .pam-badge-purple {
                background: #e9d8fd;
                color: #553c9a;
            }

            .pam-acc-title {
                font-weight: 600;
                color: #1a202c;
                flex: 1;
                min-width: 140px;
            }

            .pam-acc-dur {
                font-size: 0.75rem;
                color: #718096;
            }

            .pam-chevron {
                margin-left: auto;
                flex-shrink: 0;
                color: #718096;
                transform: rotate(0deg);
                transition: transform 0.2s ease;
                font-size: 0.7rem;
            }

            details.patient-audio-acc[open] .pam-chevron {
                transform: rotate(180deg);
            }

            .patient-audio-acc-body {
                padding: 0 0.75rem 0.75rem;
                border-top: 1px solid #edf2f7;
                font-size: 0.815rem;
            }

            .patient-audio-acc-body-inner {
                display: flex;
                flex-direction: column;
                gap: 0.65rem;
                padding-top: 0.65rem;
            }

            .pam-player-shell {
                width: 100%;
                padding: 0.5rem;
                background: #f7fafc;
                border-radius: 0.35rem;
                border: 1px solid #e2e8f0;
                box-sizing: border-box;
            }

            .pam-player-shell audio {
                width: 100%;
                max-width: 100%;
                vertical-align: middle;
            }

            .pam-meta-row {
                font-size: 0.75rem;
                color: #718096;
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
                align-items: center;
            }

            .pam-transcript-block-label {
                font-weight: 600;
                font-size: 0.8125rem;
                color: #2d3748;
                margin: 0.15rem 0 0;
            }

            .pam-transcript-snippet {
                font-size: 0.72rem;
                color: #2d3748;
                padding: 0.45rem 0.5rem;
                background: #ebf8ff;
                border-radius: 0.25rem;
                line-height: 1.45;
                max-height: 4.35em;
                overflow: hidden;
                word-break: break-word;
                white-space: pre-wrap;
            }

            .pam-actions {
                display: flex;
                flex-wrap: wrap;
                justify-content: flex-end;
                gap: 0.5rem;
                margin-top: 0.15rem;
            }

            .btn-pam-purple {
                min-width: auto;
                padding: 0.375rem 0.875rem;
                font-size: 0.8125rem;
                font-weight: 600;
                border: none;
                border-radius: 0.375rem;
                cursor: pointer;
                background: #805ad5;
                color: #fff;
                transition: background 0.2s ease, box-shadow 0.2s ease;
                box-shadow: 0 1px 2px rgba(26, 32, 44, 0.08);
            }

            .btn-pam-purple:hover {
                background: #6b46c1;
            }

            .btn-pam-blue {
                min-width: auto;
                padding: 0.375rem 0.875rem;
                font-size: 0.8125rem;
                font-weight: 600;
                border: none;
                border-radius: 0.375rem;
                cursor: pointer;
                background: #3182ce;
                color: #fff;
                transition: background 0.2s ease, box-shadow 0.2s ease;
                box-shadow: 0 1px 2px rgba(26, 32, 44, 0.08);
            }

            .btn-pam-blue:hover {
                background: #2b6cb0;
            }

            .pam-header-stack {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 0.35rem;
            }

            .pam-count-chip {
                font-size: 0.75rem;
                font-weight: 600;
                padding: 0.2rem 0.5rem;
                border-radius: 0.25rem;
                background: #bee3f8;
                color: #2c5282;
            }

            .pam-desc {
                font-size: 0.875rem;
                color: #4a5568;
                margin: 0 0 0.25rem 0;
                line-height: 1.45;
            }
            
            .settings-modal-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 1rem;
                padding-bottom: 0.5rem;
                border-bottom: 1px solid #e2e8f0;
            }
            
            .settings-modal-header h2 {
                font-size: 1.2rem;
                margin: 0;
            }
            
            .close-settings-btn {
                background: none;
                border: none;
                font-size: 1.5rem;
                cursor: pointer;
                color: #666;
                padding: 0;
                line-height: 1;
            }
            
            .close-settings-btn:hover {
                color: #333;
            }
            
            .setting-group {
                margin-bottom: 1rem;
            }
            
            .setting-group label {
                display: block;
                font-weight: 600;
                margin-bottom: 0.5rem;
                font-size: 0.95rem;
            }
            
            .mic-select {
                width: 100%;
                padding: 0.75rem;
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                font-size: 0.95rem;
                background: white;
                cursor: pointer;
            }
            
            .mic-select:focus {
                outline: none;
                border-color: #3182ce;
                box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.2);
            }
            
            .test-mic-btn {
                width: 100%;
                padding: 0.75rem;
                background: #3182ce;
                color: white;
                border: none;
                border-radius: 8px;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.3s ease;
                margin-top: 0.5rem;
            }
            
            .test-mic-btn:hover {
                background: #2c5aa0;
            }
            
            .test-mic-btn:disabled {
                background: #a0aec0;
                cursor: not-allowed;
            }
            
            .test-mic-btn.testing {
                background: #e53e3e;
            }
            
            .mic-level-indicator {
                width: 100%;
                height: 8px;
                background: #e2e8f0;
                border-radius: 4px;
                margin-top: 0.75rem;
                overflow: hidden;
            }
            
            .mic-level-bar {
                height: 100%;
                background: linear-gradient(to right, #48bb78, #38a169, #2f855a);
                border-radius: 4px;
                transition: width 0.1s ease;
                width: 0%;
            }
            
            .mic-status {
                font-size: 0.85rem;
                margin-top: 0.5rem;
                color: #666;
            }
            
            .mic-status.testing {
                color: #e53e3e;
            }
            
            .mic-status.success {
                color: #38a169;
            }

            .mic-status.error {
                color: #e53e3e;
                font-weight: bold;
            }
            
            .header h1 {
                font-size: clamp(1rem, 4vw, 2rem);
                margin-bottom: clamp(0.25rem, 1vh, 0.5rem);
                word-wrap: break-word;
                overflow-wrap: break-word;
                font-weight: 700;
                letter-spacing: -0.01em;
            }
            
            .header p {
                font-size: clamp(0.8rem, 2.5vw, 1rem);
                color: var(--text-muted);
                word-wrap: break-word;
                overflow-wrap: break-word;
            }
            
            .patient-info {
                background: var(--surface-card);
                backdrop-filter: blur(12px);
                border: 1px solid var(--surface-card-border);
                border-radius: var(--radius-md);
                padding: clamp(0.5rem, 2vw, 1rem);
                width: min(100%, 980px);
                text-align: center;
                font-size: clamp(0.8rem, 2.5vw, 1.1rem);
                flex-shrink: 0;
                word-wrap: break-word;
                overflow-wrap: break-word;
                max-width: 100%;
                box-sizing: border-box;
                box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
            }
            
            .recording-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                max-width: 100%;
                width: min(100%, 980px);
                padding: clamp(0.5rem, 2vw, 1rem);
                overflow: visible;
                box-sizing: border-box;
                gap: clamp(0.5rem, 1.5vh, 1rem);
            }
            
            /* Audio Recording Display */
            .recording-display {
                background: var(--surface-card);
                backdrop-filter: blur(12px);
                border-radius: var(--radius-lg);
                text-align: center;
                width: 100%;
                max-width: 100%;
                min-height: clamp(270px, 42vh, 350px);
                border: 1px solid var(--surface-card-border);
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: clamp(0.75rem, 2vw, 1.5rem);
                margin-bottom: clamp(0.5rem, 2vh, 1rem);
                position: relative;
                overflow: hidden;
                transition: border-color 0.3s ease;
                box-sizing: border-box;
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.07);
            }
            
            /* Recording state indicators */
            .recording-display.recording-success {
                border-color: #10B981;
            }
            
            .recording-display.recording-low-audio {
                border-color: #F59E0B;
            }
            
            .recording-display.recording-no-audio {
                border-color: #EF4444;
            }
            
            .recording-display.mic-disabled {
                border-color: #6B7280;
            }
            
            .audio-visualizer {
                position: absolute;
                bottom: 20px;
                left: 50%;
                transform: translateX(-50%);
                display: flex;
                gap: 3px;
                align-items: end;
                height: 40px;
                pointer-events: none;
            }
            
            .audio-bar {
                width: 4px;
                background: linear-gradient(to top, #10B981, #34D399);
                border-radius: 2px;
                transition: height 0.1s ease-out;
                min-height: 4px;
                opacity: 0;
            }
            
            .audio-bar.active {
                opacity: 1;
            }
            
            .record-gif {
                width: clamp(156px, 36vw, 300px);
                height: clamp(130px, 30vw, 250px);
                margin: 0 auto 1rem;
                border-radius: 50%;
                display: block;
                object-fit: cover;
            }
            
            .timer {
                font-size: clamp(1.5rem, 4vw, 2rem);
                font-weight: 300;
                margin-bottom: 1rem;
                font-family: 'Courier New', monospace;
            }
            
            .status {
                margin-bottom: 0.5rem;
                font-weight: 600;
                font-size: clamp(1rem, 3vw, 1.2rem);
            }
            
            .system-audio-status {
                font-size: clamp(0.75rem, 2vw, 0.85rem);
                font-weight: 600;
                color: #38a169;
                margin-bottom: 0.5rem;
                padding: 0.25rem 0.5rem;
                background: rgba(56, 161, 105, 0.1);
                border-radius: 4px;
                display: inline-block;
            }
            
            .system-audio-status.active {
                color: #38a169;
                animation: pulse-green 2s infinite;
            }
            
            @keyframes pulse-green {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
            }

            @keyframes flash-yellow {
                0%, 100% { color: #ed8936; opacity: 1; }
                50% { color: #fbd38d; opacity: 0.4; }
            }
            @keyframes flash-red {
                0%, 100% { color: #e53e3e; opacity: 1; }
                50% { color: #feb2b2; opacity: 0.4; }
            }
            .size-tracker {
                font-size: clamp(1.1rem, 3.5vw, 1.4rem);
                font-weight: 700;
                margin-bottom: 1rem;
                font-family: 'Courier New', monospace;
                color: #48bb78;
                letter-spacing: 0.05em;
                transition: color 0.3s ease;
            }
            .size-tracker.warning { animation: flash-yellow 1s ease-in-out infinite; }
            .size-tracker.danger { animation: flash-red 0.6s ease-in-out infinite; }

            .size-warning-banner {
                background: linear-gradient(135deg, #fffbeb, #fef3c7);
                border: 2px solid #f59e0b;
                border-radius: 10px;
                padding: 0.75rem 1rem;
                margin: 0.5rem 0 0.75rem;
                text-align: center;
                animation: flash-yellow 1.5s ease-in-out infinite;
            }
            .size-warning-banner p {
                margin: 0 0 0.6rem;
                font-size: clamp(0.78rem, 2.2vw, 0.88rem);
                color: #92400e;
                font-weight: 500;
                line-height: 1.4;
            }
            .size-warning-banner .save-continue-btn {
                background: #f59e0b;
                color: #fff;
                border: none;
                border-radius: 7px;
                padding: 0.45rem 1.1rem;
                font-size: clamp(0.8rem, 2.2vw, 0.9rem);
                font-weight: 700;
                cursor: pointer;
                transition: background 0.2s;
            }
            .size-warning-banner .save-continue-btn:hover { background: #d97706; }
            
            .controls {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 0.55rem;
                justify-content: center;
                align-items: center;
                max-width: 100%;
                width: 100%;
                margin: 0 auto;
                text-align: center;
                box-sizing: border-box;
                position: relative;
            }
            
            .btn {
                padding: 0.62rem 1.05rem;
                border: none;
                border-radius: 12px;
                font-weight: 700;
                cursor: pointer;
                transition: all 0.2s ease;
                font-size: clamp(0.86rem, 2.2vw, 0.95rem);
                width: auto;
                min-width: 148px;
                max-width: 210px;
                margin: 0 auto;
                text-align: center;
                box-shadow: 0 3px 8px rgba(0, 0, 0, 0.11);
                letter-spacing: 0.01em;
            }
            
            .btn:hover {
                filter: brightness(0.97);
                transform: translateY(-1px);
            }
            
            .btn-primary:hover {
                background: #2c5aa0;
            }
            
            .btn-secondary:hover {
                background: #c05621;
            }
            
            .btn-success:hover {
                background: #1e874b;
            }
            
            .btn-info:hover {
                background: #2f855a;
            }
            
            .btn-light:hover {
                background: rgba(255,255,255,0.3);
                border-color: rgba(255,255,255,0.5);
            }
            
            .btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
                transform: none;
                box-shadow: none;
            }
            
            .btn-primary {
                background: #3182ce;
                color: white;
            }
            
            .btn-secondary {
                background: #DD6B20;
                color: white;
            }
            
            .btn-success {
                background: #27ae60;
                color: white;
            }
            
            .btn-info {
                background: #38a169;
                color: white;
            }
            
            .btn-warning {
                background: #f39c12;
                color: white;
            }
            
            .btn-light {
                background: rgba(255,255,255,0.2);
                color: black;
                border: 2px solid rgba(255,255,255,0.3);
            }
            
            .hidden {
                display: none !important;
            }
            
            .loading {
                display: inline-block;
                width: clamp(16px, 4vw, 20px);
                height: clamp(16px, 4vw, 20px);
                border: 2px solid rgba(255,255,255,0.3);
                border-radius: 50%;
                border-top-color: black;
                animation: spin 1s ease-in-out infinite;
                margin-right: 0.5rem;
            }
            
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            
            .error-message {
                background: rgba(231, 76, 60, 0.2);
                border: 1px solid #e74c3c;
                border-radius: 10px;
                padding: 1rem;
                margin: 1rem 0;
                text-align: center;
                font-size: clamp(0.9rem, 2.5vw, 1rem);
            }
            
            .success-message {
                background: rgba(39, 174, 96, 0.2);
                border: 1px solid #27ae60;
                border-radius: 10px;
                padding: 1rem;
                margin: 1rem 0;
                text-align: center;
                font-size: clamp(0.9rem, 2.5vw, 1rem);
            }
            
            .warning-message {
                background: rgba(243, 156, 18, 0.2);
                border: 1px solid #f39c12;
                border-radius: 10px;
                padding: 1rem;
                margin: 1rem 0;
                text-align: center;
                font-size: clamp(0.9rem, 2.5vw, 1rem);
            }

            /* Live features panel */
            .live-panel {
                width: 100%;
                max-width: 100%;
                background: var(--surface-card);
                border: 1px solid var(--surface-card-border);
                border-radius: 12px;
                padding: clamp(0.75rem, 2vw, 1rem);
                margin-top: clamp(0.5rem, 2vw, 0.75rem);
                overflow: hidden;
                box-sizing: border-box;
                box-shadow: 0 5px 16px rgba(0, 0, 0, 0.06);
                backdrop-filter: blur(10px);
            }

            .live-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 0.35rem;
                gap: 0.75rem;
            }

            .live-title {
                font-weight: 700;
                font-size: 0.95rem;
            }

            .live-status {
                font-size: 0.85rem;
                color: #4a5568;
            }

            .live-content {
                background: rgba(0,0,0,0.03);
                border-radius: 8px;
                padding: clamp(0.5rem, 1.5vw, 0.75rem);
                height: clamp(100px, 20vh, 140px);
                min-height: clamp(100px, 20vh, 140px);
                max-height: clamp(100px, 20vh, 140px);
                font-size: clamp(0.85rem, 2.5vw, 0.92rem);
                line-height: 1.35;
                color: #1a202c;
                border: 1px dashed rgba(0,0,0,0.08);
                white-space: pre-wrap;
                overflow-y: auto;
                overflow-x: hidden;
                scrollbar-width: thin;
                scrollbar-color: rgba(0,0,0,0.3) transparent;
                box-sizing: border-box;
                resize: none;
            }

            .live-content::-webkit-scrollbar {
                width: 6px;
            }

            .live-content::-webkit-scrollbar-track {
                background: transparent;
            }

            .live-content::-webkit-scrollbar-thumb {
                background-color: rgba(0,0,0,0.3);
                border-radius: 3px;
            }

            .live-content::-webkit-scrollbar-thumb:hover {
                background-color: rgba(0,0,0,0.5);
            }
            
            .footer {
                text-align: center;
                margin-top: 1rem;
                opacity: 0.7;
                font-size: clamp(0.8rem, 2vw, 0.9rem);
                flex-shrink: 0;
            }
            
            @media (max-width: 480px) {
                body {
                    padding: clamp(0.4rem, 2vw, 0.6rem);
                    gap: 0.45rem;
                }
                
                .header {
                    padding-right: 4.5rem;
                }
                
                .recording-container {
                    padding: clamp(0.2rem, 1vw, 0.4rem);
                    align-items: center;
                    justify-content: center;
                }
                
                .recording-display {
                    padding: clamp(0.5rem, 2vw, 0.8rem);
                    min-height: clamp(180px, 30vh, 220px);
                    margin-bottom: clamp(0.3rem, 1vh, 0.5rem);
                }
                
                .controls {
                    flex-direction: column;
                    gap: clamp(0.3rem, 1.2vw, 0.5rem);
                    margin: 0 auto;
                    text-align: center;
                }
                
                .btn {
                    width: 100%;
                    max-width: clamp(168px, 88vw, 220px);
                    min-width: 140px;
                    margin: 0 auto;
                    padding: clamp(0.45rem, 2vw, 0.62rem) clamp(0.7rem, 2.4vw, 0.9rem);
                    font-size: clamp(0.78rem, 2.3vw, 0.88rem);
                }

                .live-content {
                    height: clamp(70px, 12vh, 90px);
                    min-height: clamp(70px, 12vh, 90px);
                    max-height: clamp(70px, 12vh, 90px);
                    font-size: clamp(0.75rem, 2vw, 0.8rem);
                    padding: clamp(0.3rem, 1vw, 0.4rem);
                }

                .live-panel {
                    padding: clamp(0.4rem, 1.8vw, 0.6rem);
                }

                .patient-info {
                    font-size: clamp(0.75rem, 2.2vw, 0.85rem);
                    padding: clamp(0.4rem, 1.8vw, 0.6rem);
                }
            }
            
            @media (prefers-reduced-motion: reduce) {
                * {
                    animation: none !important;
                    transition: none !important;
                }
            }

            /* Tabs */
            .tabs {
                display: flex;
                justify-content: center;
                flex-wrap: wrap;
                gap: 0.5rem;
                margin-bottom: 0.75rem;
                width: min(100%, 980px);
                padding: 0.35rem;
                background: var(--surface-card);
                border: 1px solid var(--surface-card-border);
                border-radius: 12px;
                box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
                backdrop-filter: blur(10px);
            }

            .tab-btn {
                background: transparent;
                border: 1px solid transparent;
                color: var(--text-muted);
                font-size: 0.9rem;
                font-weight: 600;
                padding: 0.4rem 0.8rem;
                cursor: pointer;
                transition: all 0.2s;
                border-radius: 9px;
            }

            .tab-btn:hover {
                color: var(--text-primary);
                background: rgba(79, 70, 229, 0.08);
            }

            .tab-btn.active {
                color: #3182ce;
                border-color: rgba(49, 130, 206, 0.35);
                background: rgba(49, 130, 206, 0.12);
                box-shadow: none;
            }

            .tab-content {
                display: none;
                flex-direction: column;
                width: min(100%, 980px);
                overflow: visible;
            }

            .tab-content.active {
                display: flex;
            }

            /* Results Styles */
            .results-container {
                display: flex;
                flex-direction: column;
                gap: 1rem;
                padding: 0.5rem;
                overflow-y: auto;
                height: 100%;
            }

            .result-section {
                background: rgba(255,255,255,0.4);
                border-radius: 8px;
                padding: 1rem;
                border: 1px solid rgba(0,0,0,0.1);
            }

            .result-section h3 {
                font-size: 1.1rem;
                margin-bottom: 0.5rem;
                color: #2c5282;
                border-bottom: 1px solid rgba(0,0,0,0.1);
                padding-bottom: 0.25rem;
            }

            .result-content {
                font-size: 0.9rem;
                line-height: 1.5;
                white-space: pre-wrap;
            }

            .results-actions {
                display: flex;
                justify-content: flex-end;
                margin-bottom: 1rem;
                gap: 0.5rem;
            }

            /* Ultra small screens */
            @media (max-width: 320px) {
                body {
                    padding: clamp(0.3rem, 1.5vw, 0.5rem);
                }
                
                .recording-container {
                    padding: clamp(0.1rem, 0.8vw, 0.25rem);
                }
                
                .recording-display {
                    padding: clamp(0.4rem, 1.8vw, 0.6rem);
                    min-height: clamp(160px, 28vh, 180px);
                    margin-bottom: clamp(0.25rem, 0.8vh, 0.4rem);
                }
                
                .controls {
                    gap: clamp(0.25rem, 1vw, 0.4rem);
                }
                
                .btn {
                    max-width: clamp(130px, 90vw, 160px);
                    min-width: 120px;
                    padding: clamp(0.35rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.7rem);
                    font-size: clamp(0.7rem, 2vw, 0.8rem);
                }

                .live-content {
                    height: clamp(60px, 10vh, 80px);
                    min-height: clamp(60px, 10vh, 80px);
                    max-height: clamp(60px, 10vh, 80px);
                    font-size: clamp(0.7rem, 1.8vw, 0.75rem);
                    padding: clamp(0.25rem, 0.8vw, 0.35rem);
                }

                .live-panel {
                    padding: clamp(0.35rem, 1.5vw, 0.5rem);
                }

                .patient-info {
                    font-size: clamp(0.7rem, 2vw, 0.8rem);
                    padding: clamp(0.3rem, 1.5vw, 0.5rem);
                }

                .header h1 {
                    font-size: clamp(0.9rem, 3.5vw, 1.2rem);
                }

                .header p {
                    font-size: clamp(0.7rem, 2.2vw, 0.8rem);
                }
            }
            
            @media (max-width: 768px) and (min-width: 481px) {
                .controls {
                    flex-direction: column;
                    max-width: clamp(280px, 85vw, 350px);
                }
                
                .btn {
                    width: 100%;
                    min-width: clamp(180px, 70vw, 230px);
                    max-width: clamp(230px, 80vw, 280px);
                    padding: clamp(0.6rem, 2vw, 0.9rem) clamp(0.8rem, 2.5vw, 1.2rem);
                    font-size: clamp(0.85rem, 2.2vw, 0.95rem);
                }

                .live-content {
                    height: clamp(90px, 15vh, 120px);
                    min-height: clamp(90px, 15vh, 120px);
                    max-height: clamp(90px, 15vh, 120px);
                    font-size: clamp(0.82rem, 2.1vw, 0.9rem);
                }

                .recording-display {
                    min-height: clamp(260px, 42vh, 300px);
                }
            }
            
            @media (max-width: 1024px) and (min-width: 769px) {
                .controls {
                    flex-direction: row;
                    max-width: min(100%, 720px);
                }
                
                .btn {
                    width: auto;
                    min-width: clamp(170px, 24vw, 210px);
                    max-width: clamp(210px, 32vw, 260px);
                    padding: clamp(0.65rem, 1.8vw, 0.85rem) clamp(0.9rem, 2.2vw, 1.1rem);
                    font-size: clamp(0.88rem, 2vw, 0.95rem);
                }

                .live-content {
                    height: clamp(110px, 16vh, 130px);
                    min-height: clamp(110px, 16vh, 130px);
                    max-height: clamp(110px, 16vh, 130px);
                }

                .recording-display {
                    min-height: clamp(280px, 45vh, 320px);
                }
            }
            
            @media (min-width: 1025px) {
                .controls {
                    flex-direction: row;
                    max-width: min(100%, 760px);
                }
                
                .btn {
                    width: auto;
                    min-width: clamp(160px, 18vw, 210px);
                    max-width: clamp(200px, 24vw, 240px);
                    padding: clamp(0.7rem, 1.5vw, 0.9rem) clamp(1rem, 2vw, 1.2rem);
                    font-size: clamp(0.9rem, 1.8vw, 1rem);
                }

                .live-content {
                    height: clamp(120px, 18vh, 150px);
                    min-height: clamp(120px, 18vh, 150px);
                    max-height: clamp(120px, 18vh, 150px);
                }
            }
            
            @media (max-height: 600px) {
                .header {
                    margin-bottom: clamp(0.5rem, 1.5vh, 0.75rem);
                }
                
                .patient-info {
                    margin-bottom: clamp(0.5rem, 1.5vh, 0.75rem);
                    padding: clamp(0.5rem, 1.2vh, 0.75rem);
                }
                
                .recording-display {
                    margin-bottom: clamp(0.5rem, 1.5vh, 0.75rem);
                    padding: clamp(0.75rem, 2vh, 1rem);
                    min-height: clamp(200px, 35vh, 240px);
                }
                
                .status {
                    margin-bottom: clamp(0.5rem, 1.5vh, 0.75rem);
                }

                .live-content {
                    height: clamp(70px, 14vh, 90px);
                    min-height: clamp(70px, 14vh, 90px);
                    max-height: clamp(70px, 14vh, 90px);
                }

                .live-panel {
                    padding: clamp(0.5rem, 1.5vh, 0.75rem);
                }
            }
            
            @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
                .record-gif {
                    image-rendering: -webkit-optimize-contrast;
                    image-rendering: crisp-edges;
                }
            }
        </style>
        <style>
            /* Alternative style: original palette + compact fit */
            body {
                background:
                    radial-gradient(circle at 10% -10%, rgba(49, 130, 206, 0.14), transparent 40%),
                    radial-gradient(circle at 100% -20%, rgba(56, 161, 105, 0.1), transparent 42%),
                    #f2f6fb !important;
                color: #111827 !important;
                font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                padding: 0.38rem !important;
                display: flex !important;
                flex-direction: column !important;
                gap: 0.3rem !important;
                overflow-y: auto !important;
            }

            .header,
            .patient-info,
            .tabs,
            #copilotDisabledBanner,
            #aiTabsStatus,
            .recording-display,
            .checkbox-container,
            .live-panel,
            .result-section {
                width: 100% !important;
                border: 1px solid rgba(226, 232, 240, 0.95) !important;
                background: rgba(255, 255, 255, 0.9) !important;
                backdrop-filter: blur(8px) !important;
                -webkit-backdrop-filter: blur(8px) !important;
                box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08) !important;
            }

            .header {
                border-radius: 12px !important;
                padding: 0.48rem 2.45rem 0.48rem 0.62rem !important;
                margin: 0 !important;
                min-height: 44px !important;
            }

            .header h1 {
                margin: 0 !important;
                font-size: 0.88rem !important;
                font-weight: 700 !important;
                color: #111827 !important;
            }

            .header p {
                margin-top: 0.08rem !important;
                font-size: 0.7rem !important;
                color: rgba(17, 24, 39, 0.68) !important;
            }

            .resize-btn {
                position: absolute !important;
                top: 0.5rem !important;
                right: 0.52rem !important;
                border-radius: 8px !important;
                padding: 0.28rem 0.5rem !important;
                min-width: auto !important;
                background: rgba(255, 255, 255, 0.98) !important;
                border: 1px solid #dbe3ee !important;
                color: #334155 !important;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08) !important;
            }

            .patient-info {
                border-radius: 10px !important;
                margin: 0 !important;
                padding: 0.5rem 0.62rem !important;
                font-size: 0.72rem !important;
                color: #334155 !important;
            }

            .tabs {
                margin: 0 !important;
                padding: 0.14rem !important;
                border-radius: 10px !important;
                display: grid !important;
                grid-template-columns: repeat(auto-fit, minmax(86px, 1fr)) !important;
                gap: 0.16rem !important;
                overflow: hidden !important;
                border-bottom: none !important;
                justify-content: stretch !important;
                flex-wrap: wrap !important;
            }

            .tab-btn {
                width: 100% !important;
                min-width: 0 !important;
                border-radius: 7px !important;
                border: 1px solid #dbe3ee !important;
                background: #f8fafc !important;
                color: #475569 !important;
                font-size: 0.64rem !important;
                padding: 0.28rem 0.34rem !important;
                font-weight: 600 !important;
                line-height: 1.2 !important;
                white-space: nowrap !important;
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                text-align: center !important;
            }

            .tab-btn.active {
                background: #ebf5ff !important;
                border-color: #bfdcff !important;
                color: #1d4ed8 !important;
                box-shadow: none !important;
            }

            #copilotDisabledBanner,
            #aiTabsStatus {
                margin: 0 !important;
                padding: 0.42rem 0.58rem !important;
                border-radius: 10px !important;
                font-size: 0.66rem !important;
                text-align: left !important;
                color: #475569 !important;
            }

            .recording-container {
                width: 100% !important;
                padding: 0 0 0.4rem 0 !important;
                gap: 0.28rem !important;
            }

            .recording-display {
                border-radius: 10px !important;
                min-height: clamp(180px, 31vh, 235px) !important;
                padding: 0.52rem 0.5rem 0.68rem !important;
                margin: 0 !important;
                background: rgba(255, 255, 255, 0.96) !important;
                position: relative !important;
            }

            .record-gif {
                width: clamp(92px, 30vw, 122px) !important;
                height: clamp(82px, 26vw, 110px) !important;
                margin-bottom: 0.24rem !important;
            }

            .timer {
                font-size: clamp(0.96rem, 3.8vw, 1.22rem) !important;
                margin-bottom: 0.16rem !important;
                color: #f8fafc !important;
                color: #111827 !important;
            }

            .status {
                font-size: clamp(0.72rem, 2.5vw, 0.82rem) !important;
                margin-bottom: 0.18rem !important;
                color: #334155 !important;
            }

            .audio-visualizer {
                bottom: 8px !important;
                height: 18px !important;
                gap: 2px !important;
            }

            .audio-bar {
                width: 3px !important;
            }

            .controls {
                display: grid !important;
                grid-template-columns: 1fr auto !important;
                gap: 0.22rem !important;
                width: 100% !important;
                margin: 0 !important;
                position: sticky !important;
                bottom: 0 !important;
                padding-top: 0.2rem !important;
                background: linear-gradient(to top, rgba(242, 246, 251, 0.98), rgba(242, 246, 251, 0.88)) !important;
                z-index: 12 !important;
                border-top: 1px solid #dbe3ee !important;
            }

            .controls-settings-cluster {
                position: static !important;
                top: auto !important;
                right: auto !important;
                grid-column: 2 !important;
                grid-row: 1 !important;
                display: flex !important;
                flex-direction: row !important;
                align-items: center !important;
                gap: 0.28rem !important;
                z-index: 3 !important;
            }

            .settings-btn {
                position: static !important;
                width: 38px !important;
                min-width: 38px !important;
                max-width: 38px !important;
                min-height: 38px !important;
                border-radius: 8px !important;
                border: 1px solid #d1dbe8 !important;
                background: #ffffff !important;
                color: #334155 !important;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
                padding: 0 !important;
                z-index: 3 !important;
            }

            .settings-btn .settings-btn-label {
                display: none !important;
            }

            .settings-btn svg {
                width: 16px !important;
                height: 16px !important;
            }

            .btn {
                width: 100% !important;
                grid-column: 1 / -1 !important;
                min-width: 0 !important;
                max-width: none !important;
                min-height: 38px !important;
                border-radius: 8px !important;
                padding: 0.36rem 0.42rem !important;
                font-size: 0.66rem !important;
                font-weight: 700 !important;
                line-height: 1.2 !important;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
            }

            .btn-primary {
                background: #3182ce !important;
                min-height: 42px !important;
                font-size: 0.69rem !important;
                grid-column: 1 !important;
                grid-row: 1 !important;
            }

            .btn-secondary {
                background: #dd6b20 !important;
            }

            .btn-success {
                background: #27ae60 !important;
            }

            .btn-info {
                grid-column: 1 / -1 !important;
                background: #38a169 !important;
            }

            .btn .btn-icon {
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
                margin-right: 0.28rem !important;
                vertical-align: middle !important;
            }

            .btn .btn-icon svg {
                width: 12px !important;
                height: 12px !important;
                stroke: currentColor !important;
            }

            .checkbox-container {
                border-radius: 9px !important;
                padding: 0.44rem 0.5rem !important;
                margin: 0 !important;
                text-align: left !important;
            }

            .checkbox-container > div {
                margin-bottom: 0.26rem !important;
            }

            .checkbox-container label {
                justify-content: flex-start !important;
                gap: 0.35rem !important;
            }

            .checkbox-container span {
                font-size: 0.7rem !important;
                color: #475569 !important;
            }

            .live-panel {
                border-radius: 9px !important;
                padding: 0.44rem 0.5rem !important;
                margin-top: 0 !important;
            }

            .live-title {
                font-size: 0.72rem !important;
                color: #1f2937 !important;
            }

            .live-status {
                font-size: 0.67rem !important;
                color: #4b5563 !important;
            }

            .live-content {
                border-radius: 8px !important;
                min-height: 50px !important;
                max-height: 70px !important;
                font-size: 0.7rem !important;
                padding: 0.34rem !important;
                background: rgba(255, 255, 255, 0.95) !important;
                border: 1px solid #e2e8f0 !important;
                color: #334155 !important;
            }

            .results-container {
                width: 100% !important;
                padding: 0 !important;
                gap: 0.3rem !important;
            }

            .result-section {
                border-radius: 9px !important;
                padding: 0.52rem !important;
            }

            .result-section h3 {
                font-size: 0.76rem !important;
                margin-bottom: 0.24rem !important;
                color: #2c5282 !important;
            }

            .result-content {
                font-size: 0.7rem !important;
                line-height: 1.28 !important;
                color: #334155 !important;
            }

            @media (max-width: 390px) {
                .tabs {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                .controls {
                    grid-template-columns: 1fr auto !important;
                    gap: 0.3rem !important;
                }

                .controls-settings-cluster {
                    grid-column: 2 !important;
                    grid-row: 1 !important;
                }

                .settings-btn {
                    width: 36px !important;
                    min-width: 36px !important;
                    max-width: 36px !important;
                    min-height: 36px !important;
                }

                .btn {
                    min-height: 36px !important;
                    font-size: 0.64rem !important;
                }
            }
        </style>
        <style>
            .premium-dark-mode {
                background: #000000 !important;
                color: #f3f4f6 !important;
            }

            .premium-dark-mode .header,
            .premium-dark-mode .patient-info,
            .premium-dark-mode .tabs,
            .premium-dark-mode #copilotDisabledBanner,
            .premium-dark-mode #aiTabsStatus,
            .premium-dark-mode .recording-display,
            .premium-dark-mode .checkbox-container,
            .premium-dark-mode .live-panel,
            .premium-dark-mode .result-section {
                background: #000000 !important;
                border-color: #2a2a2a !important;
                box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4) !important;
            }

            .premium-dark-mode .header h1,
            .premium-dark-mode .timer,
            .premium-dark-mode .live-title {
                color: #f9fafb !important;
            }

            .premium-dark-mode .header p,
            .premium-dark-mode .patient-info,
            .premium-dark-mode .status,
            .premium-dark-mode .live-status,
            .premium-dark-mode .checkbox-container span,
            .premium-dark-mode #aiTabsStatus {
                color: #cbd5e1 !important;
            }

            .premium-dark-mode .tab-btn {
                background: #000000 !important;
                border-color: #2f2f2f !important;
                color: #d1d5db !important;
            }

            .premium-dark-mode .tab-btn.active {
                background: #0b0b0b !important;
                border-color: #60a5fa !important;
                color: #dbeafe !important;
            }

            .premium-dark-mode .controls {
                background: #000000 !important;
                border-top-color: #2a2a2a !important;
            }

            .premium-dark-mode .settings-btn {
                background: #000000 !important;
                border-color: #2f2f2f !important;
                color: #e2e8f0 !important;
            }

            .premium-dark-mode .live-content,
            .premium-dark-mode #liveTranscriptTabContent,
            .premium-dark-mode #liveTranscriptSystemAudioContent,
            .premium-dark-mode #liveTranscriptMergedContent,
            .premium-dark-mode #liveSystemMergedOutput {
                background: #000000 !important;
                border-color: #2f2f2f !important;
                color: #e2e8f0 !important;
            }

            .premium-dark-mode .settings-modal-content,
            .premium-dark-mode .patient-audio-modal-content,
            .premium-dark-mode .patient-audio-list,
            .premium-dark-mode .patient-audio-acc,
            .premium-dark-mode .patient-audio-acc > summary,
            .premium-dark-mode .patient-audio-acc-body,
            .premium-dark-mode .pam-player-shell,
            .premium-dark-mode .setting-group,
            .premium-dark-mode .mic-level-indicator {
                background: #000000 !important;
                border-color: #2f2f2f !important;
                color: #e5e7eb !important;
            }

            .premium-dark-mode .settings-modal-header {
                border-bottom-color: #2f2f2f !important;
            }

            .premium-dark-mode .settings-modal-header h2,
            .premium-dark-mode .pam-acc-title,
            .premium-dark-mode .pam-transcript-block-label,
            .premium-dark-mode .setting-group label {
                color: #f9fafb !important;
            }

            .premium-dark-mode .close-settings-btn,
            .premium-dark-mode .pam-acc-dur,
            .premium-dark-mode .pam-chevron,
            .premium-dark-mode .pam-meta-row,
            .premium-dark-mode .pam-desc,
            .premium-dark-mode .mic-status {
                color: #cbd5e1 !important;
            }

            .premium-dark-mode .close-settings-btn:hover {
                color: #ffffff !important;
            }

            .premium-dark-mode .pam-badge-gray {
                background: #111827 !important;
                color: #cbd5e1 !important;
                border: 1px solid #374151 !important;
            }

            .premium-dark-mode .pam-badge-green {
                background: #052e1a !important;
                color: #86efac !important;
                border: 1px solid #166534 !important;
            }

            .premium-dark-mode .pam-badge-purple {
                background: #2e1065 !important;
                color: #ddd6fe !important;
                border: 1px solid #6d28d9 !important;
            }

            .premium-dark-mode .pam-count-chip {
                background: #111827 !important;
                color: #bfdbfe !important;
                border: 1px solid #374151 !important;
            }

            .premium-dark-mode .pam-transcript-snippet {
                background: #0b0b0b !important;
                color: #e2e8f0 !important;
                border: 1px solid #2f2f2f !important;
            }

            .premium-dark-mode .mic-select {
                background: #0b0b0b !important;
                color: #e5e7eb !important;
                border-color: #2f2f2f !important;
            }

            .premium-dark-mode .mic-select:focus {
                border-color: #60a5fa !important;
                box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.25) !important;
            }

            .premium-dark-mode .test-mic-btn {
                background: #1d4ed8 !important;
                color: #eff6ff !important;
            }

            .premium-dark-mode .test-mic-btn:hover {
                background: #1e40af !important;
            }

            .premium-dark-mode .test-mic-btn:disabled {
                background: #374151 !important;
                color: #9ca3af !important;
            }

            .premium-dark-mode .test-mic-btn.testing {
                background: #991b1b !important;
                color: #fee2e2 !important;
            }

            .premium-dark-mode .record-gif {
                width: clamp(144px, 40vw, 220px) !important;
                height: clamp(130px, 36vw, 196px) !important;
            }

            .premium-dark-mode #apTab,
            .premium-dark-mode #considerTab,
            .premium-dark-mode #scoresTab,
            .premium-dark-mode #reportTab,
            .premium-dark-mode #apTab *,
            .premium-dark-mode #considerTab *,
            .premium-dark-mode #scoresTab *,
            .premium-dark-mode #reportTab * {
                color: #ffffff !important;
            }

            .premium-dark-mode #apTab .result-section,
            .premium-dark-mode #considerTab .result-section,
            .premium-dark-mode #scoresTab .result-section,
            .premium-dark-mode #reportTab .result-section {
                background: #000000 !important;
                border-color: #2f2f2f !important;
            }
        </style>
    </head>
    <body class="${premiumDarkModeClass}">
        <div class="patient-info" id="patientInfoBanner">
            <strong>${getLabel(userRole, 'entity')}:</strong> ${
              patientName || "Unknown " + getLabel(userRole, 'entity')
            } | <strong>Date:</strong> ${scheduledDate || "—"}
            <button id="syncPatientBtn" onclick="requestPatientSync()" style="margin-left:8px;padding:2px 8px;font-size:12px;border:1px solid #cbd5e0;border-radius:999px;background:#edf2f7;color:#2d3748;cursor:pointer;">Sync Patient</button>
        </div>
        <button class="resize-btn" id="resizeBtn" onclick="toggleCompactMode()" title="Toggle compact mode">
                    <svg id="compactIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="4 14 10 14 10 20"></polyline>
                        <polyline points="20 10 14 10 14 4"></polyline>
                        <line x1="14" y1="10" x2="21" y2="3"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                    </svg>
                    <svg id="expandIcon" class="hidden" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" y1="3" x2="14" y2="10"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                    </svg>
                </button>
        <div class="tabs">
            <button class="tab-btn active" id="tab-recorder" onclick="switchTab('recorder')">Recorder</button>
            <button class="tab-btn" id="tab-liveTranscript" onclick="switchTab('liveTranscript')" title="Live transcript during recording + post-stop dialog and Vosk results">Transcript</button>
            ${hideResultsTab ? "" : getClinicalResultsTabButtonsHtml()}
        </div>
        <div id="copilotDisabledBanner" style="display:none;font-size:0.8rem;color:#b7791f;text-align:center;margin-bottom:0.5rem;">
            <div style="margin-bottom:0.35rem;">Upgrade to premium to enable tab functions from copilot</div>
            <div style="margin-bottom:0.35rem;">${copilotUpgradeBannerMarkup}</div>
            <div style="margin:0.35rem 0;font-size:0.72rem;color:#92400e;">If you need help upgrading your plan, use Help Desk to send a message for support.</div>
            <button type="button" id="copilotHelpDeskBtn" style="display:inline-block;padding:0.38rem 0.85rem;border-radius:8px;background:#e53e3e;color:#fff;font-weight:600;font-size:0.72rem;border:none;cursor:pointer;">Help Desk</button>
        </div>
        <div id="aiTabsStatus" style="font-size: 0.78rem; color: #4a5568; text-align: center; margin-bottom: 0.5rem;">AI: idle</div>
        <div id="aiKeyDataContainer" style="font-size: 0.72rem; color: #2c5282; margin: 0.35rem auto 0.75rem auto; max-width: 980px; width: 100%; border: 1px solid #bee3f8; background: #ebf8ff; border-radius: 8px; padding: 0.5rem; display: none; flex-direction: column; text-align: left; box-sizing: border-box;"></div>
        
        <div id="recorderTab" class="tab-content active recording-container" style="overflow: visible; height: auto; flex: none;">
            <div class="recording-display" id="recordingDisplay">
                <img class="record-gif" id="recordGif" src="${recorderGifSrc}" alt="Recording indicator" />
                <div class="timer" id="timer">00:00</div>
                <div class="status" id="status"></div>
                ${injectedCopilotEnabled === "true" ? '<div id="aiRecorderTimer" style="font-size: 0.75rem; color: #3182ce; margin-top: 0.25rem; font-weight: 600;">AI: idle</div>' : ''}
                <div class="system-audio-status hidden" id="systemAudioStatus"></div>
                <div class="system-audio-status hidden" id="micInUseStatus"></div>
                <div class="size-tracker hidden" id="sizeTracker">0 MB / ${injectedMaxUploadLabelText}</div>
                <div class="size-warning-banner hidden" id="sizeWarningBanner">
                    <p>Recording is approaching the file size limit.<br>Save now and start a new recording to continue without interruption.</p>
                    <button class="save-continue-btn" onclick="saveAndContinueRecording()">Save & Continue Recording</button>
                </div>
                <!-- Audio Visualizer Bars -->
                <div class="audio-visualizer" id="audioVisualizer">
                    <div class="audio-bar" id="audioBar1"></div>
                    <div class="audio-bar" id="audioBar2"></div>
                    <div class="audio-bar" id="audioBar3"></div>
                    <div class="audio-bar" id="audioBar4"></div>
                    <div class="audio-bar" id="audioBar5"></div>
                    <div class="audio-bar" id="audioBar6"></div>
                    <div class="audio-bar" id="audioBar7"></div>
                    <div class="audio-bar" id="audioBar8"></div>
                </div>
            </div>
            
            <div class="controls" id="controls">
                <div class="controls-settings-cluster" id="controlsSettingsCluster" aria-label="Recorder tools">
                    <button type="button" class="settings-btn" id="settingsBtn" onclick="openSettingsModal()" title="Microphone Settings">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                        </svg>
                    </button>
                </div>
                <button class="btn btn-primary" id="startBtn" onclick="startRecording()">
                    <span class="btn-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="8,5 19,12 8,19"></polygon>
                        </svg>
                    </span>
                    Start Recording
                </button>
                
                <button class="btn btn-secondary hidden" id="pauseBtn" onclick="pauseRecording()">
                    <span class="btn-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="5" width="4" height="14"></rect>
                            <rect x="14" y="5" width="4" height="14"></rect>
                        </svg>
                    </span>
                    Pause Recording
                </button>
                
                <button class="btn btn-success hidden" id="resumeBtn" onclick="resumeRecording()">
                    <span class="btn-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="8,5 19,12 8,19"></polygon>
                        </svg>
                    </span>
                    Resume Recording
                </button>
                
                <button class="btn btn-info hidden" id="stopBtn" onclick="stopAndSaveRecording()">
                    <span class="btn-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="6" width="12" height="12"></rect>
                        </svg>
                    </span>
                    Stop & Save to ${saveTargetName}
                </button>


            </div>

            <!-- Audio Manager moved out of the icon-cluster and into its own
                 dedicated row below Start Recording. Leejoe's request 2026-06-24
                 night: prior recordings should be obvious + one-click. The
                 button opens the existing Audio Manager modal which lists all
                 the patient's recordings with per-row "Process this recording"
                 buttons (the autoGenerate flow). -->
            <!-- Restyled 2026-06-28 (Leejoe): green tone matching Process button
                 so it stands out as a distinct action from blue Start Recording,
                 with smaller text so it doesn't compete visually with the
                 primary Start Recording call-to-action above. -->
            <button type="button" id="patientAudioBtn"
                onclick="openPatientAudioModal()"
                title="Open prior recordings + upload"
                aria-label="Open Audio Manager"
                style="margin-top: clamp(0.5rem, 1.5vw, 0.75rem); width: 100%; display: flex; align-items: center; justify-content: center; gap: 0.45rem; padding: 0.5rem 0.85rem; border: 1px solid rgba(16, 185, 129, 0.45); border-radius: 0.5rem; background: rgba(16, 185, 129, 0.18); color: #34d399; font-size: 0.75rem; font-weight: 500; letter-spacing: 0.01em; cursor: pointer;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M9 18V5l12-2v13"></path>
                    <circle cx="6" cy="18" r="3"></circle>
                    <circle cx="18" cy="16" r="3"></circle>
                </svg>
                <span>Prior recordings</span>
            </button>
            
            <div class="checkbox-container" id="checkboxContainer" style="margin-top: clamp(0.75rem, 2vw, 1rem); text-align: center; max-width: 100%; box-sizing: border-box;">
                <div style="margin-bottom: clamp(0.5rem, 1.5vw, 0.75rem);">
                    <label style="display: flex; align-items: center; justify-content: center; gap: clamp(0.4rem, 1.5vw, 0.5rem); cursor: not-allowed; font-weight: 500; flex-wrap: wrap; opacity: 0.6;">
                        <input type="checkbox" id="liveTranscriptionCheckbox" ${
                          liveTranscriptionEnabled ? "checked" : ""
                        }
                               disabled onchange="updateTranscriptionSetting()" style="transform: scale(1.2); cursor: not-allowed;">
                        <span style="color: ${
                          liveTranscriptionEnabled ? "#38a169" : "#666"
                        }; font-size: clamp(0.85rem, 2.5vw, 0.95rem);">Live Transcription</span>
                    </label>
                </div>
                <div style="margin-bottom: clamp(0.5rem, 1.5vw, 0.75rem);">
                    <label style="display: flex; align-items: center; justify-content: center; gap: clamp(0.4rem, 1.5vw, 0.5rem); cursor: not-allowed; font-weight: 500; flex-wrap: wrap; opacity: 0.6; pointer-events: none;">
                        <input type="checkbox" id="aiRecapCheckbox" checked disabled style="transform: scale(1.2); cursor: not-allowed; pointer-events: none;" onclick="return false;" onchange="return false;">
                        <span id="aiRecapCheckboxLabel" style="color: #666; font-size: clamp(0.85rem, 2.5vw, 0.95rem);">Generate AI Recap on save</span>
                    </label>
                </div>
                <div style="margin-bottom: clamp(0.5rem, 1.5vw, 0.75rem);">
                    <label style="display: flex; align-items: center; justify-content: center; gap: clamp(0.4rem, 1.5vw, 0.5rem); cursor: pointer; font-weight: 500; flex-wrap: wrap;">
                        <input type="checkbox" id="includeSystemAudioCheckbox" ${
                          includeSystemAudioEnabled ? "checked" : ""
                        } onchange="updateSystemAudioSetting()" style="transform: scale(1.2);">
                        <span style="color: #666; font-size: clamp(0.85rem, 2.5vw, 0.95rem);">Include system/meeting audio</span>
                    </label>
                    <div style="font-size: clamp(0.7rem, 2vw, 0.8rem); color: #888; margin-top: 0.25rem; text-align: center;">Share a tab or window when starting; <strong>check &quot;Also share tab audio&quot;</strong> so meeting audio is captured.</div>
                </div>
            </div>

            <div class="live-panel" id="liveFeaturesPanel">
                <div class="live-row">
                    <span class="live-title">Live Transcription</span>
                    <span class="live-status" id="liveTranscriptionStatus">Idle</span>
                </div>
                <div class="live-content" id="liveTranscriptionOutput">Enable the checkbox to view live transcription.</div>
                <div class="live-row" style="margin-top: 0.6rem;">
                    <span class="live-title" style="font-size: 0.82rem; color: #4a5568;">System audio</span>
                    <span style="font-size: 0.72rem; color: #718096;">Live</span>
                </div>
                <div id="systemAudioSourceBadge" style="font-size: 0.72rem; color: #718096; margin-top: 0.25rem; margin-bottom: 0.35rem;">Source: Detecting…</div>
                <div class="live-content" id="systemAudioTranscriptionOutput" style="font-size: 0.86rem; min-height: 52px;">No system audio transcript yet.</div>
                <div class="live-row" style="margin-top: 0.6rem;">
                    <span class="live-title" style="font-size: 0.82rem; color: #4a5568;">Live + System</span>
                    <span style="font-size: 0.72rem; color: #718096;">Merged</span>
                </div>
                <div class="live-content" id="liveSystemMergedOutput" style="font-size: 0.82rem; min-height: 56px; margin-top: 0.35rem;">Merged timeline appears when live or system audio has text.</div>
            </div>
            
            <!-- Settings Modal -->
            <div class="settings-modal" id="settingsModal">
                <div class="settings-modal-content">
                    <div class="settings-modal-header">
                        <h2>Microphone Settings</h2>
                        <button class="close-settings-btn" onclick="closeSettingsModal()">&times;</button>
                    </div>
                    <div class="setting-group">
                        <label for="micSelect">Select Microphone:</label>
                        <select class="mic-select" id="micSelect">
                            <option value="">Loading microphones...</option>
                        </select>
                    </div>
                    <div class="setting-group">
                        <button class="test-mic-btn" id="testMicBtn" onclick="toggleMicTest()">
                            Test Microphone
                        </button>
                        <div class="mic-level-indicator">
                            <div class="mic-level-bar" id="micLevelBar"></div>
                        </div>
                        <div class="mic-status" id="micStatus">Click "Test Microphone" to hear your mic</div>
                    </div>
                </div>
            </div>
            
            <div id="messages"></div>
        </div>

        <div id="liveTranscriptTab" class="tab-content" style="padding: 1rem; overflow-y: auto; flex-direction: column; gap: 0.75rem;">
            <!-- A6.5 (Fix B): post-stop dialog + Vosk processing state + Vosk results merged from
                 the old Transcript tab tab. Same DOM ids so existing handlers keep working. -->
            <div id="transcriptionPrompt" class="hidden" style="text-align: center; padding: 2rem; background: #ebf8ff; border: 1px solid #90cdf4; border-radius: 8px; margin-bottom: 1rem;">
                <h3 style="margin-bottom: 0.5rem; color: #2c5282;">✨ Audio Saved Successfully!</h3>
                <p style="color: #4a5568; margin-bottom: 1.5rem;">Transcribe this audio on your device with Vosk. Clinical tabs can still run from the transcript when copilot is enabled.</p>
                <div style="display: flex; gap: 1rem; justify-content: center;">
                    <button class="btn btn-info" id="transcribeYesBtn" onclick="requestTranscription()" style="min-width: 120px;">Yes, Transcribe</button>
                    <button class="btn btn-light" id="transcribeNoBtn" onclick="skipTranscription()" style="min-width: 120px;">No, Skip</button>
                </div>
            </div>
            <div id="processingState" class="hidden" style="text-align: center; padding: 2rem;">
                <div class="loading" style="width: 40px; height: 40px; border-width: 4px; margin: 0 auto 1rem;"></div>
                <h3 style="margin-bottom: 0.5rem; color: #2c5282;">Processing Audio...</h3>
                <p id="processingMessage" style="color: #4a5568;">Initializing transcription...</p>
            </div>
            <div class="results-actions hidden" id="resultsActions">
                <button class="btn btn-warning" style="min-width: auto; width: auto; font-size: 0.9rem;" onclick="exportToPDF()">Export to PDF</button>
            </div>
            <div class="results-container" id="resultsContainer" style="display:none;"></div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                <span style="font-size: 1rem; font-weight: 700; color: #2c5282;">Live Transcript</span>
                <span id="liveTranscriptTabStatus" style="font-size: 0.85rem; color: #4a5568;">Idle</span>
            </div>
            <div id="liveTranscriptTabContent" style="
                background: rgba(0,0,0,0.03);
                border-radius: 8px;
                padding: 1rem;
                font-size: 0.95rem;
                line-height: 1.6;
                color: #1a202c;
                border: 1px dashed rgba(0,0,0,0.1);
                white-space: pre-wrap;
                min-height: 300px;
                flex: 1;
                word-break: break-word;
            ">Enable Live Transcription and start recording — your transcript will appear here.</div>
            <div style="margin-top: 0.85rem;">
                <div style="font-size: 0.86rem; font-weight: 700; color: #2d3748; margin-bottom: 0.35rem;">System audio</div>
                <div id="liveTranscriptSystemAudioContent" style="
                    background: rgba(0,0,0,0.02);
                    border-radius: 8px;
                    padding: 0.8rem;
                    font-size: 0.9rem;
                    line-height: 1.5;
                    color: #2d3748;
                    border: 1px dashed rgba(0,0,0,0.1);
                    white-space: pre-wrap;
                    min-height: 80px;
                    word-break: break-word;
                ">No system audio transcript yet.</div>
            </div>
            <div style="margin-top: 0.85rem;">
                <div style="font-size: 0.86rem; font-weight: 700; color: #2d3748; margin-bottom: 0.35rem;">Live + System</div>
                <div id="liveTranscriptMergedContent" style="
                    background: rgba(0,0,0,0.02);
                    border-radius: 8px;
                    padding: 0.8rem;
                    font-size: 0.88rem;
                    line-height: 1.55;
                    color: #2d3748;
                    border: 1px dashed rgba(0,0,0,0.1);
                    white-space: pre-wrap;
                    min-height: 96px;
                    word-break: break-word;
                ">Merged timeline appears when live or system audio has text.</div>
            </div>
            <div style="font-size: 0.8rem; color: #718096; text-align: center; margin-top: 0.5rem;">
                Transcript updates as you speak. Scroll up to review earlier text.
            </div>

            <!-- AI Recap section (shown after recap is generated) -->
            <div id="aiRecapSection" style="display: none; margin-top: 1rem; border-top: 1px solid #e2e8f0; padding-top: 1rem;">
                <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
                    <span style="font-size: 0.95rem; font-weight: 700; color: #6b46c1;">AI Recap</span>
                    <span style="font-size: 0.65rem; background: #6b46c1; color: white; padding: 1px 6px; border-radius: 999px; font-weight: 600;">AI</span>
                    <span id="aiRecapSpinner" style="display: none; width: 16px; height: 16px; border: 2px solid #e9d8fd; border-top-color: #6b46c1; border-radius: 50%; animation: spin 0.8s linear infinite;"></span>
                </div>
                <div id="aiRecapGenerating" style="display: none; font-size: 0.85rem; color: #6b46c1; font-style: italic;">Generating recap…</div>
                <div id="aiRecapContent" style="display: none;">
                    <div id="aiRecapSummaryBox" style="margin-bottom: 0.75rem;">
                        <div style="font-size: 0.8rem; font-weight: 600; color: #4a5568; margin-bottom: 0.25rem;">Summary</div>
                        <div id="aiRecapSummary" style="font-size: 0.9rem; color: #2d3748; line-height: 1.5;"></div>
                    </div>
                    <div id="aiRecapTakeawaysBox" style="margin-bottom: 0.75rem; display: none;">
                        <div style="font-size: 0.8rem; font-weight: 600; color: #4a5568; margin-bottom: 0.25rem;">Key Takeaways</div>
                        <ul id="aiRecapTakeaways" style="font-size: 0.9rem; color: #2d3748; line-height: 1.6; padding-left: 1.25rem; margin: 0;"></ul>
                    </div>
                    <div id="aiRecapActionsBox" style="display: none;">
                        <div style="font-size: 0.8rem; font-weight: 600; color: #4a5568; margin-bottom: 0.25rem;">Action Items</div>
                        <ul id="aiRecapActions" style="font-size: 0.9rem; color: #2d3748; line-height: 1.6; padding-left: 1.25rem; margin: 0;"></ul>
                    </div>
                </div>
                <div id="aiRecapError" style="display: none; font-size: 0.85rem; color: #e53e3e;"></div>
            </div>
        </div>

        ${hideResultsTab ? "" : getClinicalResultsTabPanelsHtml()}
        <!-- A6.5 (Fix B): old resultsTab panel removed — its DOM contents
             (transcriptionPrompt, processingState, resultsActions, resultsContainer) now
             live at the top of liveTranscriptTab. The "Transcript tab" tab button is
             gone; all switchTab('results') calls have been remapped to 'liveTranscript'. -->


        <div class="settings-modal" id="patientAudioModal" aria-hidden="true">
            <div class="settings-modal-content patient-audio-modal-content">
                <div class="settings-modal-header">
                    <div>
                        <div class="pam-header-stack">
                            <h2 style="margin:0;">Audio Manager</h2>
                            <span id="patientAudioHeaderCount" class="pam-count-chip">0 Audios</span>
                        </div>
                    </div>
                    <button type="button" class="close-settings-btn" onclick="closePatientAudioModal()" aria-label="Close">&times;</button>
                </div>
                <p class="pam-desc">
                    Saved audios for this patient (same layout as the dashboard audio list). Upload a file below to add another, then transcribe.
                </p>
                <div class="patient-audio-list" id="patientAudioList"></div>
                <input type="file" id="patientAudioFileInput" class="hidden" accept="audio/*,.mp3,.wav,.webm,.m4a,.mp4,.ogg,.aac,.flac" onchange="onPatientAudioFileSelected(event)" />
                <button type="button" class="btn btn-primary" style="width: 100%;" onclick="triggerPatientAudioFilePick()">
                    Upload external audio file
                </button>
                <p style="font-size: 0.75rem; color: #718096; margin: 0.65rem 0 0 0; text-align: center;">
                    Max ${injectedMaxUploadLabelText}. Clinical accounts use cloud transcription; other roles use on-device Vosk when configured.
                </p>
            </div>
        </div>

        <script>
            // User role for feature gating (student, socialworker, other = tenant = no Corti, only live transcript)
            const userRole = '${(recordingUserRole || "").replace(/'/g, "\\'")}';
            const roleTokens = String(userRole || '').toLowerCase().split(',').map(r => r.trim()).filter(Boolean);
            const storageRoleRaw = (
                (typeof localStorage !== 'undefined' && (localStorage.getItem('userRole') || localStorage.getItem('role'))) || ''
            );
            const storageRoleTokens = String(storageRoleRaw || '').toLowerCase().split(',').map(r => r.trim()).filter(Boolean);
            const effectiveRole = userRole && userRole.includes(',') ? userRole.split(',')[1].trim() : userRole;
            // NOTE: this runs inside the injected recording-window <script>, so the
            // module-level normalizeRole import is NOT available here. Inline the same
            // logic (lowercase the segment after any comma) to avoid a ReferenceError.
            const _rTrim = String(effectiveRole || '').trim();
            const _r = _rTrim === '' ? null : (_rTrim.includes(',') ? _rTrim.split(',').pop().trim() : _rTrim).toLowerCase();
            const isTenantUser = _r === 'student' || _r === 'socialworker' || _r === 'other';
            const normalizedEffectiveRole = String(effectiveRole || '').toLowerCase().trim();

            // Global patient info - injected from parent component
            let patientId = '${recordingPatientId}';
            let patientName = '${recordingPatientName}';
            let injectedEhrId = '${injectedEhrId}';
            const scheduledDate = '${injectedScheduledDate}';
            const audioNumber = ${recordingAudioNumber};
            
            // Supabase credentials for independent audio upload (same project as main app)
            const EXTERNAL_SUPABASE_SERVICE_KEY = '${injectedSupabaseServiceKey}';
            const EXTERNAL_AUDIO_BUCKET_NAME = '${injectedAudioBucketName}';
            // API URL e.g. https://xxxx.supabase.co — MUST be set; popup is about:blank so location.origin is wrong
            const EXTERNAL_SUPABASE_URL = '${injectedSupabaseUrl}';
            // Backend URL used by popup to get Corti websocket URL directly (no parent relay)
            const EXTERNAL_BACKEND_BASE_URL = '${injectedBackendBaseUrl}';
            // Vosk service origin + URLs (injected from the parent bundle so the popup
            // does not depend on the opener's async warmup having populated
            // window.opener.__notesaiVoskServiceConfig).
            const EXTERNAL_VOSK_SERVICE_ORIGIN = '${injectedVoskServiceOrigin}';
            const EXTERNAL_VOSK_JS_URL = '${injectedVoskJsUrl}';
            const EXTERNAL_VOSK_MANIFEST_URL = '${injectedVoskManifestUrl}';
            const EXTERNAL_TABLE_NAME = '${injectedTableName}';
            // The parent's best guess. locateRowSchema() re-points it at the
            // schema that really holds the row before anything is written.
            let EXTERNAL_DB_SCHEMA = '${effectiveSchema || "public"}';
            const EXTERNAL_DB_SCHEMA_CANDIDATES = ${injectedSchemaCandidatesJson};
            const EXTERNAL_USER_ID = '${(userId || "").replace(/'/g, "\\\'")}';
            const EXTERNAL_USER_JWT_TOKEN = '${(userJwtToken || "").replace(/'/g, "\\\'")}';
            const COPILOT_ENABLED = (${injectedCopilotEnabled});
            // NOTE: this runs inside the injected recording-window <script>, so the
            // module-level utils/localToday import is NOT available here. Inline the
            // same logic: the clinician's local YYYY-MM-DD, not the UTC server's date.
            function localToday(now) {
                var d = now || new Date();
                var y = d.getFullYear();
                var m = String(d.getMonth() + 1).padStart(2, '0');
                var day = String(d.getDate()).padStart(2, '0');
                return y + '-' + m + '-' + day;
            }
            const AUTO_COPIOLT_ENABLED = ${auto_copilot ? "true" : "false"};
            const HIDE_RESULTS_TAB = ${hideResultsTab ? "true" : "false"};
            const copilotDebugInit = {
                userRole,
                roleTokens,
                storageRoleRaw,
                storageRoleTokens,
                effectiveRole,
                normalizedEffectiveRole,
                injectedCopilotEnabled: ${injectedCopilotEnabled},
                COPILOT_ENABLED,
            };
            console.log('🎤 [COPILOT-DEBUG] popup init flags:', copilotDebugInit);
            console.log('🎤 [COPILOT-DEBUG] popup init flags JSON:', JSON.stringify(copilotDebugInit));
            
            // =========================================================
            // INDEPENDENT SUPABASE UPLOAD FUNCTIONS
            // These allow the external window to upload audio directly
            // without relying on the parent window
            // =========================================================
            
            function resolveSupabaseProjectUrl(explicitUrl) {
                var u = (explicitUrl || '').trim();
                if (u && /^https?:\\/\\//i.test(u)) {
                    return u.replace(/\\/$/, '');
                }
                var inj = (typeof EXTERNAL_SUPABASE_URL === 'string') ? EXTERNAL_SUPABASE_URL : '';
                u = inj.trim();
                if (u && /^https?:\\/\\//i.test(u)) {
                    return u.replace(/\\/$/, '');
                }
                try {
                    if (window.opener && window.opener.localStorage) {
                        var configStr = window.opener.localStorage.getItem('supabaseConfig');
                        if (configStr) {
                            var config = JSON.parse(configStr);
                            if (config && config.supabaseUrl) {
                                return String(config.supabaseUrl).replace(/\\/$/, '');
                            }
                        }
                    }
                } catch (e) { /* ignore */ }
                return '';
            }
            
            /**
             * Upload audio blob directly to Supabase storage
             * Returns { success: true, url: string } or { success: false, error: string }
             */
            async function uploadAudioDirectly(blob, filename, bucketName, supabaseUrl, serviceKey, metadata = {}) {
                if (!serviceKey || !bucketName) {
                    return { success: false, error: 'Missing Supabase credentials for direct upload' };
                }
                
                if (!blob || blob.size === 0) {
                    console.error('🎤 [DIRECT-UPLOAD] Error: blob is empty or null', { blobSize: blob?.size });
                    return { success: false, error: 'Audio blob is empty or invalid' };
                }
                
                try {
                    console.log('🎤 [DIRECT-UPLOAD] Starting direct upload to Supabase:', {
                        filename,
                        bucketName,
                        blobSize: blob.size,
                        blobType: blob.type,
                        hasExplicitUrl: !!supabaseUrl
                    });
                    
                    var targetUrl = resolveSupabaseProjectUrl(supabaseUrl);
                    if (!targetUrl) {
                        console.error('🎤 [DIRECT-UPLOAD] No Supabase project URL (set REACT_APP_SUPABASE_URL / config or inject EXTERNAL_SUPABASE_URL).');
                        return { success: false, error: 'Supabase URL is not configured for direct upload' };
                    }
                    const storageBase = targetUrl + "/storage/v1";
                    
                    // Build the path: bucket/files/schema/year/date/filename
                    const schemaName = metadata.schemaName || 'public';
                    const year = metadata.year || new Date().getFullYear().toString();
                    const date = metadata.date || new Date().toISOString().split('T')[0];
                    const fullPath = schemaName + "/" + year + "/" + date + "/" + filename;
                    
                    console.log('🎤 [DIRECT-UPLOAD] Uploading to path:', fullPath);
                    
                    // Use fetch API to upload directly to Supabase Storage
                    const response = await fetch(storageBase + "/object/" + bucketName + "/" + fullPath, {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + serviceKey,
                            'apikey': serviceKey,
                            'Content-Type': blob.type || 'audio/webm',
                            'x-upsert': 'true'
                        },
                        body: blob
                    });
                    
                    if (!response.ok) {
                        const errorText = await response.text().catch(() => 'Unknown error');
                        console.error('🎤 [DIRECT-UPLOAD] Upload failed:', response.status, errorText);
                        return { success: false, error: 'Upload failed: ' + response.status + ' - ' + errorText };
                    }
                    
                    // Get the public URL of the uploaded file
                    const publicUrl = targetUrl + "/storage/v1/object/public/" + bucketName + "/" + fullPath;
                    
                    console.log('🎤 [DIRECT-UPLOAD] Success! URL:', publicUrl);
                    return { success: true, url: publicUrl, path: fullPath };
                    
                } catch (error) {
                    console.error('🎤 [DIRECT-UPLOAD] Error:', error);
                    return { success: false, error: error.message || 'Unknown upload error' };
                }
            }

            function sanitizePatientNameForBackup(name) {
                if (!name || typeof name !== 'string') return 'unknown_patient';
                var sanitized = name.trim()
                    .replace(/[^\\w\\s-]/g, '')
                    .replace(/\\s+/g, '_')
                    .replace(/_+/g, '_')
                    .replace(/^_+|_+$/g, '');
                return sanitized || 'unknown_patient';
            }

            function sanitizeRollingSessionSlug(raw) {
                var s = String(raw || 'session').trim();
                var o = '';
                var max = 96;
                for (var i = 0; i < s.length && o.length < max; i++) {
                    var c = s[i];
                    o += /[a-zA-Z0-9_-]/.test(c) ? c : '_';
                }
                return o.replace(/_+/g, '_').replace(/^_|_$/g, '') || 'sess';
            }

            function buildRollingBackupFilename(blob) {
                var sanitizedPatientName = sanitizePatientNameForBackup(patientName);
                var ehrPart = (injectedEhrId && String(injectedEhrId).trim())
                    ? String(injectedEhrId).trim().replace(/[^\\w-]/g, '')
                    : '';
                var sessSlug = sanitizeRollingSessionSlug(recordingSessionId);
                var ext = getAudioFileExtensionForBlob(blob);
                return sanitizedPatientName + '-' + ehrPart + '_Rolling_' + sessSlug + '.' + ext;
            }

            async function uploadPeriodicBackupDirectly(blob) {
                if (!EXTERNAL_SUPABASE_SERVICE_KEY || !resolveSupabaseProjectUrl(EXTERNAL_SUPABASE_URL)) {
                    return { success: false, error: 'Missing Supabase credentials for backup upload' };
                }
                var filename = buildRollingBackupFilename(blob);
                var schemaName = EXTERNAL_DB_SCHEMA || 'public';
                var year = scheduledDate ? scheduledDate.split('-')[0] : new Date().getFullYear().toString();
                var date = scheduledDate || new Date().toISOString().split('T')[0];
                return uploadAudioDirectly(
                    blob,
                    filename,
                    EXTERNAL_BACKUP_BUCKET_NAME,
                    null,
                    EXTERNAL_SUPABASE_SERVICE_KEY,
                    { schemaName: schemaName, year: year, date: date }
                );
            }

            /**
             * Bad-internet final-save fallback: server-side copy of the rolling
             * backup checkpoint (full recording up to the last backup tick) from
             * backup-audio into the main audio bucket — no bytes re-uploaded.
             */
            async function promoteLastBackupToAudioBucket(destFilename, schemaName, year, date) {
                var targetUrl = resolveSupabaseProjectUrl(EXTERNAL_SUPABASE_URL);
                if (!targetUrl || !EXTERNAL_SUPABASE_SERVICE_KEY) {
                    return { success: false, error: 'Missing Supabase credentials for backup promotion' };
                }
                var folder = schemaName + '/' + year + '/' + date + '/';
                var sourceKey = folder + buildRollingBackupFilename(recordedAudioBlob);
                var destKey = folder + destFilename;
                console.log('🎤 [STORAGE-COPY] Copying ' + EXTERNAL_BACKUP_BUCKET_NAME + '/' + sourceKey + ' -> ' + EXTERNAL_AUDIO_BUCKET_NAME + '/' + destKey);
                try {
                    var response = await fetch(targetUrl + '/storage/v1/object/copy', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + EXTERNAL_SUPABASE_SERVICE_KEY,
                            'apikey': EXTERNAL_SUPABASE_SERVICE_KEY,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            bucketId: EXTERNAL_BACKUP_BUCKET_NAME,
                            sourceKey: sourceKey,
                            destinationBucket: EXTERNAL_AUDIO_BUCKET_NAME,
                            destinationKey: destKey
                        })
                    });
                    if (!response.ok) {
                        var errText = await response.text().catch(function () { return 'Unknown error'; });
                        console.warn('🎤 [STORAGE-COPY] Copy failed:', response.status, errText);
                        return { success: false, error: 'Copy failed: ' + response.status };
                    }
                    var publicUrl = targetUrl + '/storage/v1/object/public/' + EXTERNAL_AUDIO_BUCKET_NAME + '/' + destKey;
                    console.log('🎤 [STORAGE-COPY] Success! URL:', publicUrl);
                    return { success: true, url: publicUrl, path: destKey };
                } catch (copyError) {
                    console.warn('🎤 [STORAGE-COPY] Copy error:', copyError);
                    return { success: false, error: (copyError && copyError.message) || 'Unknown copy error' };
                }
            }

            /**
             * Wait until recordedAudioBlob exists after MediaRecorder.stop().
             * Long recordings assemble many chunks; a fixed 3s timeout was too short.
             */
            async function waitForRecordedBlobAfterStop() {
                var chunkCount = audioChunks.length;
                var bytes = totalRecordedBytes;
                var maxWaitMs = Math.min(
                    180000,
                    Math.max(8000, 4000 + chunkCount * 30 + Math.floor(bytes / (512 * 1024)) * 1500)
                );
                var start = Date.now();

                return new Promise(function(resolve) {
                    var settled = false;
                    var timeoutId = setTimeout(function() {
                        if (settled) return;
                        settled = true;
                        console.warn('⚠️ [DEBUG] waitForRecordedBlobAfterStop timed out after ' + maxWaitMs + 'ms');
                        resolve();
                    }, maxWaitMs);

                    function finishWait() {
                        if (settled) return;
                        settled = true;
                        clearTimeout(timeoutId);
                        resolve();
                    }

                    if (mediaRecorder && mediaRecorder.state === 'inactive' && recordedAudioBlob) {
                        finishWait();
                        return;
                    }

                    if (mediaRecorder) {
                        var originalOnstop = mediaRecorder.onstop;
                        mediaRecorder.onstop = function(event) {
                            console.log('🎤 [DEBUG] onstop event received in waitForRecordedBlobAfterStop');
                            try {
                                if (originalOnstop) originalOnstop.call(mediaRecorder, event);
                            } catch (onstopErr) {
                                console.error('🎤 [ERROR] onstop handler failed:', onstopErr);
                            }
                            finishWait();
                        };
                    } else {
                        finishWait();
                    }

                    (async function pollForBlob() {
                        while (!settled && !recordedAudioBlob && Date.now() - start < maxWaitMs) {
                            await new Promise(function(r) { setTimeout(r, 100); });
                        }
                        if (!settled && !recordedAudioBlob && audioChunks.length > 0) {
                            try {
                                console.log('🎤 [DEBUG] Assembling recordedAudioBlob from ' + audioChunks.length + ' chunks (fallback)');
                                recordedAudioBlob = new Blob(audioChunks, { type: currentMimeType });
                            } catch (assembleErr) {
                                console.error('🎤 [ERROR] Fallback blob assembly failed:', assembleErr);
                            }
                        }
                        if (!settled) finishWait();
                    })();
                });
            }

            /**
             * Upload a finished segment and notify the parent (direct upload preferred for large blobs).
             */
            async function uploadCompletedSegmentAndNotifyParent(blob, options) {
                var duration = options.duration || 0;
                var transcriptText = options.transcriptText || '';
                var sessionId = options.sessionId || recordingSessionId;
                var totalBackups = options.totalBackups || 0;
                var timestamp = options.timestamp || new Date().toISOString();

                if (!blob || !blob.size) {
                    return { success: false, error: 'Empty audio blob' };
                }

                var directUploadSuccess = false;
                var finalAudioUrl = null;
                var segmentLiveTranscriptSaved = false;

                if (EXTERNAL_SUPABASE_SERVICE_KEY && EXTERNAL_AUDIO_BUCKET_NAME) {
                    var year = scheduledDate ? scheduledDate.split('-')[0] : new Date().getFullYear().toString();
                    var date = scheduledDate || new Date().toISOString().split('T')[0];
                    try {
                        await ensureRowSchema();
                        var nextAudioIndex = await getNextAudioIndexFromStorage(
                            EXTERNAL_AUDIO_BUCKET_NAME,
                            EXTERNAL_DB_SCHEMA || 'public',
                            year,
                            date,
                            patientName
                        );
                        var filename = generateAudioFilename(
                            patientName,
                            injectedEhrId,
                            nextAudioIndex,
                            getAudioFileExtensionForBlob(blob)
                        );
                        var uploadResult = await uploadAudioDirectly(
                            blob,
                            filename,
                            EXTERNAL_AUDIO_BUCKET_NAME,
                            null,
                            EXTERNAL_SUPABASE_SERVICE_KEY,
                            {
                                schemaName: EXTERNAL_DB_SCHEMA || 'public',
                                year: year,
                                date: date,
                                patientId: patientId,
                                patientName: patientName,
                                ehrId: injectedEhrId,
                                duration: duration
                            }
                        );
                        if (uploadResult.success) {
                            directUploadSuccess = true;
                            finalAudioUrl = uploadResult.url;
                            lastSavedCopilotAudioSlot = nextAudioIndex;
                            var segmentLabel = getExternalAudioLabel();
                            var patientAudioSave = await saveAudioToPatientRecordDirectly(
                                finalAudioUrl,
                                duration,
                                blob.type,
                                segmentLabel
                            );
                            if (!patientAudioSave.success) {
                                console.warn('[SEGMENT-SAVE] Patient record update failed:', patientAudioSave.error);
                            }
                            // The parent skips its own transcript write when
                            // databaseUpdatedDirectly is set, so the segment's
                            // live transcript has to be written here.
                            if (getLiveTranscriptionSetting() && (transcriptText || '').trim()) {
                                var segmentTranscriptSave = await saveLiveTranscriptDirectly(
                                    finalAudioUrl,
                                    transcriptText,
                                    duration,
                                    sessionId,
                                    segmentLabel
                                );
                                segmentLiveTranscriptSaved = !!segmentTranscriptSave.success;
                                if (!segmentTranscriptSave.success) {
                                    console.warn('[SEGMENT-SAVE] Live transcript save failed:', segmentTranscriptSave.error);
                                }
                            }
                        }
                    } catch (segmentUploadErr) {
                        console.warn('[SEGMENT-SAVE] Direct upload failed:', segmentUploadErr);
                    }
                }

                if (!window.opener) {
                    return directUploadSuccess
                        ? { success: true, audioUrl: finalAudioUrl }
                        : { success: false, error: 'Parent window not available' };
                }

                if (directUploadSuccess) {
                    window.opener.postMessage({
                        type: 'AUDIO_SAVED_SUCCESS',
                        patientId: patientId,
                        patientName: patientName,
                        audioUrl: finalAudioUrl,
                        finalAudioUrl: finalAudioUrl,
                        duration: duration,
                        timestamp: timestamp,
                        sessionId: sessionId,
                        wasDirectUpload: true,
                        liveTranscription: getLiveTranscriptionSetting(),
                        liveTranscriptText: transcriptText,
                        liveTranscriptSaved: segmentLiveTranscriptSaved,
                        scheduledDate: scheduledDate,
                        patientScheduledDate: scheduledDate,
                        generateAiRecap: getAiRecapSetting(),
                        databaseUpdatedDirectly: true
                    }, '*');
                    return { success: true, audioUrl: finalAudioUrl };
                }

                if (blob.size > POST_MESSAGE_BLOB_MAX_BYTES) {
                    return {
                        success: false,
                        error: 'Segment too large for parent window transfer (' +
                            Math.round(blob.size / (1024 * 1024)) + ' MB)'
                    };
                }

                window.opener.postMessage({
                    type: 'AUDIO_RECORDING_COMPLETE',
                    patientId: patientId,
                    patientName: patientName,
                    ehrId: injectedEhrId,
                    audioBlob: blob,
                    duration: duration,
                    timestamp: timestamp,
                    patientScheduledDate: scheduledDate,
                    scheduledDate: scheduledDate,
                    liveTranscription: getLiveTranscriptionSetting(),
                    liveTranscriptText: transcriptText,
                    liveFactExtraction: false,
                    generateAiRecap: getAiRecapSetting(),
                    totalBackups: totalBackups,
                    sessionId: sessionId
                }, '*');
                return { success: true };
            }

            function getRestHeaders(serviceKey, schemaName) {
                const schema = (schemaName || EXTERNAL_DB_SCHEMA || 'public').trim() || 'public';
                return {
                    'Authorization': 'Bearer ' + serviceKey,
                    'apikey': serviceKey,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Prefer': 'return=representation',
                    'Accept-Profile': schema,
                    'Content-Profile': schema
                };
            }

            /**
             * List existing audio files in storage to find the current max audio index
             * Returns the next available audio number (max index + 1)
             * 
             * Strategy:
             * 1. Try patient record more_audios count first (most reliable for patient-specific count)
             * 2. Fall back to storage listing to find max index from filenames
             * 3. If storage fails with 404 (bucket doesn't exist yet), return 1 (no files exist)
             * 4. Only throw on actual errors, not on empty results
             */
            async function getNextAudioIndexFromStorage(bucketName, schemaName, year, date, patientName) {
                const targetUrl = resolveSupabaseProjectUrl(EXTERNAL_SUPABASE_URL);
                if (!targetUrl || !EXTERNAL_SUPABASE_SERVICE_KEY) {
                    throw new Error('🎤 [AUDIO-INDEX] Cannot list storage - missing Supabase config (service key or URL)');
                }

                // First approach: Try to get audio count from patient record's more_audios array
                try {
                    const recordResult = await fetchPatientRecordDirectly(patientId);
                    if (recordResult.success && recordResult.record && recordResult.record.more_audios) {
                        const audioCount = Array.isArray(recordResult.record.more_audios) 
                            ? recordResult.record.more_audios.length 
                            : 0;
                        const nextIndex = audioCount + 1;
                        console.log('🎤 [AUDIO-INDEX] Using more_audios count from patient record:', audioCount, ', next audio will be:', nextIndex);
                        return nextIndex;
                    }
                } catch (recordError) {
                    console.log('🎤 [AUDIO-INDEX] Could not fetch patient record, falling back to storage listing:', recordError.message);
                }

                // Second approach: Fall back to storage listing to find max audio index
                try {
                    // Build prefix path to search for patient's audio files
                    const prefix = schemaName + "/" + year + "/" + date + "/";
                    // Supabase's list endpoint is POST with a JSON body — a GET
                    // here gets routed as GET /object/{bucket="list"}/... and
                    // fails with "Bucket not found".
                    const listUrl = targetUrl + "/storage/v1/object/list/" + bucketName;

                    console.log('🎤 [AUDIO-INDEX] Listing existing audio files:', listUrl, 'prefix:', prefix);

                    const response = await fetch(listUrl, {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + EXTERNAL_SUPABASE_SERVICE_KEY,
                            'apikey': EXTERNAL_SUPABASE_SERVICE_KEY,
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ prefix: prefix, limit: 1000 })
                    });

                    // 404 means bucket doesn't exist or has no files - not an error, just means start at 1
                    if (response.status === 404) {
                        console.log('🎤 [AUDIO-INDEX] Storage returned 404 (bucket empty or not found) - starting at 1');
                        return 1;
                    }

                    if (!response.ok) {
                        const errorText = await response.text().catch(() => 'Unknown error');
                        throw new Error('🎤 [AUDIO-INDEX] Storage list failed with status ' + response.status + ': ' + errorText);
                    }

                    const files = await response.json();
                    if (!Array.isArray(files) || files.length === 0) {
                        console.log('🎤 [AUDIO-INDEX] No existing files found - starting at 1');
                        return 1;
                    }

                    // Parse audio indices from filenames
                    // Filename pattern: {Name}[-{EHR}]_Audio{N}_{timestamp}.webm
                    // Compared on letters+digits only: generateAudioFilename
                    // rewrites spaces/commas, and files written before its
                    // regex fix carry a different spelling of the same name
                    // ("Li Garrido_ Diego Guillermo" vs "Li_Garrido_Diego_Guillermo").
                    // (Regexes in this script live inside a template literal, so
                    // every backslash must be doubled to survive into the popup.)
                    const nameKey = String(patientName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                    let maxIndex = 0;

                    for (const file of files) {
                        const filename = file.name || '';
                        const match = filename.match(/^(.*?)_Audio(\\d+)_/);
                        if (!nameKey || !match) continue;
                        const fileKey = match[1].toLowerCase().replace(/[^a-z0-9]/g, '');
                        if (fileKey.indexOf(nameKey) !== 0) continue;
                        const index = parseInt(match[2], 10);
                        if (index > maxIndex) {
                            maxIndex = index;
                        }
                    }

                    const nextIndex = maxIndex + 1;
                    console.log('🎤 [AUDIO-INDEX] Found max index from storage:', maxIndex, ', next audio will be:', nextIndex);
                    return nextIndex;

                } catch (error) {
                    // Re-throw the error without any fallback - let caller handle it
                    if (error.message.includes('🎤 [AUDIO-INDEX]')) {
                        throw error;
                    }
                    throw new Error('🎤 [AUDIO-INDEX] Error listing storage: ' + error.message);
                }
            }

            /**
             * Which schema the chart row really lives in. EXTERNAL_DB_SCHEMA is
             * the parent's best guess, and that guess reads a "public" tag as
             * "not set". A public-schema chart opened by a STUDENT viewer was
             * aimed at STUDENT."notes-c", matched no row, and Stop & Save failed
             * after the audio had already uploaded. So look the id up in each
             * candidate (best guess first) and point EXTERNAL_DB_SCHEMA (table
             * writes AND storage folders) at the first schema that has it.
             *
             * Keyed by patientId, because Sync Patient can swap the chart under
             * an idle popup. Only a hit is remembered. After a miss or a network
             * error, the next read or write looks again.
             */
            let rowSchemaPatientId = null;
            let rowSchemaLookup = null;
            const ROW_SCHEMA_LOOKUP_TIMEOUT_MS = 8000;

            function ensureRowSchema() {
                const idForLookup = String(patientId || '');
                if (!idForLookup || rowSchemaPatientId === idForLookup) {
                    return Promise.resolve(EXTERNAL_DB_SCHEMA);
                }
                if (rowSchemaLookup && rowSchemaLookup.patientId === idForLookup) {
                    return rowSchemaLookup.promise;
                }
                const lookup = { patientId: idForLookup, promise: null };
                lookup.promise = locateRowSchema(idForLookup)
                    .catch(function () { return EXTERNAL_DB_SCHEMA; })
                    .finally(function () {
                        if (rowSchemaLookup === lookup) rowSchemaLookup = null;
                    });
                rowSchemaLookup = lookup;
                return lookup.promise;
            }

            async function locateRowSchema(idForLookup) {
                const targetUrl = resolveSupabaseProjectUrl(EXTERNAL_SUPABASE_URL);
                if (!targetUrl || !EXTERNAL_SUPABASE_SERVICE_KEY) return EXTERNAL_DB_SCHEMA;
                const candidates = [];
                EXTERNAL_DB_SCHEMA_CANDIDATES.concat([EXTERNAL_DB_SCHEMA]).forEach(function (s) {
                    const name = String(s || '').trim();
                    if (name && candidates.indexOf(name) === -1) candidates.push(name);
                });
                for (const schema of candidates) {
                    let rows = null;
                    const controller = (typeof AbortController === 'function') ? new AbortController() : null;
                    const timer = controller
                        ? setTimeout(function () { controller.abort(); }, ROW_SCHEMA_LOOKUP_TIMEOUT_MS)
                        : null;
                    try {
                        const response = await fetch(
                            targetUrl + '/rest/v1/' + encodeURIComponent(EXTERNAL_TABLE_NAME)
                                + '?id=eq.' + encodeURIComponent(idForLookup) + '&select=id',
                            {
                                method: 'GET',
                                headers: getRestHeaders(EXTERNAL_SUPABASE_SERVICE_KEY, schema),
                                signal: controller ? controller.signal : undefined
                            }
                        );
                        // A schema that isn't exposed or lacks the table answers
                        // non-2xx. That's simply a miss.
                        if (response.ok) rows = await response.json();
                    } catch (lookupError) {
                        // Offline or timed out. Change nothing; the next read/write retries.
                        console.warn('🎤 [SCHEMA] Could not check ' + schema + ' for row id=' + idForLookup + ':',
                            (lookupError && lookupError.message) || lookupError);
                        return EXTERNAL_DB_SCHEMA;
                    } finally {
                        if (timer) clearTimeout(timer);
                    }
                    if (!Array.isArray(rows) || rows.length === 0) continue;
                    // Sync Patient swapped the chart during the lookup, so this answer is stale.
                    if (String(patientId || '') !== idForLookup) return EXTERNAL_DB_SCHEMA;
                    if (schema !== EXTERNAL_DB_SCHEMA) {
                        console.warn('🎤 [SCHEMA] Row id=' + idForLookup + ' is in ' + schema + '.' + EXTERNAL_TABLE_NAME
                            + ', not ' + EXTERNAL_DB_SCHEMA + ' — saving there.');
                        EXTERNAL_DB_SCHEMA = schema;
                    }
                    rowSchemaPatientId = idForLookup;
                    return schema;
                }
                console.error('🎤 [SCHEMA] Row id=' + idForLookup + ' is not in ' + EXTERNAL_TABLE_NAME
                    + ' in any of: ' + candidates.join(', '));
                return EXTERNAL_DB_SCHEMA;
            }

            async function fetchPatientRecordDirectly(patientIdToFetch) {
                const targetUrl = resolveSupabaseProjectUrl(EXTERNAL_SUPABASE_URL);
                if (!targetUrl || !EXTERNAL_SUPABASE_SERVICE_KEY) {
                    return { success: false, error: 'Supabase direct DB config missing' };
                }
                await ensureRowSchema();

                console.log('🎤 [DEBUG] fetchPatientRecordDirectly called with:', {
                    patientIdToFetch,
                    EXTERNAL_TABLE_NAME,
                    EXTERNAL_DB_SCHEMA,
                    targetUrl
                });

                const restUrl = targetUrl + '/rest/v1/' + encodeURIComponent(EXTERNAL_TABLE_NAME)
                    + '?id=eq.' + encodeURIComponent(patientIdToFetch)
                    + '&select=id,more_audios,transcripts';

                try {
                    const response = await fetch(restUrl, {
                        method: 'GET',
                        headers: getRestHeaders(EXTERNAL_SUPABASE_SERVICE_KEY, EXTERNAL_DB_SCHEMA)
                    });

                    if (!response.ok) {
                        const errorText = await response.text().catch(() => 'Unknown error');
                        console.log('🎤 [DEBUG] fetchPatientRecordDirectly response not OK:', response.status, errorText);
                        return { success: false, error: 'Fetch patient failed: ' + response.status + ' - ' + errorText };
                    }

                    const rows = await response.json();
                    console.log('🎤 [DEBUG] fetchPatientRecordDirectly response OK, rows:', JSON.stringify(rows));
                    if (!rows || rows.length === 0) {
                        // Almost always the wrong schema: the id is real, just not
                        // in the schema we asked. Name both so the log says which.
                        var missingMsg = 'No row id=' + patientIdToFetch + ' in '
                            + (EXTERNAL_DB_SCHEMA || 'public') + '.' + EXTERNAL_TABLE_NAME
                            + ' — the chart probably lives in another schema.';
                        console.error('🎤 [SCHEMA] ' + missingMsg);
                        return { success: false, error: missingMsg };
                    }
                    return { success: true, record: Array.isArray(rows) ? rows[0] : rows };
                } catch (error) {
                    return { success: false, error: error.message || 'Fetch patient failed' };
                }
            }

            async function updatePatientRecordDirectly(patientIdToUpdate, payload) {
                const targetUrl = resolveSupabaseProjectUrl(EXTERNAL_SUPABASE_URL);
                if (!targetUrl || !EXTERNAL_SUPABASE_SERVICE_KEY) {
                    return { success: false, error: 'Supabase direct DB config missing' };
                }
                await ensureRowSchema();

                const restUrl = targetUrl + '/rest/v1/' + encodeURIComponent(EXTERNAL_TABLE_NAME)
                    + '?id=eq.' + encodeURIComponent(patientIdToUpdate);

                try {
                    const response = await fetch(restUrl, {
                        method: 'PATCH',
                        headers: getRestHeaders(EXTERNAL_SUPABASE_SERVICE_KEY, EXTERNAL_DB_SCHEMA),
                        body: JSON.stringify(payload)
                    });

                    if (!response.ok) {
                        const errorText = await response.text().catch(() => 'Unknown error');
                        return { success: false, error: 'Update patient failed: ' + response.status + ' - ' + errorText };
                    }

                    const rows = await response.json().catch(() => []);
                    // A PATCH whose filter matches nothing is still a 2xx — with
                    // Prefer: return=representation the giveaway is an empty
                    // body. Treating that as success is how a recording would go
                    // "saved" with nothing written to the chart.
                    if (Array.isArray(rows) && rows.length === 0) {
                        var noRowMsg = 'Update matched no row: id=' + patientIdToUpdate
                            + ' in ' + (EXTERNAL_DB_SCHEMA || 'public') + '.' + EXTERNAL_TABLE_NAME;
                        console.error('🎤 [SCHEMA] ' + noRowMsg);
                        return { success: false, error: noRowMsg };
                    }
                    return { success: true, rows: rows };
                } catch (error) {
                    return { success: false, error: error.message || 'Update patient failed' };
                }
            }

            function getExternalAudioLabel() {
                return 'Recording ' + new Date().toLocaleDateString() + ' ' + new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }

            function buildExternalAudioEntry(audioUrl, durationSeconds, contentType, labelOverride) {
                const nowIso = new Date().toISOString();
                const label = labelOverride || getExternalAudioLabel();

                return {
                    name: label,
                    description: 'Audio recording from external window',
                    url: audioUrl,
                    duration: durationSeconds || 0,
                    uploaded_at: nowIso,
                    source: 'external_recording_window_direct',
                    recording_timestamp: nowIso,
                    verified_update: true,
                    debug_verified: true,
                    type: 'audio',
                    patient_id: patientId,
                    recording_date: nowIso,
                    file_size: null,
                    content_type: contentType || 'audio/webm',
                    displayName: label
                };
            }

            /**
             * more_audios / transcripts are whole-column read-modify-writes. A
             * Save & continue segment save and the final Stop & Save can overlap
             * (old recorder onstop vs. new recorder stop), and the second fetch
             * would then PATCH an array missing the first one's entry. Queue
             * every such write so they run strictly one after another.
             */
            let patientRecordWriteChain = Promise.resolve();
            function withPatientRecordWriteLock(fn) {
                const run = patientRecordWriteChain.then(fn);
                patientRecordWriteChain = run.catch(function () {});
                return run;
            }

            function saveAudioToPatientRecordDirectly(audioUrl, durationSeconds, contentType, labelOverride) {
                return withPatientRecordWriteLock(function () {
                    return saveAudioToPatientRecordDirectlyUnlocked(audioUrl, durationSeconds, contentType, labelOverride);
                });
            }

            function saveLiveTranscriptDirectly(audioUrl, transcriptValue, durationSeconds, sessionIdValue, audioLabelOverride) {
                return withPatientRecordWriteLock(function () {
                    return saveLiveTranscriptDirectlyUnlocked(audioUrl, transcriptValue, durationSeconds, sessionIdValue, audioLabelOverride);
                });
            }

            async function saveAudioToPatientRecordDirectlyUnlocked(audioUrl, durationSeconds, contentType, labelOverride) {
                const fetched = await fetchPatientRecordDirectly(patientId);
                if (!fetched.success || !fetched.record) {
                    return { success: false, error: fetched.error || 'Patient record not found' };
                }

                const currentAudios = Array.isArray(fetched.record.more_audios) ? fetched.record.more_audios : [];
                const updatedAudios = [...currentAudios, buildExternalAudioEntry(audioUrl, durationSeconds, contentType, labelOverride)];

                return await updatePatientRecordDirectly(patientId, {
                    more_audios: updatedAudios,
                    act_time: new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                    }).slice(0, 8)
                });
            }

            async function saveLiveTranscriptDirectlyUnlocked(audioUrl, transcriptValue, durationSeconds, sessionIdValue, audioLabelOverride) {
                const fetched = await fetchPatientRecordDirectly(patientId);
                if (!fetched.success || !fetched.record) {
                    return { success: false, error: fetched.error || 'Patient record not found' };
                }

                const currentTranscripts = Array.isArray(fetched.record.transcripts)
                    ? fetched.record.transcripts
                    : (fetched.record.transcripts ? [fetched.record.transcripts] : []);

                if (
                    sessionIdValue &&
                    currentTranscripts.some((t) => t && t.is_live_transcript && t.session_id === sessionIdValue)
                ) {
                    return { success: true, skipped: true };
                }

                const safeAudioUrl = (audioUrl || '').trim();
                const audioFileName = audioLabelOverride || getExternalAudioLabel();

                const transcriptEntry = {
                    id: (crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()),
                    session_id: sessionIdValue || null,
                    patient_id: String(patientId || ''),
                    name: patientName || 'Unknown Patient',
                    transcript: transcriptValue && transcriptValue.trim() ? [transcriptValue.trim()] : [],
                    facts: [],
                    soap_notes: {},
                    audio_duration: durationSeconds || 0,
                    audio_source: 'live_transcript',
                    audio_file_name: audioFileName,
                    audio_url: safeAudioUrl,
                    created_at: new Date().toISOString(),
                    is_live_transcript: true
                };

                return await updatePatientRecordDirectly(patientId, {
                    transcripts: [...currentTranscripts, transcriptEntry]
                });
            }
            
            /**
             * Generate a standardized filename for the audio recording
             */
            function generateAudioFilename(patientName, ehrId, audioNumber, extension = 'webm') {
                const sanitizeName = (name) => {
                    if (!name || typeof name !== 'string') return '';
                    const trimmed = name.trim();
                    if (!trimmed) return '';
                    // Keep alphanumeric, spaces, hyphens, underscores, and commas (common in names like "Smith, John")
                    // First remove only truly dangerous characters for filenames
                    const cleaned = trimmed.replace(/[<>:"/\\|?*]/g, '');
                    if (!cleaned) return '';
                    // Replace spaces and multiple special chars with single underscore
                    // Backslashes are doubled on purpose: this runs from a template
                    // literal, where a single one is dropped and the class becomes
                    // [s,] — stripping the letter "s" instead of whitespace.
                    const result = cleaned.replace(/[\\s,]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
                    if (result && result.length > 0) return result;
                    // FALLBACK: If result is empty despite having input, try extracting just alphanumeric chars
                    const alphanumericOnly = trimmed.replace(/[^a-zA-Z0-9]/g, '');
                    if (alphanumericOnly && alphanumericOnly.length > 0) {
                        return alphanumericOnly;
                    }
                    return '';
                };
                
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const safeName = sanitizeName(patientName);
                console.log('🎤 [DEBUG] sanitizeName input:', patientName, ', output:', safeName);
                const safeEhrId = (ehrId && typeof ehrId === 'string' && ehrId.trim()) ? ehrId.trim().replace(/[^\\w-]/g, '') : '';
                console.log('🎤 [DEBUG] safeEhrId:', safeEhrId);
                
                // Only use 'unnamed_patient' if sanitizeName returned empty AND ehrId is empty
                const namePart = safeName || (safeEhrId ? '' : 'unnamed_patient');
                const ehrPart = safeEhrId ? safeEhrId : '';
                return (namePart + (ehrPart ? '-' + ehrPart : '') + "_Audio" + audioNumber + "_" + timestamp + "." + extension).replace(/--/g, '-');
            }

            function getAudioFileExtensionForBlob(blob) {
                const t = (blob && blob.type) || "audio/webm";
                if (t.includes("webm")) return "webm";
                if (t.includes("mp4") || t.includes("mp4a") || t.includes("aac") || t.includes("m4a")) return "mp4";
                if (t.includes("ogg")) return "ogg";
                if (t.includes("wav")) return "wav";
                if (t.includes("mpeg") || t.includes("mp3")) return "mp3";
                return "webm";
            }

            let isCompactMode = false;
            let originalWidth = window.innerWidth;
            let originalHeight = window.innerHeight;
            let compactWidth = Math.max(320, originalWidth * 0.6);
            let compactHeight = Math.max(400, originalHeight * 0.5);

            function toggleCompactMode() {
                isCompactMode = !isCompactMode;
                const body = document.body;
                const resizeBtn = document.getElementById('resizeBtn');
                const compactIcon = document.getElementById('compactIcon');
                const expandIcon = document.getElementById('expandIcon');
                
                if (isCompactMode) {
                    body.classList.add('compact-mode');
                    compactIcon.classList.add('hidden');
                    expandIcon.classList.remove('hidden');
                    window.resizeTo(compactWidth, compactHeight);
                } else {
                    body.classList.remove('compact-mode');
                    compactIcon.classList.remove('hidden');
                    expandIcon.classList.add('hidden');
                    window.resizeTo(originalWidth, originalHeight);
                }
                
                // Focus the window to ensure it's visible
                window.focus();
            }

            // Tab switching
            function switchTab(tabName) {
                console.log('🎤 [DEBUG] switchTab called:', tabName);
                if (tabName === 'results' && !document.getElementById('resultsTab')) return;
                var clinical = ['ap', 'consider', 'scores', 'report'];
                if (clinical.indexOf(tabName) !== -1 && !document.getElementById(tabName + 'Tab')) return;
                var tabBtn = document.getElementById('tab-' + tabName);
                var tabContent = document.getElementById(tabName + 'Tab');
                if (!tabBtn || !tabContent) return;
                document.querySelectorAll('.tab-btn').forEach(btn => btn.classList.remove('active'));
                tabBtn.classList.add('active');
                document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));
                tabContent.classList.add('active');
                console.log('🎤 [DEBUG] Tab switched to:', tabName);
            }

            // Settings Modal Functions
            function openSettingsModal() {
                document.getElementById('settingsModal').classList.add('active');
                loadMicrophones();
            }

            function closeSettingsModal() {
                document.getElementById('settingsModal').classList.remove('active');
                // Stop mic test if running
                if (isTestingMic) {
                    toggleMicTest();
                }
            }

            // One set of mic constraints for Record, Test Microphone and the "Mic:" preview, so all
            // three get the same microphone. ideal, not exact: device ids are per browser and per
            // origin, so a saved id can be stale (unplugged mic, other computer); fall back instead.
            function buildMicConstraints(includeSystemAudio) {
                const audio = {
                    // With tab/screen audio mixed in, browser AEC treats meeting output as "echo"
                    // and strips it, so processing is off for that mode.
                    echoCancellation: !includeSystemAudio,
                    noiseSuppression: !includeSystemAudio,
                    autoGainControl: !includeSystemAudio,
                    sampleRate: 44100
                };
                const micSelect = document.getElementById('micSelect');
                const micId = (micSelect && micSelect.value) || localStorage.getItem('selectedMicDeviceId') || PREFERRED_MIC_DEVICE_ID;
                if (micId) {
                    audio.deviceId = { ideal: micId };
                }
                return audio;
            }

            function setMicInUseStatus(label) {
                const el = document.getElementById('micInUseStatus');
                if (!el) return;
                el.textContent = 'Mic: ' + (label || 'default microphone');
                el.classList.remove('hidden');
            }

            // Before recording, ask for the mic exactly as Record will and show which one the
            // browser picks, so it can be checked with Test Microphone first.
            async function showPickedMic() {
                if (isRecording || isTestingMic) return;
                try {
                    // Don't trigger a permission prompt just to preview; Record will ask.
                    if (navigator.permissions) {
                        const perm = await navigator.permissions.query({ name: 'microphone' });
                        if (perm.state !== 'granted') return;
                    }
                    const systemAudioCb = document.getElementById('includeSystemAudioCheckbox');
                    const probe = await navigator.mediaDevices.getUserMedia({
                        audio: buildMicConstraints(!!(systemAudioCb && systemAudioCb.checked))
                    });
                    const label = probe.getAudioTracks()[0]?.label || '';
                    probe.getTracks().forEach(t => t.stop());
                    setMicInUseStatus(label);
                } catch (e) {
                    console.warn('🎤 Could not preview the microphone:', e && e.name);
                }
            }

            async function loadMicrophones() {
                try {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const microphones = devices.filter(device => device.kind === 'audioinput');
                    const select = document.getElementById('micSelect');
                    
                    select.innerHTML = '';
                    
                    if (microphones.length === 0) {
                        const option = document.createElement('option');
                        option.value = '';
                        option.textContent = 'No microphones found';
                        select.appendChild(option);
                        return;
                    }
                    
                    // Prefer this browser's last choice (set by the dropdown below); the server-saved
                    // preference is never updated by it, so it is only a fallback
                    const savedMicId = localStorage.getItem('selectedMicDeviceId') || PREFERRED_MIC_DEVICE_ID;

                    microphones.forEach((mic, index) => {
                        const option = document.createElement('option');
                        option.value = mic.deviceId;
                        option.textContent = mic.label || 'Microphone ' + (index + 1);
                        select.appendChild(option);
                    });

                    // Select saved mic if it's available on this device
                    if (savedMicId) {
                        const savedOption = Array.from(select.options).find(opt => opt.value === savedMicId);
                        if (savedOption) {
                            select.value = savedMicId;
                        }
                    }

                    // Save selection to localStorage and notify parent to persist to server
                    select.onchange = function() {
                        localStorage.setItem('selectedMicDeviceId', this.value);
                        showPickedMic();
                        if (window.opener) {
                            window.opener.postMessage({
                                type: 'MIC_PREFERENCE_CHANGED',
                                deviceId: this.value,
                                deviceLabel: this.options[this.selectedIndex]?.text || '',
                            }, '*');
                        }
                    };
                } catch (error) {
                    console.error('Error loading microphones:', error);
                    const select = document.getElementById('micSelect');
                    select.innerHTML = '<option value="">Error loading microphones</option>';
                }
            }

            function toggleMicTest() {
                const btn = document.getElementById('testMicBtn');
                const status = document.getElementById('micStatus');
                const levelBar = document.getElementById('micLevelBar');
                
                if (isTestingMic) {
                    // Stop testing
                    stopMicTest();
                    btn.textContent = 'Test Microphone';
                    btn.classList.remove('testing');
                    status.textContent = 'Click "Test Microphone" to hear your mic';
                    status.className = 'mic-status';
                    levelBar.style.width = '0%';
                } else {
                    // Start testing
                    startMicTest();
                    btn.textContent = 'Stop Test';
                    btn.classList.add('testing');
                    status.textContent = 'Listening... Speak into your microphone';
                    status.className = 'mic-status testing';
                }
            }

            async function startMicTest() {
                try {
                    const status = document.getElementById('micStatus');
                    const levelBar = document.getElementById('micLevelBar');

                    // Same constraints as Record (mic only), so this tests the mic Record will get
                    testMicStream = await navigator.mediaDevices.getUserMedia({ audio: buildMicConstraints(false) });
                    const testMicLabel = testMicStream.getAudioTracks()[0]?.label || 'default microphone';
                    setMicInUseStatus(testMicLabel);
                    
                    // Create audio element for direct playback
                    testMicAudioElement = new Audio();
                    testMicAudioElement.srcObject = testMicStream;
                    testMicAudioElement.autoplay = true;
                    testMicAudioElement.volume = 1.0;
                    testMicAudioElement.muted = false;
                    
                    // Set up audio analysis for visualization
                    testMicContext = new (window.AudioContext || window.webkitAudioContext)();
                    testMicSource = testMicContext.createMediaStreamSource(testMicStream);
                    testMicAnalyser = testMicContext.createAnalyser();
                    testMicAnalyser.fftSize = 256;
                    testMicAnalyser.smoothingTimeConstant = 0.8;
                    testMicSource.connect(testMicAnalyser);
                    
                    isTestingMic = true;
                    let lastSoundTime = Date.now();
                    let noSoundAlertShown = false;
                    
                    // Update visualization more frequently for smoother display
                    const updateLevel = () => {
                        if (!isTestingMic) return;
                        
                        const bufferLength = testMicAnalyser.frequencyBinCount;
                        const dataArray = new Uint8Array(bufferLength);
                        testMicAnalyser.getByteFrequencyData(dataArray);
                        
                        // Get the maximum value for better responsiveness
                        let max = 0;
                        for (let i = 0; i < bufferLength; i++) {
                            if (dataArray[i] > max) max = dataArray[i];
                        }
                        
                        const level = Math.min((max / 255) * 100, 100);
                        levelBar.style.width = level + '%';

                        // Check for sound
                        if (max > 10) { // Threshold for silence (adjusted to 10 for better sensitivity)
                            lastSoundTime = Date.now();
                            if (noSoundAlertShown) {
                                const status = document.getElementById('micStatus');
                                status.textContent = 'Testing... Speak into your microphone';
                                status.className = 'mic-status success';
                                noSoundAlertShown = false;
                            }
                        } else {
                            if (Date.now() - lastSoundTime > 3000 && !noSoundAlertShown) {
                                const status = document.getElementById('micStatus');
                                status.textContent = 'No sound detected, please check your microphone is connected';
                                status.className = 'mic-status error';
                                noSoundAlertShown = true;
                            }
                        }
                        
                        testMicLevelInterval = requestAnimationFrame(updateLevel);
                    };
                    
                    testMicLevelInterval = requestAnimationFrame(updateLevel);

                    status.textContent = 'Testing ' + testMicLabel + '... Speak into your microphone';
                    status.classList.remove('testing');
                    status.classList.add('success');
                    
                } catch (error) {
                    console.error('Error starting mic test:', error);
                    document.getElementById('micStatus').textContent = 'Error: ' + error.message;
                    document.getElementById('micStatus').classList.add('testing');
                }
            }

            function stopMicTest() {
                isTestingMic = false;
                
                // Cancel any pending animation frames
                if (testMicLevelInterval) {
                    if (typeof testMicLevelInterval === 'number') {
                        cancelAnimationFrame(testMicLevelInterval);
                    } else {
                        clearInterval(testMicLevelInterval);
                    }
                    testMicLevelInterval = null;
                }
                
                // Stop audio element playback
                if (testMicAudioElement) {
                    testMicAudioElement.pause();
                    testMicAudioElement.srcObject = null;
                    testMicAudioElement = null;
                }
                
                // Disconnect and cleanup audio context
                if (testMicSource) {
                    testMicSource.disconnect();
                    testMicSource = null;
                }
                
                if (testMicAnalyser) {
                    testMicAnalyser.disconnect();
                    testMicAnalyser = null;
                }
                
                if (testMicContext) {
                    testMicContext.close().then(() => {
                        testMicContext = null;
                    }).catch(e => {
                        testMicContext = null;
                    });
                }
                
                // Stop all tracks in the stream
                if (testMicStream) {
                    testMicStream.getTracks().forEach(track => track.stop());
                    testMicStream = null;
                }
            }

            async function copyToClipboard(text) {
                try {
                    await navigator.clipboard.writeText(text);
                    showSuccess('Copied to clipboard!');
                    setTimeout(() => {
                        const msg = document.getElementById('messages');
                        if (msg && msg.innerHTML.includes('Copied to clipboard')) {
                            msg.innerHTML = '';
                        }
                    }, 2000);
                } catch (err) {
                    console.error('Failed to copy:', err);
                    showError('Failed to copy to clipboard');
                }
            }

            function copyTextFromElement(elementId) {
                const el = document.getElementById(elementId);
                if (el) {
                    const text = el.innerText;
                    copyToClipboard(text);
                }
            }

            function copyClinicalReportFromHiddenField() {
                var ta = document.getElementById('clinicalReportPlainField');
                if (ta && ta.value) {
                    copyToClipboard(ta.value);
                    showSuccess('Report copied to clipboard');
                } else {
                    showError('Nothing to copy yet.');
                }
            }

            // Leejoe's "Make this my note" — pushes the SOAP report into the
            // patient's chart note in the parent window. Two-step:
            //   1. copy to clipboard (so Cmd+V works as a fallback)
            //   2. postMessage opener with INSERT_REPORT_AS_NOTE so the parent
            //      can inject directly into the active Quill editor without
            //      requiring the clinician to switch tabs and paste.
            function makeThisMyNote() {
                var ta = document.getElementById('clinicalReportPlainField');
                if (!ta || !ta.value) {
                    showError('Nothing to insert yet — record + generate the report first.');
                    return;
                }
                // Clipboard fallback (clinician can also paste manually).
                copyToClipboard(ta.value);
                if (window.opener && !window.opener.closed) {
                    try {
                        window.opener.postMessage({
                            type: 'INSERT_REPORT_AS_NOTE',
                            payload: { plain: ta.value },
                            sessionId: (window.sessionId || null),
                        }, '*');
                        showSuccess('Sent to chart — check your note. Also copied as a fallback.');
                    } catch (e) {
                        showSuccess('Copied to clipboard — switch to chart + paste (Cmd+V).');
                    }
                } else {
                    showSuccess('Copied to clipboard — switch to chart + paste (Cmd+V).');
                }
            }

            // Approach B convergence: instead of wholesale-replacing the note with
            // the inline SOAP, route the latest recording through the SAME
            // diff-review flow Process Forms uses. Tells the chart window to open
            // ProcessModal; the modal auto-selects the most-recent unprocessed
            // audio (i.e. the one we just saved) and the fill-template engine
            // runs against the existing note content, returning a Cursor-style
            // diff. The user accepts per-edit or Accept All.
            function processThisRecording() {
                if (window.opener && !window.opener.closed) {
                    try {
                        window.opener.postMessage({
                            type: 'OPEN_PROCESS_FOR_RECORDING',
                            payload: { sessionId: (window.sessionId || null) },
                        }, '*');
                        showSuccess('Opening diff review in the chart window…');
                    } catch (e) {
                        showError('Could not open the chart window — focus the chart tab and click Process there.');
                    }
                } else {
                    showError('Chart window not open — focus the chart tab and click Process there.');
                }
            }

            let aiTimerStart = null;
            let lastAiSuccessTime = null;
            let currentAiStatusText = 'AI: idle';
            let currentAiStatusColor = '#4a5568';
            let isGeneratingAi = false;

            function updateAiStatusDisplay() {
                const n = document.getElementById('aiTabsStatus');
                const recTimer = document.getElementById('aiRecorderTimer');
                if (!COPILOT_ENABLED) return;
                
                let text = currentAiStatusText;
                if (isGeneratingAi && aiTimerStart) {
                    const elapsed = Math.round((Date.now() - aiTimerStart) / 1000);
                    text += ' (' + elapsed + 's)';
                } else if (lastAiSuccessTime) {
                    const elapsed = Math.round((Date.now() - lastAiSuccessTime) / 1000);
                    text += ' (updated ' + elapsed + 's ago)';
                }
                
                if (n) {
                    n.textContent = text;
                    n.style.color = currentAiStatusColor;
                }
                if (recTimer) {
                    recTimer.textContent = text;
                    recTimer.style.color = currentAiStatusColor;
                }
            }

            function setAiTabsStatus(msg, color) {
                currentAiStatusText = msg || 'AI: idle';
                currentAiStatusColor = color || '#4a5568';
                
                const lowerText = currentAiStatusText.toLowerCase();
                if (lowerText.includes('generating') || lowerText.includes('triggered') || lowerText.includes('…')) {
                    isGeneratingAi = true;
                    if (!aiTimerStart) {
                        aiTimerStart = Date.now();
                    }
                } else if (lowerText.includes('updated') || lowerText.includes('complete') || lowerText.includes('success')) {
                    isGeneratingAi = false;
                    aiTimerStart = null;
                    lastAiSuccessTime = Date.now();
                } else {
                    isGeneratingAi = false;
                    aiTimerStart = null;
                }
                
                updateAiStatusDisplay();
            }

            function openHelpDeskFromPopup() {
                if (window.opener) {
                    window.opener.postMessage({ type: '${OPEN_HELP_DESK_POST_MESSAGE_TYPE}' }, '*');
                }
            }

            function bindCopilotHelpDeskButton() {
                const btn = document.getElementById('copilotHelpDeskBtn');
                if (btn && !btn.dataset.bound) {
                    btn.dataset.bound = '1';
                    btn.addEventListener('click', openHelpDeskFromPopup);
                }
            }

            function bindCopilotUpgradeButton() {
                const btn = document.getElementById('copilotUpgradeBtn');
                const options = document.getElementById('copilotUpgradeOptions');
                if (btn && !btn.dataset.bound) {
                    btn.dataset.bound = '1';
                    btn.addEventListener('click', function () {
                        if (!options) return;
                        const isHidden = options.style.display === 'none' || !options.style.display;
                        options.style.display = isHidden ? 'flex' : 'none';
                    });
                }
            }

            function setCopilotDisabledState() {
                const banner = document.getElementById('copilotDisabledBanner');
                if (banner) banner.style.display = 'block';
                bindCopilotHelpDeskButton();
                bindCopilotUpgradeButton();
                setAiTabsStatus('AI: disabled (Upgrade plan)', '#b7791f');
                clearClinicalTabPanels('Copilot is disabled for your account. Please contact support to enable A&P, Consider, Scores, and Report.');
                const copilotDebugDisabled = {
                    userRole,
                    roleTokens,
                    storageRoleRaw,
                    storageRoleTokens,
                    effectiveRole,
                    normalizedEffectiveRole,
                    COPILOT_ENABLED,
                };
                console.warn('🎤 [COPILOT-DEBUG] setCopilotDisabledState triggered', copilotDebugDisabled);
                console.warn('🎤 [COPILOT-DEBUG] setCopilotDisabledState JSON:', JSON.stringify(copilotDebugDisabled));
            }

            // POST /api/generate-ai-recap/ model_choice: 0 = Haiku (live), 1–2 = Sonnet (post-save) — see backend claude_chat_v3 map
            var COPILOT_LIVE_CLINICAL_MODEL_CHOICE = 0;
            var COPILOT_POST_SAVE_CLINICAL_MODEL_CHOICE = 1;

            async function runAiFromSavedTranscript(transcriptValue) {
                if (!COPILOT_ENABLED) {
                    setCopilotDisabledState();
                    return;
                }
                if (!AUTO_COPIOLT_ENABLED) {
                    setAiTabsStatus('AI: auto-copilot disabled', '#b7791f');
                    return;
                }
                clearLiveClinicalDebounceTimer();
                liveClinicalAiQueuedFlush = false;
                bumpLiveClinicalAiGeneration();
                liveClinicalAiLastFingerprint = '';
                var t = (transcriptValue || '').trim();
                if (!t) {
                    setAiTabsStatus('AI: skipped (no transcript text)', '#b7791f');
                    return;
                }
                try {
                    console.log('🎤 [AI-TABS] Starting AI generation from saved transcript (Sonnet). length=', t.length);
                    setAiTabsStatus('AI: generating SOAP sections in parallel (Sonnet)…', '#2b6cb0');
                    showAiRecapGenerating();
                    clearClinicalTabPanels('Generating AI clinical tabs...');
                    var baseUrl = getBackendBaseUrl();
                    if (!baseUrl) {
                        throw new Error('Backend URL unavailable for AI generation');
                    }
                    // A6: parallel A5 + template-fill.
                    // - A5 (/api/generate-soap-by-section/) → structured per-section JSON for the
                    //   A&P / Consider / Scores tabs (each tab consumes one key).
                    // - A6 (/api/recorder/fill-template/) → filled blank SOAP template as plain
                    //   text. The SOAP note (full) tab renders this directly, bypassing the
                    //   composer + schema entirely. Sole-owner of the SOAP tab; live Haiku and
                    //   parent recap calls cannot touch it.
                    var commonBody = {
                        transcript: t,
                        model_choice: COPILOT_POST_SAVE_CLINICAL_MODEL_CHOICE,
                        userId: EXTERNAL_USER_ID,
                        patientId: patientId,
                        sessionId: recordingSessionId,
                        schema: EXTERNAL_DB_SCHEMA,
                        userJwtToken: EXTERNAL_USER_JWT_TOKEN,
                        // The clinician's date, not the UTC server's — see utils/localToday.
                        clientToday: localToday(),
                    };
                    var existingTabs = buildExistingClinicalTabsContext();
                    var a5Body = Object.assign({}, commonBody);
                    if (existingTabs && existingTabs.key_info_array && existingTabs.key_info_array.length > 0) {
                        a5Body.existing_clinical_tabs = existingTabs;
                    }

                    // A5 retired 2026-07-26: its A&P/Consider/Scores tabs have been hidden
                    // since Stage 1b (6/22) and the backend lost _call_one_section in the
                    // July merge, so every call 500'd. The visible SOAP Note tab is fully
                    // owned by the A6 template-fill below. Downstream a5Result handling is
                    // null-safe, so a resolved null keeps the flow untouched.
                    var a5Promise = Promise.resolve(null);

                    var fillTemplatePromise = fetch(baseUrl + '/api/recorder/fill-template/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(commonBody),
                    }).then(function (r) { return r.json(); });

                    var settled = await Promise.allSettled([a5Promise, fillTemplatePromise]);
                    var a5Result = settled[0].status === 'fulfilled' ? settled[0].value : null;
                    var fillResult = settled[1].status === 'fulfilled' ? settled[1].value : null;

                    if (a5Result && a5Result.error) console.warn('🎤 [AI-TABS] A5 error:', a5Result.error);
                    if (fillResult && fillResult.error) console.warn('🎤 [AI-TABS] template-fill error:', fillResult.error);

                    if (a5Result && a5Result.recap) {
                        showAiRecap(a5Result.recap);
                    }
                    if (a5Result && a5Result.clinical_tabs) {
                        renderClinicalTabsFromLlm(a5Result.clinical_tabs);
                    }
                    if (fillResult && fillResult.filled_template) {
                        renderSoapNoteFromTemplate(fillResult.filled_template);
                    }

                    setAiTabsStatus('AI: SOAP sections updated (A5 + template-fill v6)', '#2f855a');
                    // Stage 1b (Leejoe 2026-06-22): A&P/Scores/Orders tabs hidden;
                    // SOAP Note is the only surviving clinical-results tab.
                    switchTab('report');
                    void persistCopilotTabsSnapshot('run_ai_complete');
                } catch (e) {
                    console.error('🎤 [AI-TABS] Failed to generate:', e);
                    setAiTabsStatus('AI: failed - ' + ((e && e.message) ? e.message : 'unknown error'), '#c53030');
                    showAiRecapError((e && e.message) ? e.message : 'AI recap failed');
                    showError((e && e.message) ? e.message : 'Clinical tabs generation failed');
                }
            }

            // Store audio URL for transcription
            let savedAudioUrl = null;
            // True while in-popup Vosk/device transcription is running (patient audio upload or "Transcribe with Vosk").
            let deviceTranscriptionInProgress = false;
            // Track whether the live transcript was already auto-saved by the parent window
            let liveTranscriptAutoSaved = false;

            function updatePatientInfoBanner() {
                const banner = document.getElementById('patientInfoBanner');
                if (!banner) return;
                const label = '${getLabel(userRole, 'entity')}';
                const resolvedName = patientName || 'Unknown ' + label;
                // scheduledDate is locked at window-open time (parent template
                // interpolation, line ~2411). Sync Patient only refreshes
                // patientId/Name/ehrId from the parent, so we re-use the
                // original visit date — switching visits requires reopening
                // the window anyway.
                const resolvedDate = scheduledDate || '—';
                banner.innerHTML =
                    '<strong>' + label + ':</strong> ' + resolvedName +
                    ' | <strong>Date:</strong> ' + resolvedDate +
                    ' <button id="syncPatientBtn" onclick="requestPatientSync()" style="margin-left:8px;padding:2px 8px;font-size:12px;border:1px solid #cbd5e0;border-radius:999px;background:#edf2f7;color:#2d3748;cursor:pointer;">Sync Patient</button>';
            }

            function requestPatientSync() {
                try {
                    console.log('🎤 [POPUP] requestPatientSync triggered. window.opener is:', !!window.opener);
                    if (!window.opener) {
                        console.warn('🎤 [POPUP] Sync failed: window.opener is not available.');
                        showError('Parent window is not available.');
                        return;
                    }
                    if ((mediaRecorder && (mediaRecorder.state === 'recording' || mediaRecorder.state === 'paused')) ||
                        (typeof externalRecordingState !== 'undefined' && (externalRecordingState === 'recording' || externalRecordingState === 'paused'))) {
                        alert("Please make sure to stop and save the audio before syncing");
                        return;
                    }
                    console.log('🎤 [POPUP] Posting REQUEST_PATIENT_SYNC to opener window...');
                    window.opener.postMessage({ type: 'REQUEST_PATIENT_SYNC' }, '*');
                    showSuccess('Sync request sent. Waiting for latest patient...');
                } catch (err) {
                    console.error('🎤 [POPUP] Failed to request patient sync:', err);
                    showError('Failed to request patient sync.');
                }
            }

            let patientAudioUploadBusy = false;

            function openPatientAudioModal() {
                const modal = document.getElementById('patientAudioModal');
                if (!modal) return;
                modal.classList.add('active');
                refreshPatientAudioModalList();
            }

            function closePatientAudioModal() {
                const modal = document.getElementById('patientAudioModal');
                if (!modal) return;
                modal.classList.remove('active');
            }

            (function wirePatientAudioModalBackdrop() {
                const modal = document.getElementById('patientAudioModal');
                if (!modal) return;
                modal.addEventListener('click', function (ev) {
                    if (ev.target === modal) closePatientAudioModal();
                });
            })();

            function pamSetAudioManagerHeaderCount(displayText) {
                const chip = document.getElementById('patientAudioHeaderCount');
                if (chip) chip.textContent = displayText;
            }

            function pamFormatDuration(seconds) {
                if (seconds === undefined || seconds === null || !isFinite(Number(seconds)) || Number(seconds) <= 0) {
                    return '—';
                }
                var sec = Number(seconds);
                var m = Math.floor(sec / 60);
                var s = Math.round(sec % 60);
                return m + ':' + (s < 10 ? '0' : '') + s;
            }

            function pamFormatDate(dateStr) {
                if (!dateStr) return '—';
                try {
                    return new Date(dateStr).toLocaleString();
                } catch (e) {
                    return '—';
                }
            }

            function pamTranscriptBodyText(t) {
                if (!t || t.transcript === undefined || t.transcript === null) return '';
                var tr = t.transcript;
                if (Array.isArray(tr)) {
                    return tr.map(function (line) {
                        if (typeof line === 'string') return line;
                        return (line && (line.text || line.transcript || '')) || '';
                    }).join('\\n').trim();
                }
                return String(tr).trim();
            }

            /** Match AudioManagerModal / audioMetadata: session_id, audio_url, then filename. */
            function pamFindTranscriptForAudio(audioEntry, transcriptsArr) {
                if (!audioEntry || !transcriptsArr || !transcriptsArr.length) return null;
                var audioSessionId = audioEntry.session_id;
                var audioFilename = audioEntry.filename || audioEntry.name || '';
                var audioUrl = '';
                try {
                    audioUrl = String(audioEntry.url || '').split('?')[0];
                } catch (e) {
                    audioUrl = '';
                }
                var i;
                if (audioSessionId) {
                    for (i = 0; i < transcriptsArr.length; i++) {
                        var t = transcriptsArr[i];
                        if (!t) continue;
                        var ts = t.session_id || (t.metadata && t.metadata.session_id);
                        if (ts && ts === audioSessionId) return t;
                    }
                }
                if (audioUrl) {
                    for (i = 0; i < transcriptsArr.length; i++) {
                        var tUrl = transcriptsArr[i];
                        if (!tUrl || !tUrl.audio_url) continue;
                        if (String(tUrl.audio_url).split('?')[0] === audioUrl) return tUrl;
                    }
                }
                if (audioFilename) {
                    for (i = 0; i < transcriptsArr.length; i++) {
                        var tf = transcriptsArr[i];
                        if (!tf) continue;
                        var tFilename = tf.filename || tf.audio_file_name || '';
                        if (!tFilename) continue;
                        var audioBase = audioFilename.replace(/\\.(webm|mp3|wav|m4a|ogg)$/i, '');
                        var tBase = tFilename.replace(/\\.json$/i, '').replace(/\\.(webm|mp3|wav|m4a|ogg)$/i, '');
                        if (audioBase === tBase || audioBase.indexOf(tBase) >= 0 || tBase.indexOf(audioBase) >= 0) {
                            return tf;
                        }
                    }
                }
                return null;
            }

            /** Same rules as PatientPage visibleAudios: only urls under audio/ or backup-audio/ for this visit date + restored rows. */
            function pamVisibleAudiosForVisit(moreAudiosArr) {
                if (!Array.isArray(moreAudiosArr) || moreAudiosArr.length === 0) return [];
                var schemaRaw = (typeof EXTERNAL_DB_SCHEMA !== 'undefined' && EXTERNAL_DB_SCHEMA) ? String(EXTERNAL_DB_SCHEMA) : 'public';
                var schema = schemaRaw.split(',')[0].trim();
                var visit = String(typeof scheduledDate !== 'undefined' ? scheduledDate : '').trim();
                if (!schema || !visit) return moreAudiosArr;
                var year = visit.split('-')[0];
                var expectedAudioSegment = '/audio/' + schema + '/' + year + '/' + visit + '/';
                var expectedBackupSegment = '/backup-audio/' + schema + '/' + year + '/' + visit + '/';
                return moreAudiosArr.filter(function (a) {
                    if (!a) return false;
                    if (a.restored_from_backup || a.source === 'backup_restore') return true;
                    var url = typeof a.url === 'string' ? a.url : '';
                    if (!url) return false;
                    return url.indexOf(expectedAudioSegment) >= 0 || url.indexOf(expectedBackupSegment) >= 0;
                });
            }

            async function refreshPatientAudioModalList() {
                const listEl = document.getElementById('patientAudioList');
                if (!listEl) return;
                pamSetAudioManagerHeaderCount('—');
                if (!patientId) {
                    listEl.innerHTML = '<p style="color:#c53030;margin:1.5rem 0;text-align:center;">No patient ID. Use Sync Patient from the banner.</p>';
                    pamSetAudioManagerHeaderCount('0 Audios');
                    return;
                }
                listEl.innerHTML = '<p style="color:#718096;margin:1.5rem 0;text-align:center;">Loading…</p>';
                const r = await fetchPatientRecordDirectly(patientId);
                if (!r.success || !r.record) {
                    listEl.innerHTML = '<p style="color:#c53030;margin:1.5rem 0;text-align:center;">Could not load this patient\\'s audio list.</p>';
                    pamSetAudioManagerHeaderCount('—');
                    return;
                }
                var rawAudios = Array.isArray(r.record.more_audios) ? r.record.more_audios : [];
                const audiosForVisit = pamVisibleAudiosForVisit(rawAudios);
                // Sort newest-first by uploaded_at / recording_date / created_at so the
                // most recent recording is at the top — the only reason a clinician
                // opens this list mid-encounter is to grab a fresh one.
                const audios = audiosForVisit.slice().sort(function (a, b) {
                    const aT = (a && (a.uploaded_at || a.recording_date || a.created_at)) || '';
                    const bT = (b && (b.uploaded_at || b.recording_date || b.created_at)) || '';
                    if (aT === bT) return 0;
                    return aT < bT ? 1 : -1;
                });
                const transcriptsRaw = r.record.transcripts;
                const transcripts = Array.isArray(transcriptsRaw)
                    ? transcriptsRaw
                    : (transcriptsRaw ? [transcriptsRaw] : []);

                pamSetAudioManagerHeaderCount(audios.length + ' Audio' + (audios.length === 1 ? '' : 's'));

                if (audios.length === 0) {
                    var emptyMsg =
                        '<div style="text-align:center;padding:2rem 0.5rem;">' +
                        '<p style="color:#718096;margin:0;font-size:0.9375rem;">' +
                        (rawAudios.length > 0
                            ? ('No audio for this visit' +
                                (scheduledDate ? ' (' + String(scheduledDate) + ')' : '') +
                                '. This patient may have recordings on other days.')
                            : 'No audio files for this patient.') +
                        '</p></div>';
                    listEl.innerHTML = emptyMsg;
                    return;
                }

                const wrap = document.createElement('div');
                wrap.className = 'patient-audio-accordion-wrap';

                audios.forEach(function (entry, idx) {
                    const url = (entry && entry.url) || '';
                    const name = (entry && (entry.name || entry.displayName)) || ('Audio ' + (idx + 1));
                    const transcript = pamFindTranscriptForAudio(entry, transcripts);
                    const hasTranscript = !!(transcript && pamTranscriptBodyText(transcript).length > 0);
                    const durationVal = entry && entry.duration !== undefined ? entry.duration : null;

                    const details = document.createElement('details');
                    details.className = 'patient-audio-acc';
                    if (idx === 0) details.open = true;

                    const summary = document.createElement('summary');
                    const badge = document.createElement('span');
                    badge.className = 'pam-badge ' + (hasTranscript ? 'pam-badge-green' : 'pam-badge-gray');
                    badge.textContent = hasTranscript ? 'Transcribed' : 'Not transcribed';

                    const titleEl = document.createElement('span');
                    titleEl.className = 'pam-acc-title';
                    titleEl.textContent = name;

                    const durEl = document.createElement('span');
                    durEl.className = 'pam-acc-dur';
                    durEl.textContent = pamFormatDuration(durationVal);

                    const chevron = document.createElement('span');
                    chevron.className = 'pam-chevron';
                    chevron.setAttribute('aria-hidden', 'true');
                    chevron.textContent = '▼';

                    summary.appendChild(badge);
                    summary.appendChild(titleEl);
                    summary.appendChild(durEl);
                    summary.appendChild(chevron);
                    details.appendChild(summary);

                    const body = document.createElement('div');
                    body.className = 'patient-audio-acc-body';

                    const bodyInner = document.createElement('div');
                    bodyInner.className = 'patient-audio-acc-body-inner';

                    if (url) {
                        const shell = document.createElement('div');
                        shell.className = 'pam-player-shell';
                        const audio = document.createElement('audio');
                        audio.controls = true;
                        audio.preload = 'none';
                        audio.src = url;
                        shell.appendChild(audio);
                        bodyInner.appendChild(shell);
                    } else {
                        const noUrl = document.createElement('p');
                        noUrl.style.margin = '0';
                        noUrl.style.color = '#a0aec0';
                        noUrl.style.fontSize = '0.8125rem';
                        noUrl.textContent = 'No playback URL';
                        bodyInner.appendChild(noUrl);
                    }

                    const meta = document.createElement('div');
                    meta.className = 'pam-meta-row';
                    const recorded = entry && (entry.uploaded_at || entry.recording_date || entry.created_at);
                    meta.textContent = 'Recorded: ' + pamFormatDate(recorded);
                    // 2026-06-29 Leejoe — source tag pill (e.g.
                    // "fixture_seed_synthetic") removed. The Audio
                    // Manager is a file picker; provenance tags belong
                    // in audit logs, not the operational surface.
                    bodyInner.appendChild(meta);

                    // 2026-06-29 Leejoe — transcript label + Corti/Live
                    // badge + N-facts badge + snippet preview all
                    // removed. The Audio Manager popup is a FILE PICKER
                    // (per [[project_chatgpt_workflow_thesis]] split-
                    // screen rule) — note summaries and transcript
                    // previews belong in the chart's Copilot panel, not
                    // duplicated in the recorder modal. Old block
                    // rendered: pam-transcript-block-label + Corti/Live
                    // badge + facts count + snippet (up to 400 chars).
                    // All killed. Update Note button below handles the
                    // "do something with this transcribed audio" action.

                    const actions = document.createElement('div');
                    actions.className = 'pam-actions';
                    // 2026-06-29 Leejoe — Update Note hoisted to FIRST
                    // in actions so the primary action is right under
                    // the "Recorded: <date>" line. Was buried after
                    // Select Audio + Transcribe in old ordering.
                    if (hasTranscript) {
                        const processPriorBtn = document.createElement('button');
                        processPriorBtn.type = 'button';
                        processPriorBtn.style.cssText = 'background:#10b981;color:#fff;border:none;border-radius:6px;font-weight:600;padding:0.32rem 0.7rem;font-size:0.78rem;cursor:pointer;';
                        processPriorBtn.textContent = '✓ Update Note with this recording';
                        processPriorBtn.title = 'Update the note with this recording — review the diff against your existing note in the Copilot panel.';
                        processPriorBtn.onclick = function () {
                            const sessionIdForRecording = (entry && (entry.session_id || entry.audio_index || entry.url)) || null;
                            closePatientAudioModal();
                            if (window.opener && !window.opener.closed) {
                                try {
                                    // autoGenerate: chart should skip the source-picker
                                    // step entirely and run fill-template against this
                                    // recording immediately, then show the diff modal.
                                    // We already established the intent — extra clicks
                                    // through the source picker are friction.
                                    window.opener.postMessage({
                                        type: 'OPEN_PROCESS_FOR_RECORDING',
                                        payload: {
                                            sessionId: sessionIdForRecording ? String(sessionIdForRecording) : null,
                                            autoGenerate: true,
                                        },
                                    }, '*');
                                    // Bring the chart window to the front so the diff
                                    // modal isn't hidden behind this popup.
                                    try { window.opener.focus(); } catch (_) {}
                                    showSuccess('Processing in the chart — diff will appear there.');
                                } catch (e) {
                                    showError('Could not signal the chart window. Open Process Forms manually.');
                                }
                            } else {
                                showError('Chart window is closed. Re-open the chart, then try again.');
                            }
                        };
                        actions.appendChild(processPriorBtn);
                    }
                    // 2026-06-29 — Select Audio button killed per Leejoe:
                    // "I have NEVER used the select audio successfully
                    // for anything." When transcript exists, the button
                    // re-transcribes (waste). When no transcript, the
                    // purple "Transcribe" button below does the same
                    // requestTranscriptionForUrl call already — so
                    // "Select & Transcribe" was duplicating it.
                    // Result: one obvious action per state.
                    if (url && !hasTranscript) {
                        const btn = document.createElement('button');
                        btn.type = 'button';
                        btn.className = 'btn-pam-purple';
                        btn.textContent = 'Transcribe';
                        btn.onclick = function () {
                            closePatientAudioModal();
                            requestTranscriptionForUrl(
                                url,
                                Math.floor(Number(entry.duration) || 0),
                                name,
                            );
                        };
                        actions.appendChild(btn);
                    }
                    bodyInner.appendChild(actions);

                    body.appendChild(bodyInner);
                    details.appendChild(body);
                    wrap.appendChild(details);
                });

                listEl.innerHTML = '';
                listEl.appendChild(wrap);
            }

            function extensionFromUploadName(filename) {
                const m = String(filename || '').match(/\\.([a-zA-Z0-9]+)$/);
                if (!m) return 'webm';
                let ext = m[1].toLowerCase();
                if (ext === 'm4a') ext = 'mp4';
                if (['webm', 'wav', 'mp3', 'mp4', 'ogg', 'mpeg'].indexOf(ext) < 0) return 'webm';
                return ext;
            }

            function triggerPatientAudioFilePick() {
                const inp = document.getElementById('patientAudioFileInput');
                if (inp) inp.click();
            }

            async function onPatientAudioFileSelected(evt) {
                const f = evt.target && evt.target.files && evt.target.files[0];
                if (evt.target) evt.target.value = '';
                if (!f) return;
                await handleExternalPatientAudioUpload(f);
            }

            function showResultsProcessingUi(message) {
                if (HIDE_RESULTS_TAB) {
                    showLoading(message || 'Processing…');
                    return;
                }
                const ps = document.getElementById('processingState');
                const pm = document.getElementById('processingMessage');
                if (ps) ps.classList.remove('hidden');
                if (pm) pm.textContent = message || 'Processing…';
            }

            function hideResultsProcessingUi() {
                if (HIDE_RESULTS_TAB) {
                    hideLoading();
                    return;
                }
                const ps = document.getElementById('processingState');
                if (ps) ps.classList.add('hidden');
            }

            async function requestTranscriptionForUrl(audioUrl, durationSeconds, label) {
                if (!audioUrl) {
                    showError('No audio URL for transcription.');
                    return;
                }
                savedAudioUrl = audioUrl;
                try {
                    showLoading('Loading audio for transcription…');
                    const res = await fetch(audioUrl, { mode: 'cors' });
                    if (!res.ok) throw new Error('Could not download this audio file.');
                    const blob = await res.blob();
                    hideLoading();
                    const baseName = String(label || 'audio').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 80) || 'audio';
                    let ext = 'webm';
                    try {
                        ext = extensionFromUploadName(new URL(audioUrl, window.location.href).pathname) || ext;
                    } catch (_) {
                        ext = extensionFromUploadName(blob.type ? 'x.' + (blob.type.split('/')[1] || 'webm') : '') || ext;
                    }
                    const file = new File([blob], baseName + '.' + ext, { type: blob.type || 'audio/' + ext });
                    await runTenantUploadTranscription(
                        audioUrl,
                        file,
                        durationSeconds,
                        label || baseName,
                    );
                } catch (e) {
                    hideLoading();
                    hideResultsProcessingUi();
                    console.error('🎤 [VOSK] requestTranscriptionForUrl failed:', e);
                    showError((e && e.message) ? e.message : 'Transcription failed');
                }
            }

            async function handleExternalPatientAudioUpload(file) {
                if (patientAudioUploadBusy) return;
                if (!patientId) {
                    showError('No patient selected.');
                    return;
                }
                if (!EXTERNAL_SUPABASE_SERVICE_KEY || !EXTERNAL_AUDIO_BUCKET_NAME) {
                    showError('Upload is not configured (missing Supabase credentials).');
                    return;
                }
                const maxBytes = ${injectedMaxAudioUploadBytes};
                if (file.size > maxBytes) {
                    showError('File is too large (max ${injectedMaxUploadLabelText}).');
                    return;
                }
                const mime = file.type || '';
                const okByName = /\\.(webm|wav|mp3|m4a|mp4|ogg|aac|flac|mpeg)$/i.test(file.name);
                if (!okByName && mime.indexOf('audio') !== 0 && mime.indexOf('video') !== 0) {
                    showError('Please choose a supported audio file.');
                    return;
                }
                patientAudioUploadBusy = true;
                closePatientAudioModal();
                try {
                    showLoading('Uploading audio…');
                    await ensureRowSchema();
                    const year = scheduledDate ? scheduledDate.split('-')[0] : new Date().getFullYear().toString();
                    const date = scheduledDate || new Date().toISOString().split('T')[0];
                    const nextAudioIndex = await getNextAudioIndexFromStorage(
                        EXTERNAL_AUDIO_BUCKET_NAME,
                        EXTERNAL_DB_SCHEMA || 'public',
                        year,
                        date,
                        patientName
                    );
                    const ext = extensionFromUploadName(file.name);
                    const filename = generateAudioFilename(patientName, injectedEhrId, nextAudioIndex, ext);
                    const contentType = file.type || ('audio/' + (ext === 'mp4' ? 'mp4' : ext));
                    const uploadResult = await uploadAudioDirectly(
                        file,
                        filename,
                        EXTERNAL_AUDIO_BUCKET_NAME,
                        null,
                        EXTERNAL_SUPABASE_SERVICE_KEY,
                        { schemaName: EXTERNAL_DB_SCHEMA || 'public', year: year, date: date }
                    );
                    if (!uploadResult.success) {
                        throw new Error(uploadResult.error || 'Upload failed');
                    }
                    const finalAudioUrl = uploadResult.url;
                    const label = String(file.name || '').replace(/\\.[^/.]+$/, '') || getExternalAudioLabel();
                    const durationSeconds = await estimateDurationSecondsFromFile(file);
                    showLoading('Saving to patient record…');
                    const patientAudioSave = await saveAudioToPatientRecordDirectly(
                        finalAudioUrl,
                        durationSeconds,
                        contentType,
                        label
                    );
                    if (!patientAudioSave.success) {
                        throw new Error(patientAudioSave.error || 'Failed to update patient record');
                    }
                    hideLoading();
                    showSuccess('Audio uploaded. Starting transcription…');
                    savedAudioUrl = finalAudioUrl;
                    await runTenantUploadTranscription(finalAudioUrl, file, durationSeconds, label);
                } catch (e) {
                    hideLoading();
                    hideResultsProcessingUi();
                    console.error('🎤 [UPLOAD] External audio failed:', e);
                    showError((e && e.message) ? e.message : 'Upload failed');
                } finally {
                    patientAudioUploadBusy = false;
                }
            }

            function estimateDurationSecondsFromFile(file) {
                return new Promise(function (resolve) {
                    const url = URL.createObjectURL(file);
                    const audio = new Audio();
                    let settled = false;
                    const finish = function (sec) {
                        if (settled) return;
                        settled = true;
                        try { URL.revokeObjectURL(url); } catch (_) {}
                        resolve(Math.max(0, Math.floor(sec || 0)));
                    };
                    audio.preload = 'metadata';
                    audio.onloadedmetadata = function () {
                        const d = audio.duration;
                        finish(Number.isFinite(d) ? d : 0);
                    };
                    audio.onerror = function () { finish(0); };
                    setTimeout(function () { finish(0); }, 10000);
                    audio.src = url;
                });
            }

            async function transcribeUploadedFileWithVosk(file) {
                const handler = await ensureClientSideSystemStt();
                if (!handler || typeof handler.transcribePcm !== 'function') {
                    throw new Error('Local transcription (Vosk) is not available. Could not reach the Vosk service. Check REACT_APP_VOSK_SERVICE_ORIGIN or your network.');
                }
                const nameHint = file && file.name ? file.name : '(upload)';
                console.log(
                    '🎤 [TRANSCRIPTION] transcribe job — engine: Vosk (in-browser) | model:',
                    handler.voskModelLabel || 'vosk-browser',
                    '| file:',
                    nameHint,
                );
                const arr = await file.arrayBuffer();
                const AC = window.AudioContext || window.webkitAudioContext;
                const ac = new AC();
                try {
                    const decoded = await ac.decodeAudioData(arr.slice(0));
                    const pcm = decoded.getChannelData(0);
                    const rate = decoded.sampleRate || 16000;
                    if (typeof handler.transcribeFullFilePcm === 'function') {
                        const text = await handler.transcribeFullFilePcm(pcm, rate);
                        return String(text || '').trim();
                    }
                    const out = await handler.transcribePcm(pcm, { inputSampleRate: rate });
                    return (out && out.text) ? String(out.text).trim() : '';
                } finally {
                    try { await ac.close(); } catch (_) {}
                }
            }

            async function runTenantUploadTranscription(audioUrl, file, durationSeconds, label) {
                deviceTranscriptionInProgress = true;
                showResultsProcessingUi('Transcribing on device (Vosk)…');
                if (!HIDE_RESULTS_TAB) {
                    document.getElementById('resultsContainer').innerHTML = '';
                    document.getElementById('resultsActions').classList.add('hidden');
                    switchTab('liveTranscript');
                } else {
                    switchTab('liveTranscript');
                }
                try {
                    const text = await transcribeUploadedFileWithVosk(file);
                    if (text && text.trim()) {
                        hideResultsProcessingUi();
                        if (!HIDE_RESULTS_TAB) {
                            renderResults({
                                transcript: [text.trim()],
                                facts: [],
                                soap_notes: { transcript_only: text.trim() },
                            });
                            switchTab('liveTranscript');
                        } else {
                            const tab = document.getElementById('liveTranscriptTabContent');
                            if (tab) tab.textContent = text.trim();
                            switchTab('liveTranscript');
                        }
                        showSuccess('Local transcription complete.');
                        await saveLiveTranscriptDirectly(audioUrl, text, durationSeconds, null, label);
                        if (COPILOT_ENABLED && text.trim()) {
                            runAiFromSavedTranscript(text.trim());
                        }
                    } else {
                        throw new Error('No speech recognized. Try another file or check the Vosk model.');
                    }
                } catch (e) {
                    hideResultsProcessingUi();
                    showError((e && e.message) ? e.message : 'Transcription failed');
                } finally {
                    deviceTranscriptionInProgress = false;
                }
            }
            
            // Handle incoming messages
            window.addEventListener('message', (event) => {
                const data = event.data;
                console.log('🎤 [POPUP] Window received postMessage event:', {
                    origin: event.origin,
                    sourceExists: !!event.source,
                    data: data,
                });
                if (data) {
                    if (data.type === 'AUDIO_SAVED_SUCCESS') {
                        console.log('🎤 Received audio URL from parent window:', data.audioUrl);
                        savedAudioUrl = data.audioUrl || data.finalAudioUrl || savedAudioUrl;
                        if (data.clinicalAudioSlot != null && data.clinicalAudioSlot >= 1) {
                            lastSavedCopilotAudioSlot = data.clinicalAudioSlot;
                        } else {
                            var parsedCopilotSlot = parseClinicalAudioSlotFromUrl(data.audioUrl || data.finalAudioUrl || savedAudioUrl);
                            if (parsedCopilotSlot != null) lastSavedCopilotAudioSlot = parsedCopilotSlot;
                        }
                        if (data.storageFolderYear) copilotPersistFolderYear = String(data.storageFolderYear);
                        if (data.storageFolderDate) copilotPersistFolderDate = String(data.storageFolderDate);
                        var savedTranscriptText = (data.liveTranscriptText || '').trim()
                            || (buildCombinedLiveTranscriptForAi() || '').trim()
                            || (lastCapturedTranscript || '').trim();
                        // v1 "I'm in a hurry": auto-run AI on the live transcript for ALL users (tenant + clinical) the
                        // moment audio is saved, so SOAP appears without the user clicking through the prompt. Vosk
                        // stays available via the prompt if the user wants higher-quality on-device transcription.
                        var runAiNow = (
                            !!savedTranscriptText &&
                            !deviceTranscriptionInProgress &&
                            (isTenantUser || COPILOT_ENABLED)
                        );
                        if (runAiNow) {
                            setAiTabsStatus('AI: triggered from AUDIO_SAVED_SUCCESS', '#2b6cb0');
                            runAiFromSavedTranscript(savedTranscriptText);
                        } else if (deviceTranscriptionInProgress) {
                            setAiTabsStatus('AI: waiting for transcription to finish…', '#718096');
                        } else if (!savedTranscriptText) {
                            console.warn('🎤 [AI-TABS] No transcript text available at save time');
                            setAiTabsStatus('AI: no transcript at save event', '#b7791f');
                        }
                        if (isTenantUser) {
                            // Tenant roles: no Corti, only live transcript. Show success and stay on recorder tab.
                            const transcriptForStatus = (lastCapturedTranscript || transcriptText || '').trim();
                            const liveSaved = getLiveTranscriptionSetting() && transcriptForStatus.length > 0;
                            showSuccess(liveSaved ? 'Audio saved. Live transcript was captured.' : 'Audio saved successfully.');
                        } else {
                            showTranscriptionPrompt();
                        }
                    } else if (data.type === 'AUDIO_SAVE_FAILED') {
                        console.error('🎤 [EXTERNAL] Audio save failed:', data.error, 'Retries:', data.retryAttempts);
                        showError('Failed to save audio after ' + (data.retryAttempts || 0) + ' attempts: ' + (data.error || 'Unknown error'));
                        // Re-enable the save button so user can retry
                        hasFinalSaveBeenSent = false;
                        setIsSaving(false);
                        updateUI('download');
                    } else if (data.type === 'TRANSCRIPTION_RESULTS') {
                        console.log('Received transcription results:', data.data);
                        if (!HIDE_RESULTS_TAB) {
                            const ps = document.getElementById('processingState');
                            if (ps) ps.classList.add('hidden');
                            hideTranscriptionPrompt();
                            const ra = document.getElementById('resultsActions');
                            if (ra) ra.classList.remove('hidden');
                            renderResults(data.data);
                            switchTab('liveTranscript');
                            if (COPILOT_ENABLED && data.data && !isTenantUser) {
                                var tr = data.data.transcript;
                                var flat = '';
                                if (Array.isArray(tr)) {
                                    flat = tr.map(function (line) {
                                        return typeof line === 'string' ? line : (line.text || line.transcript || '');
                                    }).join('\\n').trim();
                                } else if (typeof tr === 'string') {
                                    flat = tr.trim();
                                }
                                if (flat) {
                                    runAiFromSavedTranscript(flat);
                                }
                            }
                        }
                    } else if (data.type === 'TRANSCRIPTION_STARTED') {
                        console.log('Transcription started');
                        if (!HIDE_RESULTS_TAB) {
                            switchTab('liveTranscript');
                            hideTranscriptionPrompt();
                            const ps = document.getElementById('processingState');
                            if (ps) ps.classList.remove('hidden');
                            const rc = document.getElementById('resultsContainer');
                            if (rc) rc.innerHTML = '';
                            clearClinicalTabPanels('Processing transcription…');
                            const ra = document.getElementById('resultsActions');
                            if (ra) ra.classList.add('hidden');
                            const pm = document.getElementById('processingMessage');
                            if (pm) pm.textContent = 'Initializing transcription...';
                        }
                    } else if (data.type === 'TRANSCRIPTION_PROGRESS') {
                        console.log('Transcription progress:', data.data);
                        if (!HIDE_RESULTS_TAB) {
                            const ps = document.getElementById('processingState');
                            if (ps) ps.classList.remove('hidden');
                            const pm = document.getElementById('processingMessage');
                            if (pm && data.data && data.data.message) {
                                pm.textContent = data.data.message;
                            }
                        }
                    } else if (data.type === 'TRANSCRIPTION_ERROR') {
                        console.error('Transcription error:', data.data);
                        if (!HIDE_RESULTS_TAB) {
                            const ps = document.getElementById('processingState');
                            if (ps) ps.classList.add('hidden');
                            hideTranscriptionPrompt();
                            const rc = document.getElementById('resultsContainer');
                            if (rc) {
                                rc.innerHTML =
                                    '<div class="error-message">Transcription failed: ' + (data.data.error || 'Unknown error') + '</div>';
                            }
                            clearClinicalTabPanels('Transcription failed — see Transcript for details.');
                        }
                    } else if (data.type === 'LIVE_TRANSCRIPT_SAVED') {
                        document.getElementById('processingState').classList.add('hidden');
                        if (data.success) {
                            liveTranscriptAutoSaved = true;
                            hideTranscriptionPrompt();
                            if (data.autoSaved) {
                                showSuccess('\\u2713 Live transcript auto-saved. AI is generating your SOAP note.');
                            } else {
                                showSuccess('Live transcript saved successfully!');
                                switchTab('recorder');
                            }
                        } else {
                            showError('Failed to save live transcript: ' + (data.error || 'Unknown error'));
                        }
                    } else if (data.type === 'AI_RECAP_GENERATING') {
                        showAiRecapGenerating();
                    } else if (data.type === 'AI_RECAP_READY') {
                        showAiRecap(data.recap);
                    } else if (data.type === 'AI_RECAP_ERROR') {
                        showAiRecapError(data.error || 'Failed to generate recap.');
                    } else if (data.type === 'LIVE_CLINICAL_TABS_GENERATING') {
                        if (!COPILOT_ENABLED) {
                            setCopilotDisabledState();
                            return;
                        }
                        if (!AUTO_COPIOLT_ENABLED) {
                            return;
                        }
                        clearClinicalTabPanels('Generating AI clinical tabs…');
                    } else if (data.type === 'LIVE_CLINICAL_TABS_READY') {
                        if (!COPILOT_ENABLED) {
                            setCopilotDisabledState();
                            return;
                        }
                        if (!AUTO_COPIOLT_ENABLED) {
                            return;
                        }
                        renderClinicalTabsFromLlm(data.clinical || {});
                    } else if (data.type === 'LIVE_CLINICAL_TABS_ERROR') {
                        showError(data.error || 'Clinical AI analysis failed.');
                    } else if (data.type === 'TRANSCRIPTION_RESTORED') {
                        // Restore transcription text from backup when resuming recording
                        console.log('💾 [EXTERNAL] Received transcription restored:', {
                            transcriptLength: data.transcriptText?.length || 0,
                            duration: data.duration,
                        });
                        var currentCanonical = (buildLiveTranscriptTextForSave() || '').trim();
                        var restoreForOtherSession = data.sessionId && data.sessionId !== recordingSessionId;
                        if (restoreForOtherSession) {
                            console.warn('💾 [EXTERNAL] Ignoring restore for another session:', data.sessionId);
                        } else if (data.transcriptText && data.transcriptText.trim() && data.transcriptText.trim().length <= currentCanonical.length) {
                            console.log('💾 [EXTERNAL] Ignoring restore: in-memory transcript is already as complete');
                        } else if (data.transcriptText && data.transcriptText.trim()) {
                            resetStructuredTranscriptLines();
                            transcriptText = data.transcriptText;
                            micTranscriptText = data.transcriptText;
                            latestLiveTranscriptText = data.transcriptText.trim();
                            var trRestore = data.transcriptText.trim();
                            var prRestore = trRestore.split(/\\r?\\n/).map(function (ln) { return ln.trim(); }).filter(Boolean);
                            micTranscriptDisplayLines = prRestore.length ? prRestore : [trRestore];
                            setLiveTranscriptionOutput((buildMicLiveDisplayMultiline() || '').trim() || trRestore);
                            console.log('💾 [EXTERNAL] Transcription restored and displayed');
                        }
                    } else if (data.type === 'LIVE_TRANSCRIPT_UPDATE') {
                        // Allow parent window to push live transcript updates (e.g., WebSocket transcription
                        // for system/tab audio). External window is the primary UI surface for live transcript.
                        if (USE_BROWSER_ONLY_LIVE_TRANSCRIPTION) {
                            // In browser-only mode we split mic/system locally in this popup.
                            // Ignore parent pushes to avoid re-mixing separated transcript panes.
                            return;
                        }
                        const nextText = (data.text || '').toString();
                        if (nextText.trim()) {
                            resetStructuredTranscriptLines();
                            transcriptText = nextText.trim();
                            micTranscriptText = transcriptText;
                            latestLiveTranscriptText = nextText.trim();
                            var prPush = transcriptText.split(/\\r?\\n/).map(function (ln) { return ln.trim(); }).filter(Boolean);
                            micTranscriptDisplayLines = prPush.length ? prPush : [transcriptText];
                            setLiveTranscriptionOutput((buildMicLiveDisplayMultiline() || '').trim() || transcriptText);
                        }
                    } else if (data.type === 'AUDIO_CHUNK_FOR_TRANSCRIPTION_ACK') {
                        // Parent window confirmed receipt of audio chunk for WebSocket transcription
                        console.log('✅ [EXTERNAL] Parent confirmed receipt of audio chunk for WebSocket transcription', {
                            sessionId: data.sessionId,
                            timestamp: data.timestamp
                        });
                    } else if (data.type === 'PARENT_PATIENT_CONTEXT') {
                        if ((mediaRecorder && (mediaRecorder.state === 'recording' || mediaRecorder.state === 'paused')) ||
                            (typeof externalRecordingState !== 'undefined' && (externalRecordingState === 'recording' || externalRecordingState === 'paused'))) {
                            console.warn('🎤 [POPUP] Sync blocked: active recording in progress.');
                            showError('Sync blocked: Please stop and save the audio before switching patients.');
                            return;
                        }
                        const nextPatientId = data.patientId || '';
                        const nextPatientName = data.patientName || '';
                        const nextEhrId = data.ehrId || '';
                        console.log('🎤 [POPUP] Processing PARENT_PATIENT_CONTEXT:', {
                            nextPatientId,
                            nextPatientName,
                            nextEhrId,
                        });
                        if (!nextPatientId) {
                            console.error('🎤 [POPUP] Sync aborted: patientId is empty in PARENT_PATIENT_CONTEXT payload.');
                            showError('Could not sync patient: missing patient ID.');
                            return;
                        }
                        var prevBoundIdCtx = patientId;
                        patientId = nextPatientId;
                        patientName = nextPatientName;
                        injectedEhrId = nextEhrId;
                        updatePatientInfoBanner();
                        console.log('🎤 [EXTERNAL] Synced patient context from parent:', {
                            patientId,
                            patientName,
                            injectedEhrId
                        });
                        showSuccess('Patient synced from parent window.');
                        if (String(prevBoundIdCtx || '') !== String(nextPatientId)) {
                            clearPreviousPatientArtifacts(nextPatientName);
                        }
                    } else if (data.type === 'PARENT_NAVIGATED_AWAY') {
                        // Task #269 HYBRID (Leejoe correction 2026-06-30 late PM):
                        // If IDLE  → silently auto-sync to the new patient (mirrors
                        //             clicking "Sync Patient" without user action).
                        // If RECORDING or PAUSED → show RED WARNING BANNER, do NOT
                        //             close, do NOT stop the recording. User keeps
                        //             control: they can finish + Stop & Save (correct
                        //             attribution to OLD patient), then click Sync
                        //             Patient manually to switch. Or ignore and keep
                        //             recording for OLD patient.
                        //
                        // Prior ship was force-close + stop+save; that destroyed the
                        // pause/resume option and made accidental stops permanent.
                        // Hybrid = safety floor without destructive decisions.
                        try {
                            console.log('🎤 [POPUP] PARENT_NAVIGATED_AWAY received. Bound popup patient:', patientId, '| new parent patient:', data.newPatientId, '| newPatientName:', data.newPatientName);
                            var isRecordingHybrid = (mediaRecorder && (mediaRecorder.state === 'recording' || mediaRecorder.state === 'paused')) ||
                                (typeof externalRecordingState !== 'undefined' && (externalRecordingState === 'recording' || externalRecordingState === 'paused'));
                            if (isRecordingHybrid) {
                                // RED BANNER path — non-destructive warning.
                                var bannerEl = document.getElementById('patientInfoBanner');
                                if (bannerEl) {
                                    bannerEl.style.backgroundColor = 'rgba(220, 38, 38, 0.12)';
                                    bannerEl.style.borderLeft = '4px solid #dc2626';
                                    bannerEl.style.padding = '10px 12px';
                                    bannerEl.style.color = '#7f1d1d';
                                    bannerEl.style.fontWeight = '500';
                                    var oldName = (patientName || 'previous patient');
                                    var newName = (data.newPatientName || 'a different patient');
                                    bannerEl.innerHTML =
                                        '<strong>⚠ PATIENT MISMATCH</strong> — recording is for <strong>' + oldName + '</strong>, ' +
                                        'but the main chart moved to <strong>' + newName + '</strong>. ' +
                                        'Finish + Stop &amp; Save to attribute to <strong>' + oldName + '</strong>, ' +
                                        'then click <em>Sync Patient</em> to switch. ' +
                                        '<button id="syncPatientBtn" onclick="requestPatientSync()" style="margin-left:8px;padding:2px 8px;font-size:12px;border:1px solid #dc2626;border-radius:999px;background:#fef2f2;color:#7f1d1d;cursor:pointer;">Sync Patient</button>';
                                }
                                try {
                                    showError('Patient changed in main window. Recording continues for ' + (patientName || 'previous patient') + '. Stop & Save to finalize, then Sync Patient.');
                                } catch (_) { /* non-fatal */ }
                            } else {
                                // IDLE path — auto-sync silently.
                                var nextIdA = data.newPatientId || '';
                                var nextNameA = data.newPatientName || '';
                                var nextEhrA = data.newEhrId || '';
                                if (nextIdA) {
                                    var prevBoundIdNav = patientId;
                                    patientId = nextIdA;
                                    patientName = nextNameA;
                                    injectedEhrId = nextEhrA;
                                    try { updatePatientInfoBanner(); } catch (_) { /* non-fatal */ }
                                    console.log('🎤 [POPUP] Auto-synced patient (idle):', { patientId, patientName, injectedEhrId });
                                    if (String(prevBoundIdNav || '') !== String(nextIdA)) {
                                        clearPreviousPatientArtifacts(nextNameA);
                                    }
                                } else {
                                    console.log('🎤 [POPUP] Idle + new patient is null — leaving popup alone.');
                                }
                            }
                        } catch (navErr) {
                            console.error('🎤 [POPUP] Error handling PARENT_NAVIGATED_AWAY:', navErr);
                            // Non-destructive fallback: don't touch state. User can manually
                            // click Sync Patient if they notice.
                        }
                    }
                }
            });

            function clearClinicalTabPanels(message) {
                var msg = message || 'Waiting for transcription…';
                ['apTabContent', 'considerTabContent', 'scoresTabContent', 'reportTabContent'].forEach(function (id) {
                    var el = document.getElementById(id);
                    if (el) {
                        el.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">' + msg + '</p>';
                    }
                });
            }

            // Patient switch must also clear the PREVIOUS patient's artifacts —
            // a stale SOAP card + "Update Note with this recording" button on
            // the new patient's popup reads as wrong-chart data (Leejoe
            // 2026-07-08: "as I open another chart, old recorder from past
            // patient still there"). Called only when the bound patient
            // actually changes and no recording is active; the old patient's
            // audio + transcript are already persisted under their own chart,
            // so clearing the display loses nothing.
            function clearPreviousPatientArtifacts(nextName) {
                try {
                    var rcSw = document.getElementById('resultsContainer');
                    if (rcSw) { rcSw.innerHTML = ''; rcSw.style.display = 'none'; }
                    clearClinicalTabPanels('No recording yet for ' + (nextName || 'this patient') + '.');
                    ['liveTranscriptTabContent', 'liveTranscriptMergedContent', 'liveTranscriptSystemAudioContent'].forEach(function (idSw) {
                        var elSw = document.getElementById(idSw);
                        if (elSw) elSw.innerHTML = '';
                    });
                    var kdSw = document.getElementById('aiKeyDataContainer');
                    if (kdSw) { kdSw.innerHTML = ''; kdSw.style.display = 'none'; }
                    try { window.sessionId = null; } catch (_) { /* non-fatal */ }
                    if (typeof recordingSessionId !== 'undefined') {
                        try { recordingSessionId = null; } catch (_) { /* non-fatal */ }
                    }
                    console.log('🎤 [POPUP] Cleared previous patient artifacts on patient switch.');
                } catch (clrErr) {
                    console.warn('🎤 [POPUP] Artifact clear failed (patient banner still synced):', clrErr);
                }
            }

            if (!COPILOT_ENABLED) {
                setCopilotDisabledState();
            }

            function escapeHtmlClinical(s) {
                if (s == null || s === undefined) return '';
                return String(s)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;');
            }

            function clinicalTabValueToPlain(v) {
                if (v == null || v === '') return '';
                var t = typeof v;
                if (t === 'string') return v;
                if (t === 'number' || t === 'boolean') return String(v);
                if (Array.isArray(v)) {
                    return v.map(clinicalTabValueToPlain).filter(function (x) { return x && String(x).trim(); }).join('\\n\\n');
                }
                if (t === 'object') {
                    var keys = ['text', 'content', 'summary', 'body', 'narrative', 'value', 'description', 'detail', 'finding', 'statement'];
                    for (var ki = 0; ki < keys.length; ki++) {
                        var xv = v[keys[ki]];
                        if (xv != null && typeof xv === 'string' && xv.trim()) return xv;
                    }
                    var parts = [];
                    for (var prop in v) {
                        if (!Object.prototype.hasOwnProperty.call(v, prop)) continue;
                        var val = v[prop];
                        if (typeof val === 'string' && val.trim()) parts.push(val.trim());
                    }
                    if (parts.length) return parts.join('\\n\\n');
                    try { return JSON.stringify(v, null, 2); } catch (e) { return String(v); }
                }
                return String(v);
            }

            function formatAiRecapListItem(item) {
                if (item === null || item === undefined || item === '') return '';
                var t = typeof item;
                if (t === 'string' || t === 'number') return String(item);
                if (t === 'boolean') return item ? 'Yes' : 'No';
                if (Array.isArray(item)) {
                    return item.map(formatAiRecapListItem).filter(Boolean).join('; ');
                }
                if (t === 'object') {
                    var owner = item.owner != null ? String(item.owner).trim() : '';
                    var action = item.action != null ? String(item.action).trim() : '';
                    var intent = item.intent != null ? String(item.intent).trim() : '';
                    var timeline = item.timeline != null ? String(item.timeline).trim() : '';
                    var head = '';
                    if (owner && action) head = owner + ': ' + action;
                    else head = action || owner || '';
                    var extras = [];
                    if (intent) extras.push(intent);
                    if (timeline) extras.push(timeline);
                    var tail = extras.length ? ' (' + extras.join(' · ') + ')' : '';
                    var out = (head + tail).trim();
                    if (out) return out;
                    try { return JSON.stringify(item); } catch (e) { return String(item); }
                }
                return String(item);
            }

            function firstSoapText(soap) {
                if (!soap || typeof soap !== 'object') return '';
                if (soap.combined && typeof soap.combined === 'string') return soap.combined;
                if (soap.transcript_only && typeof soap.transcript_only === 'string') return soap.transcript_only;
                if (soap.facts_only && typeof soap.facts_only === 'string') return soap.facts_only;
                return '';
            }

            function extractSoapSections(text) {
                var t = (text || '').trim();
                if (!t) return {};
                function pick(label) {
                    var re = new RegExp(label + '\\s*:\\s*([\\s\\S]*?)(?=(?:Subjective|Objective|Assessment|Plan)\\s*:|$)', 'i');
                    var m = t.match(re);
                    return m ? m[1].trim() : '';
                }
                return {
                    subjective: pick('Subjective'),
                    objective: pick('Objective'),
                    assessment: pick('Assessment'),
                    plan: pick('Plan'),
                };
            }

            function formatScoresBlock(scores) {
                if (scores === null || scores === undefined || scores === '') {
                    return '<p style="color:#718096;font-style:italic;margin:0;">No structured scores were returned for this encounter.</p>';
                }
                if (typeof scores === 'string') {
                    return '<div class="result-content">' + escapeHtmlClinical(scores).replace(/\\n/g, '<br>') + '</div>';
                }
                if (Array.isArray(scores)) {
                    if (scores.length === 0) {
                        return '<p style="color:#718096;font-style:italic;margin:0;">No structured scores were returned for this encounter.</p>';
                    }
                    var items = scores.map(function (s) {
                        return '<li>' + escapeHtmlClinical(typeof s === 'object' ? JSON.stringify(s) : String(s)) + '</li>';
                    }).join('');
                    return '<ul style="margin:0;padding-left:1.25rem;line-height:1.6;">' + items + '</ul>';
                }
                if (typeof scores === 'object') {
                    return '<pre class="result-content" style="white-space:pre-wrap;font-size:0.85rem;">' +
                        escapeHtmlClinical(JSON.stringify(scores, null, 2)) + '</pre>';
                }
                return '<div class="result-content">' + escapeHtmlClinical(String(scores)) + '</div>';
            }

            function factsAsEvidenceList(facts) {
                if (!facts || !facts.length) {
                    return '<p style="color:#718096;font-style:italic;margin:0;">No extracted facts available.</p>';
                }
                var html = '<ul style="margin:0;padding-left:1.25rem;line-height:1.6;">';
                facts.forEach(function (fact) {
                    var text = typeof fact === 'string' ? fact : clinicalTabValueToPlain(fact);
                    html += '<li>' + escapeHtmlClinical(text) + '</li>';
                });
                html += '</ul>';
                return html;
            }

            function transcriptTextFromResults(results) {
                if (!results || !results.transcript || !results.transcript.length) return '';
                var lines = [];
                results.transcript.forEach(function(line) {
                    var text = typeof line === 'string' ? line : (line.text || line.transcript || '');
                    if (text) lines.push(String(text).trim());
                });
                return lines.join('\\n\\n');
            }

            function scoresPlainForReportData(scoresData) {
                if (scoresData === null || scoresData === undefined || scoresData === '') return '';
                if (typeof scoresData === 'string') return scoresData;
                if (Array.isArray(scoresData)) {
                    return scoresData.map(function(s) {
                        return typeof s === 'object' ? JSON.stringify(s) : String(s);
                    }).join('\\n');
                }
                if (typeof scoresData === 'object') return JSON.stringify(scoresData, null, 2);
                return String(scoresData);
            }

            function factsPlainLines(facts) {
                if (!facts || !facts.length) return '';
                var out = [];
                facts.forEach(function(fact) {
                    var text = typeof fact === 'string' ? fact : (fact.text || fact.fact || '');
                    if (text) out.push('- ' + String(text).trim());
                });
                return out.join('\\n');
            }

            function formatClinicalReportParagraphs(text) {
                var t = (text || '').trim();
                if (!t) return '<p style="color:#718096;font-style:italic;margin:0;">No narrative body available for this section.</p>';
                var esc = escapeHtmlClinical(t);
                // A6.1: convert markdown **bold** to <strong> (post-escape — escapeHtmlClinical
                // does not escape asterisks). Used by the template-fill prompt to mark abnormal
                // physical exam findings.
                esc = esc.replace(/\\*\\*([^*\\n]+?)\\*\\*/g, '<strong>$1</strong>');
                return esc.split(/\\n\\n+/).map(function(para) {
                    return '<p style="margin:0 0 0.75rem;line-height:1.58;color:#1a202c;text-align:left;">' +
                        para.replace(/\\n/g, '<br>') + '</p>';
                }).join('');
            }

            function composeLlmTabsIntoReportPlain(c) {
                if (!c || typeof c !== 'object') return '';
                var lines = [];
                lines.push('CLINICAL ENCOUNTER REPORT (compiled from structured AI sections)');
                lines.push('');
                if (c.subjective) {
                    lines.push('SUBJECTIVE');
                    lines.push(clinicalTabValueToPlain(c.subjective));
                    lines.push('');
                }
                if (c.ros) {
                    lines.push('REVIEW OF SYSTEMS');
                    lines.push(clinicalTabValueToPlain(c.ros));
                    lines.push('');
                }
                if (c.pe) {
                    lines.push('PHYSICAL EXAM');
                    lines.push(clinicalTabValueToPlain(c.pe));
                    lines.push('');
                }
                if (c.objective) {
                    lines.push('OBJECTIVE (VITALS / LABS / IMAGING)');
                    lines.push(clinicalTabValueToPlain(c.objective));
                    lines.push('');
                }
                if (c.assessment) {
                    lines.push('ASSESSMENT');
                    lines.push(clinicalTabValueToPlain(c.assessment));
                    lines.push('');
                }
                if (c.plan) {
                    lines.push('PLAN');
                    lines.push(clinicalTabValueToPlain(c.plan));
                    lines.push('');
                }
                if (c.consider) {
                    lines.push('CONSIDERATIONS / DIFFERENTIAL / RISK CONTEXT');
                    lines.push(clinicalTabValueToPlain(c.consider));
                    lines.push('');
                }
                var sc = scoresPlainForReportData(c.scores);
                if (sc) {
                    lines.push('SCORES / RATING SCALES');
                    lines.push(sc);
                    lines.push('');
                }
                var ev = c.evidence;
                if (ev != null && ev !== '') {
                    lines.push('EVIDENCE AND DATA POINTS');
                    if (Array.isArray(ev)) {
                        ev.forEach(function(item) {
                            var ti = clinicalTabValueToPlain(item);
                            if (ti) lines.push('- ' + ti);
                        });
                    } else {
                        lines.push(clinicalTabValueToPlain(ev));
                    }
                    lines.push('');
                }
                return lines.join('\\n').trim();
            }

            function renderClinicalEncounterReportCompiled(results) {
                var reportEl = document.getElementById('reportTabContent');
                if (!reportEl) return;

                var soap = results.soap_notes || {};
                var soapText = firstSoapText(soap);
                var sections = typeof soapText === 'string' ? extractSoapSections(soapText) : {};
                var assessment = soap.assessment || soap.clinical_assessment || sections.assessment || '';
                var plan = soap.plan || soap.clinical_plan || sections.plan || '';
                var considerRaw = soap.consider || soap.considerations || soap.consideration;
                var considerText = considerRaw;
                if (!considerText || (typeof considerText !== 'string')) {
                    var subjC = sections.subjective || soap.subjective || '';
                    var objC = sections.objective || soap.objective || '';
                    considerText = [subjC ? ('Subjective:\\n' + subjC) : '', objC ? ('Objective:\\n' + objC) : ''].filter(Boolean).join('\\n\\n');
                }

                var scoresData = soap.scores !== undefined ? soap.scores : results.scores;
                var scoresPlain = scoresPlainForReportData(scoresData);
                var transcriptBody = transcriptTextFromResults(results);
                var factsPlain = factsPlainLines(results.facts);

                var hasConsider = considerText && String(considerText).trim();
                var hasAny = !!(transcriptBody || assessment || plan || hasConsider || scoresPlain || factsPlain);
                if (!hasAny) {
                    reportEl.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">Insufficient data to compile an encounter report yet. Run transcription and review Transcript tab or enable copilot for AI-generated detail.</p>';
                    return;
                }

                var plain = [];
                plain.push('INTEGRATED CLINICAL ENCOUNTER REPORT');
                plain.push('Patient: ' + (patientName || '') + ' | ID: ' + (patientId || 'N/A'));
                plain.push('Compiled: ' + new Date().toISOString());
                plain.push('');
                plain.push('NOTICE: Supporting documentation only; not a substitute for independent clinical judgment or the legal medical record.');
                plain.push('');

                var html = [];
                html.push('<textarea id="clinicalReportPlainField" readonly tabindex="-1" aria-hidden="true" style="position:absolute;left:-9999px;top:0;width:2px;height:2px;opacity:0;border:none;padding:0;"></textarea>');
                html.push('<div class="result-section" style="margin-bottom:0.65rem;">');
                html.push('<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:0.5rem;flex-wrap:wrap;">');
                html.push('<h3 style="margin:0;color:#2c5282;">Integrated clinical encounter report</h3>');
                html.push('<button type="button" class="btn btn-light" style="min-width:auto;padding:0.28rem 0.55rem;font-size:0.74rem;" onclick="copyClinicalReportFromHiddenField()">Copy full report</button>');
                html.push('</div>');
                html.push('<p style="font-size:0.78rem;color:#4a5568;margin:0.35rem 0 0 0;line-height:1.4;"><strong>Patient:</strong> ' + escapeHtmlClinical(patientName || '') +
                    ' | <strong>ID:</strong> ' + escapeHtmlClinical(patientId || 'N/A') +
                    ' | <strong>Compiled:</strong> ' + escapeHtmlClinical(new Date().toLocaleString()) + '</p>');
                html.push('<p style="font-size:0.7rem;color:#718096;font-style:italic;margin:0.4rem 0 0 0;line-height:1.38;">Structured compilation of the encounter transcript, SOAP output, A&amp;P synthesis, Consider / S&amp;O content, scored instruments, and extracted facts. Intended for handoff preparation and documentation support.</p>');
                html.push('</div>');

                if (transcriptBody) {
                    plain.push('----- FULL TRANSCRIPT -----');
                    plain.push(transcriptBody);
                    plain.push('');
                    html.push('<div class="result-section" style="margin-bottom:0.65rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Encounter transcript (verbatim)</h4>');
                    html.push('<div class="result-content" style="max-height:280px;overflow-y:auto;font-size:0.82rem;line-height:1.48;border:1px solid rgba(0,0,0,0.08);border-radius:8px;padding:0.55rem;">');
                    html.push(escapeHtmlClinical(transcriptBody).replace(/\\n/g, '<br>'));
                    html.push('</div></div>');
                }

                if (hasConsider) {
                    plain.push('----- HISTORY, ROS, SUBJECTIVE/OBJECTIVE CONTEXT -----');
                    plain.push(String(considerText).trim());
                    plain.push('');
                    html.push('<div class="result-section" style="margin-bottom:0.65rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">History, review, and exam-oriented context</h4>');
                    html.push('<div class="result-content">' + formatClinicalReportParagraphs(String(considerText)) + '</div></div>');
                }

                if (assessment) {
                    plain.push('----- ASSESSMENT -----');
                    plain.push(String(assessment).trim());
                    plain.push('');
                    html.push('<div class="result-section" style="margin-bottom:0.65rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Assessment / problem synthesis</h4>');
                    html.push('<div class="result-content">' + formatClinicalReportParagraphs(String(assessment)) + '</div></div>');
                }

                if (plan) {
                    plain.push('----- PLAN -----');
                    plain.push(String(plan).trim());
                    plain.push('');
                    html.push('<div class="result-section" style="margin-bottom:0.65rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Plan</h4>');
                    html.push('<div class="result-content">' + formatClinicalReportParagraphs(String(plan)) + '</div></div>');
                }

                if (scoresPlain) {
                    plain.push('----- SCORES / STRUCTURED METRICS -----');
                    plain.push(scoresPlain);
                    plain.push('');
                    html.push('<div class="result-section" style="margin-bottom:0.65rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Scores and structured metrics</h4>');
                    html.push(formatScoresBlock(scoresData));
                    html.push('</div>');
                }

                if (factsPlain) {
                    plain.push('----- SUPPORTING EXTRACTED FACTS -----');
                    plain.push(factsPlain);
                    plain.push('');
                    html.push('<div class="result-section" style="margin-bottom:0.65rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Evidence-aligned facts</h4>');
                    html.push('<div class="result-content" style="font-size:0.84rem;line-height:1.5;">' + escapeHtmlClinical(factsPlain).replace(/\\n/g, '<br>') + '</div></div>');
                }

                reportEl.innerHTML = html.join('');
                var ta = document.getElementById('clinicalReportPlainField');
                if (ta) ta.value = plain.join('\\n');
            }

            function renderClinicalReportTabFromLlm(c) {
                var reportEl = document.getElementById('reportTabContent');
                if (!reportEl || !c || typeof c !== 'object') return;
                // Prefer the composer over the AI's "report" field so SOAP note (full) always renders
                // Subjective + ROS + PE + Objective + Assessment + Plan even when the AI ignores the
                // "set report to empty string" instruction. Authored only kicks in when the composer
                // has no structured sections to render (rare).
                var composed = composeLlmTabsIntoReportPlain(c);
                var authored = (c.report && clinicalTabValueToPlain(c.report).trim()) || '';
                var bodyText = composed || authored;
                if (!bodyText) {
                    reportEl.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">No AI report body returned. Run analysis again or check Transcript tab.</p>';
                    return;
                }

                var usedComposer = bodyText === composed && !!composed;
                var plain = [];
                plain.push('CLINICAL ENCOUNTER REPORT (AI-generated)');
                plain.push('Patient: ' + (patientName || '') + ' | ID: ' + (patientId || 'N/A'));
                plain.push('Generated: ' + new Date().toISOString());
                plain.push('');
                if (usedComposer) {
                    plain.push('(Compiled from AI structured sections: Subjective, ROS, PE, Objective, Assessment, Plan, Consider, Scores, Evidence)');
                    plain.push('');
                }
                plain.push(bodyText);

                var html = [];
                html.push('<textarea id="clinicalReportPlainField" readonly tabindex="-1" aria-hidden="true" style="position:absolute;left:-9999px;top:0;width:2px;height:2px;opacity:0;border:none;padding:0;"></textarea>');
                html.push('<div class="result-section" style="margin-bottom:0.65rem;">');
                html.push('<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:0.5rem;flex-wrap:wrap;">');
                html.push('<h3 style="margin:0;color:#2c5282;">SOAP note (full) — v4</h3>');
                html.push('<div style="display:flex;gap:0.4rem;flex-wrap:wrap;">');
                html.push('<button type="button" class="btn btn-light" style="min-width:auto;padding:0.28rem 0.55rem;font-size:0.74rem;" onclick="copyClinicalReportFromHiddenField()">Copy full report</button>');
                // "Make this my note" hidden 2026-06-24: wholesale-replace
                // destroyed the chart's actual template structure (used the
                // DEFAULT_SOAP_TEMPLATE shape, not the chart's FFP/etc.), and
                // the Restore button didn't reliably undo it. Use
                // "Process this recording (diff review)" instead (v6 path
                // below). makeThisMyNote() handler kept wired in case we
                // re-enable after fixing the template-aware path.
                // html.push('<button type="button" class="btn btn-primary make-my-note-btn" style="min-width:auto;padding:0.28rem 0.7rem;font-size:0.74rem;background:#3182ce;color:#fff;border:none;border-radius:6px;font-weight:600;cursor:pointer;" onclick="makeThisMyNote()">↪ Make this my note</button>');
                html.push('</div>');
                html.push('</div>');
                html.push('<p style="font-size:0.78rem;color:#4a5568;margin:0.35rem 0 0 0;line-height:1.4;"><strong>Patient:</strong> ' + escapeHtmlClinical(patientName || '') +
                    ' | <strong>ID:</strong> ' + escapeHtmlClinical(patientId || 'N/A') + '</p>');
                html.push('<p style="font-size:0.7rem;color:#718096;font-style:italic;margin:0.4rem 0 0 0;line-height:1.38;">Compiled from AI structured sections: Subjective, ROS, PE, Objective, Assessment, Plan, Consider, Scores, Evidence.</p>');
                html.push('</div>');
                html.push('<div class="result-section"><div class="result-content" style="font-size:0.86rem;">');
                html.push(formatClinicalReportParagraphs(bodyText));
                html.push('</div></div>');

                reportEl.innerHTML = html.join('');
                var ta = document.getElementById('clinicalReportPlainField');
                if (ta) ta.value = plain.join('\\n');
            }

            function renderClinicalTabs(results) {
                var soap = results.soap_notes || {};
                var soapText = firstSoapText(soap);
                var sections = typeof soapText === 'string' ? extractSoapSections(soapText) : {};

                var assessment = soap.assessment || soap.clinical_assessment || sections.assessment || '';
                var plan = soap.plan || soap.clinical_plan || sections.plan || '';

                var apEl = document.getElementById('apTabContent');
                if (apEl) {
                    var hasFacts = !!(results.facts && results.facts.length);
                    var evidenceSection =
                        '<div class="result-section" style="' + (assessment || plan ? 'margin-top:0.75rem;' : '') +
                        '"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Evidence (extracted facts)</h4>' +
                        factsAsEvidenceList(results.facts) +
                        '</div>';
                    var apParts = '';
                    if (assessment) {
                        apParts +=
                            '<div class="result-section" style="margin-bottom:0.75rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Assessment</h4><div class="result-content">' +
                            escapeHtmlClinical(assessment).replace(/\\n/g, '<br>') +
                            '</div></div>';
                    }
                    if (plan) {
                        apParts +=
                            '<div class="result-section" style="margin-bottom:0.75rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Plan</h4><div class="result-content">' +
                            escapeHtmlClinical(plan).replace(/\\n/g, '<br>') +
                            '</div></div>';
                    }
                    var hasSoap = !!(assessment || plan);
                    if (!hasSoap && !hasFacts) {
                        apEl.innerHTML =
                            '<p style="color:#718096;font-style:italic;margin:0;">No Assessment / Plan sections detected in SOAP output and no extracted facts. Use Transcript for the full note.</p>';
                    } else if (!hasSoap && hasFacts) {
                        evidenceSection =
                            '<div class="result-section"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Evidence (extracted facts)</h4>' +
                            factsAsEvidenceList(results.facts) +
                            '</div>';
                        apEl.innerHTML = evidenceSection;
                    } else {
                        apEl.innerHTML = apParts + evidenceSection;
                    }
                }

                var considerEl = document.getElementById('considerTabContent');
                if (considerEl) {
                    var considerRaw = soap.consider || soap.considerations || soap.consideration;
                    var considerText = considerRaw;
                    if (!considerText || (typeof considerText !== 'string')) {
                        var subj = sections.subjective || soap.subjective || '';
                        var obj = sections.objective || soap.objective || '';
                        considerText = [subj ? ('Subjective:\\n' + subj) : '', obj ? ('Objective:\\n' + obj) : ''].filter(Boolean).join('\\n\\n');
                    }
                    if (considerText && String(considerText).trim()) {
                        considerEl.innerHTML = '<div class="result-section"><div class="result-content">' +
                            escapeHtmlClinical(String(considerText)).replace(/\\n/g, '<br>') + '</div></div>';
                    } else {
                        considerEl.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">No Consider data or Subjective/Objective sections were found.</p>';
                    }
                }

                var scoresEl = document.getElementById('scoresTabContent');
                if (scoresEl) {
                    var scoresData = soap.scores !== undefined ? soap.scores : results.scores;
                    scoresEl.innerHTML = formatScoresBlock(scoresData);
                }

                renderClinicalEncounterReportCompiled(results);
                rememberClinicalTabsFromCorti(results);
            }

            function renderClinicalTabsFromLlm(c) {
                if (!c || typeof c !== 'object') return;
                var apEl = document.getElementById('apTabContent');
                if (apEl) {
                    var a = c.assessment || '';
                    var p = c.plan || '';
                    var apHtml = '';
                    if (a) {
                        apHtml += '<div class="result-section" style="margin-bottom:0.75rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Assessment</h4><div class="result-content">' +
                            escapeHtmlClinical(clinicalTabValueToPlain(a)).replace(/\\n/g, '<br>') + '</div></div>';
                    }
                    if (p) {
                        apHtml += '<div class="result-section" style="margin-bottom:0.75rem;"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Plan</h4><div class="result-content">' +
                            escapeHtmlClinical(clinicalTabValueToPlain(p)).replace(/\\n/g, '<br>') + '</div></div>';
                    }
                    var ev = c.evidence;
                    var evInner;
                    if (ev == null || ev === '') {
                        evInner = '<p style="color:#718096;font-style:italic;margin:0;">No evidence summary.</p>';
                    } else if (Array.isArray(ev)) {
                        evInner = factsAsEvidenceList(ev);
                    } else {
                        evInner = '<div class="result-content">' + escapeHtmlClinical(clinicalTabValueToPlain(ev)).replace(/\\n/g, '<br>') + '</div>';
                    }
                    apHtml +=
                        '<div class="result-section" style="' +
                        (a || p ? 'margin-top:0.75rem;' : '') +
                        '"><h4 style="margin:0 0 0.35rem 0;color:#2c5282;">Evidence</h4>' +
                        evInner +
                        '</div>';
                    if (!a && !p && (ev == null || ev === '' || (Array.isArray(ev) && ev.length === 0))) {
                        apEl.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">No assessment/plan/evidence returned.</p>';
                    } else {
                        apEl.innerHTML = apHtml;
                    }
                }
                var considerEl = document.getElementById('considerTabContent');
                if (considerEl) {
                    var cv = c.consider || '';
                    considerEl.innerHTML = cv
                        ? '<div class="result-section"><div class="result-content">' + escapeHtmlClinical(clinicalTabValueToPlain(cv)).replace(/\\n/g, '<br>') + '</div></div>'
                        : '<p style="color:#718096;font-style:italic;margin:0;">Nothing returned for Consider.</p>';
                }
                var scoresEl = document.getElementById('scoresTabContent');
                if (scoresEl) {
                    scoresEl.innerHTML = formatScoresBlock(c.scores);
                }
                // A6: SOAP note (full) tab is owned by renderSoapNoteFromTemplate() —
                // do NOT call renderClinicalReportTabFromLlm(c) here. Live Haiku updates
                // would otherwise re-render the SOAP tab from sparse 9-key payloads,
                // clobbering the template-fill result.
                rememberClinicalTabsFromLlm(c);
            }

            // A6.3 / A6.4: extract a single section from a filled template by header pattern.
            // Returns content between the matching header and the next ALL-CAPS header line, trimmed.
            function extractTemplateSection(filledTemplate, headerRegex) {
                if (typeof filledTemplate !== 'string') return '';
                var lines = filledTemplate.split(/\\r?\\n/);
                var startIdx = -1;
                for (var i = 0; i < lines.length; i++) {
                    if (headerRegex.test(lines[i])) {
                        startIdx = i + 1;
                        break;
                    }
                }
                if (startIdx === -1) return '';
                // Find the next section header (line that's a known section name in caps) and stop there.
                var endIdx = lines.length;
                var nextHeaderRegex = /^\\s*(SUBJECTIVE|REVIEW OF SYSTEMS|PHYSICAL EXAM|OBJECTIVE|ASSESSMENT|ASSESSMENT & PLAN|PLAN|CONSIDERATIONS|SCORES|ORDERS)(\\s|$)/i;
                for (var j = startIdx; j < lines.length; j++) {
                    if (nextHeaderRegex.test(lines[j])) {
                        endIdx = j;
                        break;
                    }
                }
                return lines.slice(startIdx, endIdx).join('\\n').trim();
            }

            // A6.3: extract the ORDERS section from a filled template.
            function extractOrdersFromTemplate(filledTemplate) {
                return extractTemplateSection(filledTemplate, /^\\s*ORDERS\\s*$/i);
            }

            // A6.4: extract the ASSESSMENT & PLAN section from a filled template.
            function extractAssessmentPlanFromTemplate(filledTemplate) {
                return extractTemplateSection(filledTemplate, /^\\s*ASSESSMENT\\s*&\\s*PLAN\\s*$/i);
            }

            // A6.4: extract SCORES section from a filled template.
            function extractScoresFromTemplate(filledTemplate) {
                return extractTemplateSection(filledTemplate, /^\\s*SCORES\\s*(\\/\\s*RATING SCALES)?\\s*$/i);
            }

            // A6.3: render the Orders tab (top-level, dedicated panel for remote-team handoff).
            // Pulled from the same filled_template that drives the SOAP note tab — single AI
            // call, two surfaces, always consistent.
            function renderOrdersTabFromTemplate(filledTemplate) {
                var el = document.getElementById('ordersTabContent');
                if (!el) return;
                var orders = extractOrdersFromTemplate(filledTemplate);
                if (!orders) {
                    el.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">No Orders section returned from the AI template-fill. Record a session and stop to populate.</p>';
                    return;
                }
                var html = [];
                html.push('<div class="result-section" style="margin-bottom:0.65rem;">');
                html.push('<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:0.5rem;flex-wrap:wrap;">');
                html.push('<h3 style="margin:0;color:#2c5282;">Orders to enter</h3>');
                html.push('<button type="button" class="btn btn-light" style="min-width:auto;padding:0.28rem 0.55rem;font-size:0.74rem;" onclick="copyOrdersFromHiddenField()">Copy orders</button>');
                html.push('</div>');
                html.push('<p style="font-size:0.7rem;color:#718096;font-style:italic;margin:0.4rem 0 0 0;line-height:1.38;">Handoff to the remote-entry team. Items in [edit: \\u2026] need physician clarification before being sent.</p>');
                html.push('</div>');
                html.push('<textarea id="ordersPlainField" readonly tabindex="-1" aria-hidden="true" style="position:absolute;left:-9999px;top:0;width:2px;height:2px;opacity:0;border:none;padding:0;"></textarea>');
                html.push('<div class="result-section"><div class="result-content" style="font-size:0.86rem;">');
                html.push(formatClinicalReportParagraphs(orders));
                html.push('</div></div>');
                el.innerHTML = html.join('');
                var ta = document.getElementById('ordersPlainField');
                if (ta) ta.value = orders;
            }

            function copyOrdersFromHiddenField() {
                var ta = document.getElementById('ordersPlainField');
                if (!ta || !ta.value) { showError('No orders to copy yet.'); return; }
                copyToClipboard(ta.value);
                showSuccess('Orders copied to clipboard.');
            }

            // A6.4: render the A&P tab from the ASSESSMENT & PLAN section of the filled template.
            // Problem-based format — one-liner patient summary, then numbered ICD-10-style problems
            // with bulleted plan items per problem. Same source-of-truth as the SOAP tab.
            function renderApTabFromTemplate(filledTemplate) {
                var el = document.getElementById('apTabContent');
                if (!el) return;
                var content = extractAssessmentPlanFromTemplate(filledTemplate);
                if (!content) {
                    el.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">A&amp;P will populate after recording stops and template-fill completes.</p>';
                    return;
                }
                var html = [];
                html.push('<div class="result-section" style="margin-bottom:0.65rem;">');
                html.push('<h3 style="margin:0;color:#2c5282;">Assessment &amp; Plan</h3>');
                html.push('<p style="font-size:0.7rem;color:#718096;font-style:italic;margin:0.35rem 0 0 0;line-height:1.38;">Problem-based, ICD-10-style problem names. Patient summary up top; numbered problems with per-problem workup/plan below.</p>');
                html.push('</div>');
                html.push('<div class="result-section"><div class="result-content" style="font-size:0.88rem;">');
                html.push(formatClinicalReportParagraphs(content));
                html.push('</div></div>');
                el.innerHTML = html.join('');
            }

            // A6.4: render the Scores tab from the SCORES section of the filled template.
            function renderScoresTabFromTemplate(filledTemplate) {
                var el = document.getElementById('scoresTabContent');
                if (!el) return;
                var content = extractScoresFromTemplate(filledTemplate);
                if (!content) {
                    el.innerHTML = '<p style="color:#718096;font-style:italic;margin:0;">Scores will populate after recording stops and template-fill completes.</p>';
                    return;
                }
                var html = [];
                html.push('<div class="result-section" style="margin-bottom:0.65rem;">');
                html.push('<h3 style="margin:0;color:#2c5282;">Scores / Rating Scales</h3>');
                html.push('</div>');
                html.push('<div class="result-section"><div class="result-content" style="font-size:0.88rem;">');
                html.push(formatClinicalReportParagraphs(content));
                html.push('</div></div>');
                el.innerHTML = html.join('');
            }

            // A6: render the SOAP note (full) tab from a filled template string returned by
            // /api/recorder/fill-template/. Bypasses the structured-JSON composer entirely —
            // the template literally IS the schema. Sole renderer for the SOAP tab.
            function renderSoapNoteFromTemplate(filledTemplate) {
                // Sibling renders: same source of truth feeds the dedicated A&P, Scores, and Orders tabs.
                // Template-fill is the canonical source for ALL clinical tabs after stop+save.
                renderApTabFromTemplate(filledTemplate);
                renderScoresTabFromTemplate(filledTemplate);
                renderOrdersTabFromTemplate(filledTemplate);

                var reportEl = document.getElementById('reportTabContent');
                if (!reportEl || typeof filledTemplate !== 'string' || !filledTemplate.trim()) return;
                var plain = String(filledTemplate).trim();
                var html = [];
                html.push('<textarea id="clinicalReportPlainField" readonly tabindex="-1" aria-hidden="true" style="position:absolute;left:-9999px;top:0;width:2px;height:2px;opacity:0;border:none;padding:0;"></textarea>');
                html.push('<div class="result-section" style="margin-bottom:0.65rem;">');
                html.push('<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:0.5rem;flex-wrap:wrap;">');
                html.push('<h3 style="margin:0;color:#2c5282;">SOAP note (full) — v6 template-fill</h3>');
                html.push('<div style="display:flex;gap:0.4rem;flex-wrap:wrap;">');
                html.push('<button type="button" class="btn btn-light" style="min-width:auto;padding:0.28rem 0.55rem;font-size:0.74rem;" onclick="copyClinicalReportFromHiddenField()">Copy full report</button>');
                html.push('<button type="button" class="btn btn-primary process-recording-btn" style="min-width:auto;padding:0.28rem 0.7rem;font-size:0.74rem;background:#10b981;color:#fff;border:none;border-radius:6px;font-weight:600;cursor:pointer;" onclick="processThisRecording()" title="Update the note with this recording — review the per-edit diff in the Copilot panel.">\\u2713 Update Note with this recording</button>');
                // "Make this my note" hidden 2026-06-24 — see comment above
                // the v4 instance. Process this recording (diff review) is the
                // canonical safe path now. handler kept in place; re-enable by
                // uncommenting once the destructive replace is template-aware
                // and Restore reliably reverses it.
                // html.push('<button type="button" class="btn btn-primary make-my-note-btn" style="min-width:auto;padding:0.28rem 0.7rem;font-size:0.74rem;background:#3182ce;color:#fff;border:none;border-radius:6px;font-weight:600;cursor:pointer;" onclick="makeThisMyNote()" title="Wholesale-replace your note with the auto-generated SOAP (no diff review).">\\u21aa Make this my note</button>');
                html.push('</div>');
                html.push('</div>');
                html.push('<p style="font-size:0.78rem;color:#4a5568;margin:0.35rem 0 0 0;line-height:1.4;"><strong>Patient:</strong> ' + escapeHtmlClinical(patientName || '') +
                    ' | <strong>ID:</strong> ' + escapeHtmlClinical(patientId || 'N/A') + '</p>');
                html.push('<p style="font-size:0.7rem;color:#718096;font-style:italic;margin:0.4rem 0 0 0;line-height:1.38;">AI filled the clinic\\u2019s blank SOAP template directly from the encounter transcript. Template-fill (no schema enforcement).</p>');
                html.push('</div>');
                html.push('<div class="result-section"><div class="result-content" style="font-size:0.86rem;">');
                html.push(formatClinicalReportParagraphs(plain));
                html.push('</div></div>');
                reportEl.innerHTML = html.join('');
                var ta = document.getElementById('clinicalReportPlainField');
                if (ta) ta.value = plain;
            }

            function renderResults(results) {
                const container = document.getElementById('resultsContainer');
                if (!results) return;

                let html = '';

                // Transcript
                if (results.transcript && results.transcript.length > 0) {
                    html += '<div class="result-section">';
                    html += '<div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid rgba(0,0,0,0.1);margin-bottom:0.5rem;padding-bottom:0.25rem;">';
                    html += '<h3 style="border:none;margin:0;">Transcript</h3>';
                    html += '<button class="btn btn-light" style="min-width:auto;width:auto;padding:0.2rem 0.5rem;font-size:0.8rem;" onclick="copyTextFromElement(\\'transcript-content\\')">Copy</button>';
                    html += '</div>';
                    html += '<div class="result-content" id="transcript-content">';
                    results.transcript.forEach(line => {
                        const text = typeof line === 'string' ? line : (line.text || line.transcript || '');
                        html += '<p style="margin-bottom: 0.5rem;">' + text + '</p>';
                    });
                    html += '</div></div>';
                }

                // Facts
                if (results.facts && results.facts.length > 0) {
                    html += '<div class="result-section">';
                    html += '<div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid rgba(0,0,0,0.1);margin-bottom:0.5rem;padding-bottom:0.25rem;">';
                    html += '<h3 style="border:none;margin:0;">Extracted Facts</h3>';
                    html += '<button class="btn btn-light" style="min-width:auto;width:auto;padding:0.2rem 0.5rem;font-size:0.8rem;" onclick="copyTextFromElement(\\'facts-content\\')">Copy</button>';
                    html += '</div>';
                    html += '<div class="result-content" id="facts-content">';
                    results.facts.forEach(fact => {
                        const text = typeof fact === 'string' ? fact : fact.text;
                        html += '<div class="fact-item">' + text + '</div>';
                    });
                    html += '</div></div>';
                }

                // SOAP Notes
                if (results.soap_notes) {
                    const soap = results.soap_notes;
                    if (soap.transcript_only || soap.facts_only) {
                        html += '<div class="result-section">';
                        html += '<div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid rgba(0,0,0,0.1);margin-bottom:0.5rem;padding-bottom:0.25rem;">';
                        html += '<h3 style="border:none;margin:0;">SOAP Notes</h3>';
                        html += '<button class="btn btn-light" style="min-width:auto;width:auto;padding:0.2rem 0.5rem;font-size:0.8rem;" onclick="copyTextFromElement(\\'soap-content\\')">Copy</button>';
                        html += '</div>';
                        html += '<div id="soap-content">';
                        
                        if (soap.transcript_only) {
                            html += '<h4 style="font-size: 1rem; margin: 0.5rem 0; color: #4a5568;">Transcript-Based</h4>';
                            html += '<div class="result-content" style="margin-bottom: 1rem;">' + soap.transcript_only + '</div>';
                        }
                        
                        if (soap.facts_only) {
                            html += '<h4 style="font-size: 1rem; margin: 0.5rem 0; color: #4a5568;">Facts-Based</h4>';
                            html += '<div class="result-content">' + soap.facts_only + '</div>';
                        }
                        html += '</div></div>';
                    }
                }
                
                if (html === '') {
                     container.innerHTML = '<div style="text-align: center; margin-top: 2rem; color: #666;">No data found in results.</div>';
                } else {
                    container.innerHTML = html;
                }
                renderClinicalTabs(results);
            }

            // Preferred mic device ID injected from user's saved preference (Supabase)
            const PREFERRED_MIC_DEVICE_ID = '${injectedMicDeviceId}';
            // AI Recap default injected from schema_settings.ai_recap_enabled
            const AI_RECAP_DEFAULT = ${injectedAiRecapDefault};

            let externalRecordingState = 'prompt';
            let isRecording = false;
            let isPaused = false;
            let timer = { minutes: 0, seconds: 0 };
            let timerInterval = null;

            function formatRecordingTimestamp() {
                var totalSec = Math.max(0, (timer.minutes || 0) * 60 + (timer.seconds || 0));
                var h = Math.floor(totalSec / 3600);
                var m = Math.floor((totalSec % 3600) / 60);
                var s = totalSec % 60;
                function pad2(n) { return (n < 10 ? '0' : '') + n; }
                if (h > 0) {
                    return '[' + h + ':' + pad2(m) + ':' + pad2(s) + ']';
                }
                return '[' + pad2(m) + ':' + pad2(s) + ']';
            }
            let totalRecordedBytes = 0;
            const MAX_UPLOAD_BYTES = ${injectedMaxAudioUploadBytes};
            const AUTO_SAVE_MARGIN_BYTES = ${injectedAutoSaveMarginBytes};
            const AUTO_SAVE_BYTES = Math.max(0, MAX_UPLOAD_BYTES - AUTO_SAVE_MARGIN_BYTES);
            const SIZE_WARN_NEAR_BYTES = Math.min(512 * 1024 * 1024, Math.floor(MAX_UPLOAD_BYTES / 8));
            const SIZE_DANGER_NEAR_BYTES = Math.min(128 * 1024 * 1024, Math.floor(MAX_UPLOAD_BYTES / 32));
            const POST_MESSAGE_BLOB_MAX_BYTES = 32 * 1024 * 1024;
            const EXTERNAL_BACKUP_BUCKET_NAME = 'backup-audio';
            let autoSaveTriggered = false;
            let backupUploadInProgress = false;
            let stream = null;
            let mediaRecorder = null;
            let audioChunks = [];
            let recordedAudioBlob = null;
            let currentMimeType = 'audio/webm;codecs=opus'; // Store detected MIME type for Safari compatibility
            
            // Session tracking for deduplication
            function generateSessionId() {
                return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            }
            
            let recordingSessionId = generateSessionId();
            let hasFinalSaveBeenSent = false;
            let lastSavedCopilotAudioSlot = audioNumber;
            let copilotPersistFolderYear = scheduledDate ? scheduledDate.split('-')[0] : new Date().getFullYear().toString();
            let copilotPersistFolderDate = scheduledDate || new Date().toISOString().split('T')[0];
            var lastClinicalTabsStructured = null;
            var copilotPersistDebounceTimer = null;

            // Backup functionality
            let backupTimerInterval = null;
            let backupCount = 0;
            const BACKUP_INTERVAL_MS = ${clampedBackupIntervalMs};
            const RECORDING_TIMESLICE_MS = ${injectedRecorderTimesliceMs};
            
            // Audio level detection and visualizer variables
            let audioContext = null;
            let analyser = null;
            let microphone = null;
            let audioLevelInterval = null;
            let currentAudioLevel = 0;
            let audioBars = [];
            
            // Mic testing variables
            let isTestingMic = false;
            let testMicStream = null;
            let testMicContext = null;
            let testMicAnalyser = null;
            let testMicSource = null;
            let testMicLevelInterval = null;
            let testMicAudioElement = null;

            // Live transcription (browser speech API fallback) state
            let recognition = null;
            let isRecognitionActive = false;
            let transcriptText = '';
            let micTranscriptText = '';
            let systemAudioTranscriptText = '';
            /** Visual lines with recording-time prefix (Mic / SpeechRecognition finals). Plain micTranscriptText stays merge-friendly for saves. */
            let micTranscriptDisplayLines = [];
            let micLiveInterim = '';
            let systemAudioDisplayLines = [];
            let systemAudioLivePartial = '';
            let lastCapturedTranscript = '';
            let latestLiveTranscriptText = '';
            var LIVE_MERGED_PLACEHOLDER = 'Merged timeline appears when live or system audio has text.';

            function resetStructuredTranscriptLines() {
                micTranscriptDisplayLines = [];
                micLiveInterim = '';
                systemAudioDisplayLines = [];
                systemAudioLivePartial = '';
            }

            function buildMicLiveDisplayMultiline() {
                var base = micTranscriptDisplayLines.join('\\n');
                var im = (micLiveInterim || '').trim();
                if (!base && !im) return '';
                if (!im) return base;
                return base ? (base + '\\n' + im) : im;
            }

            function pushMicFinalTimedLine(segment) {
                var trimmed = (segment || '').trim();
                if (!trimmed) return;
                micTranscriptText += segment + ' ';
                micTranscriptDisplayLines.push(formatRecordingTimestamp() + ' ' + trimmed);
            }

            function buildSystemLiveDisplayMultiline() {
                var base = systemAudioDisplayLines.join('\\n');
                var partial = (systemAudioLivePartial || '').trim();
                if (!base && !partial) return '';
                if (!partial) return base;
                return base ? (base + '\\n' + partial) : partial;
            }

            function pushSystemAudioTimedLine(piece) {
                var txt = (piece || '').trim();
                if (!txt) return;
                var line = formatRecordingTimestamp() + ' ' + txt;
                var prev = systemAudioDisplayLines.length
                    ? systemAudioDisplayLines[systemAudioDisplayLines.length - 1]
                    : '';
                if (line === prev) return;
                systemAudioDisplayLines.push(line);
            }

            function bracketInnerToSeconds(inner) {
                var parts = String(inner || '').split(':').map(function (x) {
                    var n = parseInt(x, 10);
                    return Number.isFinite(n) ? n : NaN;
                });
                if (parts.some(function (n) { return !Number.isFinite(n); })) return 86400000;
                if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
                if (parts.length === 2) return parts[0] * 60 + parts[1];
                return 86400000;
            }

            function parsedStampFromTimedLine(line) {
                var lineStr = String(line || '').trim();
                var m = lineStr.match(/^\\[([^\\]]+)\\]\\s*(.*)$/);
                if (!m) return { sec: 86400000 - 1, bracket: '[?]', text: lineStr };
                return {
                    sec: bracketInnerToSeconds(m[1]),
                    bracket: '[' + String(m[1]).trim() + ']',
                    text: (m[2] || '').trim(),
                };
            }

            /** Mic + system finals sorted by [recording clock] stamps; provisional lines follow. Labels match request copy. */
            function buildLiveSystemMergedMultiline() {
                var LABEL_MIC = 'Live transcription';
                var LABEL_SYS = 'System Audio';
                var items = [];
                var tie = 0;
                micTranscriptDisplayLines.forEach(function (ln) {
                    var p = parsedStampFromTimedLine(ln);
                    items.push({
                        sec: p.sec,
                        tie: tie++,
                        out: p.bracket + ' ' + LABEL_MIC + ': ' + (p.text || ''),
                    });
                });
                systemAudioDisplayLines.forEach(function (ln) {
                    var p = parsedStampFromTimedLine(ln);
                    items.push({
                        sec: p.sec,
                        tie: tie++,
                        out: p.bracket + ' ' + LABEL_SYS + ': ' + (p.text || ''),
                    });
                });
                items.sort(function (a, b) {
                    if (a.sec !== b.sec) return a.sec - b.sec;
                    return a.tie - b.tie;
                });
                var finals = items.map(function (it) { return it.out; }).join('\\n');
                var prov = [];
                var curStamp = formatRecordingTimestamp();
                if ((micLiveInterim || '').trim()) {
                    prov.push(curStamp + ' ' + LABEL_MIC + ' (in progress): ' + micLiveInterim.trim());
                }
                if ((systemAudioLivePartial || '').trim()) {
                    prov.push(curStamp + ' ' + LABEL_SYS + ' (in progress): ' + systemAudioLivePartial.trim());
                }
                if (prov.length) {
                    return finals ? finals + '\\n\\n' + prov.join('\\n') : prov.join('\\n');
                }
                return finals;
            }

            function updateLiveSystemMergedPanels() {
                var merged = (buildLiveSystemMergedMultiline() || '').trim();
                var text = merged || LIVE_MERGED_PLACEHOLDER;
                var panel = document.getElementById('liveSystemMergedOutput');
                var tabM = document.getElementById('liveTranscriptMergedContent');
                if (panel) {
                    panel.textContent = text;
                    panel.style.color = '';
                }
                if (tabM) {
                    tabM.textContent = text;
                    tabM.style.color = '';
                }
                scheduleDebouncedLiveClinicalTabsRefresh();
            }

            /** Persisted with audio/backups/parent handlers: merged Live + System timeline when present. */
            function buildLiveTranscriptTextForSave() {
                var merged = (buildLiveSystemMergedMultiline() || '').trim();
                if (merged && merged !== LIVE_MERGED_PLACEHOLDER) return merged;
                var forAi = (buildCombinedLiveTranscriptForAi() || '').trim();
                if (forAi) return forAi;
                return (
                    (transcriptText || '').trim() ||
                    (micTranscriptText || '').trim() ||
                    (systemAudioTranscriptText || '').trim()
                );
            }

            function buildCombinedLiveTranscriptForAi() {
                var micEl = document.getElementById('liveTranscriptTabContent');
                var sysEl = document.getElementById('liveTranscriptSystemAudioContent');
                var mic = (micTranscriptText || '').trim() || (transcriptText || '').trim();
                if (!mic && micEl) mic = (micEl.textContent || '').trim();
                var sys = (systemAudioTranscriptText || '').trim();
                if (!sys && sysEl) sys = (sysEl.textContent || '').trim();
                sys = sys.replace(/^No system audio transcript yet\\.?$/i, '').trim();
                var parts = [];
                if (mic) parts.push('Mic / visit audio:\\n' + mic);
                if (sys) parts.push('System / meeting audio:\\n' + sys);
                return parts.join('\\n\\n---\\n\\n');
            }

            const USE_BROWSER_ONLY_LIVE_TRANSCRIPTION = true;
            let liveWs = null;
            let liveWsConfigAccepted = false;
            let liveWsConnecting = false;
            let liveWsCloseTimer = null;
            let liveWsFlushTimer = null;
            /** Corti expects each WS binary frame to be a full MediaRecorder blob (same as useWebSocket sendAudioChunk), not a byte tail into a merged WebM. */
            let lastLiveWsSentChunkIndex = 0;
            let liveWsFlushChain = Promise.resolve();
            let includeSystemAudioForCurrentRecording = false;
            let localWhisperFlushChain = Promise.resolve();
            const LOCAL_SYSTEM_AUDIO_SEGMENT_SECONDS = 4;
            const USE_CLIENT_SIDE_SYSTEM_STT = true;
            let clientSideSystemSttFn = null;
            let clientSideSystemSttLoading = null;
            let clientSideSystemSttUnavailable = false;
            let clientSideSystemSttWarned = false;
            let systemAudioRecorder = null;
            let systemAudioChunkIndex = 0;
            let systemAudioSttTapCleanup = null;
            let systemAudioSttStreamRef = null;

            function stopSystemAudioSttTap() {
                if (systemAudioSttTapCleanup) {
                    try { systemAudioSttTapCleanup(); } catch (e) { console.warn('🎤 [CLIENT-STT] tap cleanup:', e); }
                    systemAudioSttTapCleanup = null;
                }
            }

            async function startSystemAudioSttTap(mediaStream) {
                stopSystemAudioSttTap();
                if (!mediaStream || !mediaStream.getAudioTracks || mediaStream.getAudioTracks().length === 0) return;
                if (!USE_CLIENT_SIDE_SYSTEM_STT || !getLiveTranscriptionSetting()) return;
                try {
                    const handler = await ensureClientSideSystemStt();
                    if (!handler || typeof handler.feedAudioBuffer !== 'function') {
                        console.warn('🎤 [CLIENT-STT] No feedAudioBuffer; PCM tap skipped');
                        return;
                    }
                    const AC = window.AudioContext || window.webkitAudioContext;
                    const ctx = new AC();
                    const source = ctx.createMediaStreamSource(mediaStream);
                    var bufferSize = 4096;
                    var proc = ctx.createScriptProcessor(bufferSize, 1, 1);
                    proc.onaudioprocess = function (e) {
                        try {
                            var input = e.inputBuffer.getChannelData(0);
                            var rate = e.inputBuffer.sampleRate;
                            var normalized = downsampleTo16kMono(input, rate);
                            if (!normalized.length) return;
                            var ab = createMonoAudioBuffer(normalized, 16000);
                            handler.feedAudioBuffer(ab);
                        } catch (err) {
                            console.warn('🎤 [CLIENT-STT] tap process error:', (err && err.message) ? err.message : err);
                        }
                    };
                    source.connect(proc);
                    proc.connect(ctx.destination);
                    systemAudioSttTapCleanup = function () {
                        try { proc.disconnect(); } catch (_) {}
                        try { source.disconnect(); } catch (_) {}
                        try { ctx.close(); } catch (_) {}
                    };
                    console.log('🎤 [CLIENT-STT] System-audio PCM tap started');
                } catch (e) {
                    console.warn('🎤 [CLIENT-STT] Failed to start PCM tap:', (e && e.message) ? e.message : e);
                }
            }

            function setSystemAudioSource(sourceKey) {
                const node = document.getElementById('systemAudioSourceBadge');
                if (!node) return;
                if (sourceKey === 'client') {
                    node.textContent = 'Source: Client model';
                    node.style.color = '#2f855a';
                } else if (sourceKey === 'loading') {
                    node.textContent = 'Source: Loading client model...';
                    node.style.color = '#b7791f';
                } else if (sourceKey === 'backend') {
                    node.textContent = 'Source: Backend fallback';
                    node.style.color = '#b7791f';
                } else if (sourceKey === 'unavailable') {
                    node.textContent = 'Source: Client unavailable';
                    node.style.color = '#c53030';
                } else {
                    node.textContent = 'Source: Detecting…';
                    node.style.color = '#718096';
                }
            }

            function setSystemAudioUnavailableNotice(reason) {
                systemAudioDisplayLines = [];
                systemAudioLivePartial = '';
                const node = document.getElementById('systemAudioTranscriptionOutput');
                const tabSys = document.getElementById('liveTranscriptSystemAudioContent');
                const detail = (reason || '').trim();
                const shortDetail = detail ? (' (' + detail.slice(0, 120) + ')') : '';
                const msg =
                    'System-audio local transcription unavailable. Could not reach the Vosk service. Check REACT_APP_VOSK_SERVICE_ORIGIN or your network.' +
                    shortDetail;
                if (node) {
                    node.textContent = msg;
                    node.style.color = '#c53030';
                }
                if (tabSys) {
                    tabSys.textContent = msg;
                    tabSys.style.color = '#c53030';
                }
            }

            function mergeSystemText(prev, next) {
                const a = (prev || '').trim();
                const b = (next || '').trim();
                if (!b) return a;
                if (!a) return b;
                if (a.endsWith(b)) return a;
                if (b.startsWith(a)) return b;
                return (a + ' ' + b).trim();
            }

            function downsampleTo16kMono(input, sourceSampleRate) {
                if (!input || !input.length) return new Float32Array();
                const srcRate = Number(sourceSampleRate) || 16000;
                if (srcRate === 16000) return input;
                const ratio = srcRate / 16000;
                if (!Number.isFinite(ratio) || ratio <= 0) return input;
                const outLength = Math.max(1, Math.floor(input.length / ratio));
                const output = new Float32Array(outLength);
                let outIndex = 0;
                let inIndex = 0;
                while (outIndex < outLength) {
                    const nextInIndex = Math.min(input.length, Math.floor((outIndex + 1) * ratio));
                    let accum = 0;
                    let count = 0;
                    while (inIndex < nextInIndex) {
                        accum += input[inIndex];
                        inIndex += 1;
                        count += 1;
                    }
                    output[outIndex] = count > 0 ? accum / count : 0;
                    outIndex += 1;
                }
                return output;
            }

            function createMonoAudioBuffer(input, sampleRate) {
                const data = input || new Float32Array();
                if (typeof AudioBuffer === 'function') {
                    const audioBuffer = new AudioBuffer({
                        length: data.length,
                        numberOfChannels: 1,
                        sampleRate: sampleRate,
                    });
                    audioBuffer.copyToChannel(data, 0);
                    return audioBuffer;
                }
                const AC = window.AudioContext || window.webkitAudioContext;
                if (!AC) throw new Error('AudioContext unavailable for Vosk buffer conversion');
                const ac = new AC({ sampleRate: sampleRate });
                const audioBuffer = ac.createBuffer(1, data.length, sampleRate);
                audioBuffer.copyToChannel(data, 0);
                try { ac.close(); } catch (_) {}
                return audioBuffer;
            }

            async function ensurePopupVoskRecognizer() {
                setSystemAudioSource('loading');
                const MODEL_LOAD_TIMEOUT_MS = 180000;
                const FRONTEND_ORIGIN = (() => {
                    try {
                        const openerOrigin = window.opener && window.opener.location && window.opener.location.origin;
                        if (openerOrigin && /^https?:/i.test(openerOrigin)) return openerOrigin;
                    } catch (_) {}
                    try {
                        if (window.location && /^https?:/i.test(window.location.origin)) return window.location.origin;
                    } catch (_) {}
                    return '';
                })();
                const openerBasePath = (() => {
                    try {
                        const rawPath = String(
                            (window.opener && window.opener.location && window.opener.location.pathname) || '',
                        ).split('?')[0];
                        const firstSegment = rawPath.split('/').filter(Boolean)[0];
                        if (firstSegment) return '/' + firstSegment;
                    } catch (_) {}
                    return '';
                })();
                const openerBundleBasePath = (() => {
                    try {
                        const openerDoc = window.opener && window.opener.document;
                        if (!openerDoc) return '';
                        const bundleScript = openerDoc.querySelector('script[src*="/static/js/"]');
                        if (!bundleScript) return '';
                        const src = String(bundleScript.getAttribute('src') || '');
                        const marker = '/static/js/';
                        const idx = src.indexOf(marker);
                        if (idx <= 0) return '';
                        return src.slice(0, idx);
                    } catch (_) {}
                    return '';
                })();
                const toAbsoluteUrl = (path) => {
                    const lowerPath = String(path || '').toLowerCase();
                    if (lowerPath.startsWith('http://') || lowerPath.startsWith('https://')) return path;
                    return FRONTEND_ORIGIN ? (FRONTEND_ORIGIN + path) : path;
                };
                const buildAssetCandidates = (assetPath) => {
                    const normalizedPath = assetPath.startsWith('/') ? assetPath : ('/' + assetPath);
                    // Prefer prefixed paths first: bare /file often serves SPA index.html (invalid as model archive).
                    const variants = [];
                    if (openerBundleBasePath) {
                        variants.push(openerBundleBasePath + normalizedPath);
                    }
                    variants.push('/NotesAI' + normalizedPath);
                    if (openerBasePath && openerBasePath !== '/NotesAI') {
                        variants.push(openerBasePath + normalizedPath);
                    }
                    variants.push(normalizedPath);
                    return Array.from(new Set(variants)).map(toAbsoluteUrl);
                };
                // Read the Vosk service config from the opener (set by App.js's
                // __notesaiClientSystemSttGet warmup). Poll briefly because
                // the popup may be opened before the opener finishes
                // resolving the manifest.
                const getOpenerVoskConfig = async () => {
                    try {
                        for (let i = 0; i < 30; i++) {
                            const cfg = window.opener && window.opener.__notesaiVoskServiceConfig;
                            if (cfg && cfg.scriptUrl && cfg.modelUrl) return cfg;
                            await new Promise((r) => setTimeout(r, 200));
                        }
                    } catch (_) {}
                    return null;
                };
                const openerVoskConfig = await getOpenerVoskConfig();
                const cdnScriptUrl = openerVoskConfig ? openerVoskConfig.scriptUrl : null;
                const cdnModelUrl = openerVoskConfig ? openerVoskConfig.modelUrl : null;
                // Prefer the Vosk URLs injected by generateExternalHTML (sourced
                // from src/components/env.js -> services/vosk/config.js). These are baked
                // into the popup at window-open time, so the popup loads them
                // immediately without waiting on the opener's async warmup.
                const injectedScriptUrl =
                    (typeof EXTERNAL_VOSK_JS_URL === 'string' && EXTERNAL_VOSK_JS_URL) || null;
                const scriptCandidates = [
                    injectedScriptUrl,
                    cdnScriptUrl,
                    ...buildAssetCandidates('/vendors/vosk.js'),
                ].filter(Boolean);
                let scriptErr = null;
                const loadScriptViaSrc = async (scriptSrc) => {
                    const resp = await fetch(scriptSrc, { cache: 'default' });
                    if (!resp.ok) throw new Error('HTTP ' + resp.status + ' for ' + scriptSrc);
                    const head = (await resp.text()).trimStart().slice(0, 64);
                    if (!head || head.startsWith('<')) {
                        throw new Error('Invalid JavaScript payload for ' + scriptSrc);
                    }
                    const absoluteSrc = new URL(scriptSrc, window.location.href).href;
                    await new Promise((resolve, reject) => {
                        const scripts = document.querySelectorAll('script[data-vosk-src]');
                        for (let i = 0; i < scripts.length; i++) {
                            if (scripts[i].getAttribute('data-vosk-src') === absoluteSrc) {
                                resolve();
                                return;
                            }
                        }
                        const script = document.createElement('script');
                        script.async = true;
                        script.src = absoluteSrc;
                        script.dataset.voskSrc = absoluteSrc;
                        script.onload = function () { resolve(); };
                        script.onerror = function () {
                            reject(new Error('Failed to load script ' + absoluteSrc));
                        };
                        document.head.appendChild(script);
                    });
                };
                const getCreateModel = () => {
                    if (typeof window.createModel === 'function') return window.createModel;
                    if (window.Vosk && typeof window.Vosk.createModel === 'function') {
                        return window.Vosk.createModel.bind(window.Vosk);
                    }
                    return null;
                };
                let createModel = getCreateModel();
                if (!createModel) {
                    for (const scriptSrc of scriptCandidates) {
                        try {
                            const absoluteSrc = new URL(scriptSrc, window.location.href).href;
                            let existing = false;
                            const scripts = document.querySelectorAll('script[data-vosk-src]');
                            for (let i = 0; i < scripts.length; i++) {
                                if (scripts[i].getAttribute('data-vosk-src') === absoluteSrc) {
                                    existing = true;
                                    break;
                                }
                            }
                            if (!existing) {
                                await loadScriptViaSrc(scriptSrc);
                            }
                            createModel = getCreateModel();
                            if (createModel) break;
                        } catch (err) {
                            scriptErr = err;
                        }
                    }
                }
                if (!createModel) {
                    throw scriptErr || new Error('Vosk browser loader missing');
                }

                // Prefer flat-root .tar.gz (am/conf/graph at archive root). Nested-folder archives break vosk-browser Model().
                // The notesai-vosk-service CDN URL (resolved by App.js warmup) is preferred when available.
                const modelCandidates = cdnModelUrl
                    ? [
                          cdnModelUrl,
                          ...buildAssetCandidates('/vosk-model-small-en-us-0.15-flat.tar.gz'),
                          ...buildAssetCandidates('/vosk-model-small-en-us-0.15.tar.gz'),
                          ...buildAssetCandidates('/model.tar.gz'),
                          ...buildAssetCandidates('/vosk-model-small-en-us-0.15.zip'),
                          ...buildAssetCandidates('/model.zip'),
                      ]
                    : [
                          ...buildAssetCandidates('/vosk-model-small-en-us-0.15-flat.tar.gz'),
                          ...buildAssetCandidates('/vosk-model-small-en-us-0.15.tar.gz'),
                          ...buildAssetCandidates('/model.tar.gz'),
                          ...buildAssetCandidates('/vosk-model-small-en-us-0.15.zip'),
                          ...buildAssetCandidates('/model.zip'),
                      ];
                const loadModelWithTimeout = async (createModelFn, modelPath) => {
                    return Promise.race([
                        createModelFn(modelPath),
                        new Promise((_, reject) =>
                            setTimeout(
                                () => reject(new Error('Vosk model load timed out for ' + modelPath)),
                                MODEL_LOAD_TIMEOUT_MS,
                            ),
                        ),
                    ]);
                };
                let model = null;
                let lastModelErr = null;
                let loadedVoskModelArchiveUrl = null;
                for (const modelPath of modelCandidates) {
                    try {
                        console.log('🎤 [CLIENT-STT] Trying model archive:', modelPath);
                        model = await loadModelWithTimeout(createModel, modelPath);
                        if (model) {
                            loadedVoskModelArchiveUrl = modelPath;
                            break;
                        }
                    } catch (e) {
                        console.warn(
                            '🎤 [CLIENT-STT] Model candidate failed:',
                            modelPath,
                            (e && e.message) ? e.message : e,
                        );
                        lastModelErr = e;
                    }
                }
                if (!model) throw lastModelErr || new Error('Vosk model load failed');
                const voskModelLabel = (function () {
                    try {
                        var seg = String(loadedVoskModelArchiveUrl || '').split('/').filter(Boolean).pop() || '';
                        if (seg.toLowerCase().indexOf('small-en-us') >= 0) return 'Vosk browser — small-en-us-0.15';
                        if (seg.toLowerCase().indexOf('model.tar') >= 0 || seg.toLowerCase().indexOf('model.zip') >= 0) {
                            return 'Vosk browser — custom model archive';
                        }
                        return seg ? 'Vosk browser — ' + seg : 'Vosk browser';
                    } catch (_) {
                        return 'Vosk browser';
                    }
                })();
                console.log(
                    '🎤 [TRANSCRIPTION] Active transcription model:',
                    voskModelLabel,
                    '| archive URL:',
                    loadedVoskModelArchiveUrl,
                );

                const applyFinalSystemText = (raw) => {
                    const piece = (raw || '').trim();
                    if (!piece) return;
                    systemAudioTranscriptText = mergeSystemText(systemAudioTranscriptText, piece);
                    pushSystemAudioTimedLine(piece);
                    systemAudioLivePartial = '';
                    transcriptText = [micTranscriptText, systemAudioTranscriptText].filter(Boolean).join(' ').trim();
                    setLiveTranscriptionOutput(buildMicLiveDisplayMultiline() || (micTranscriptText || '').trim() || 'Listening...');
                };

                const recognizer = new model.KaldiRecognizer(16000);
                recognizer.setWords?.(true);
                recognizer.setPartialWords?.(false);
                let latestText = '';
                recognizer.on?.('result', (message) => {
                    const t = message?.result?.text;
                    if (typeof t === 'string' && t.trim()) {
                        latestText = t.trim();
                        applyFinalSystemText(t);
                    }
                });
                recognizer.on?.('partialresult', (message) => {
                    const p = message?.result?.partial;
                    if (typeof p === 'string' && p.trim()) {
                        latestText = p.trim();
                        systemAudioLivePartial = p.trim();
                        setLiveTranscriptionOutput((buildMicLiveDisplayMultiline() || '').trim() || 'Listening...');
                    }
                });

                const feedAudioBuffer = (audioBuffer) => {
                    if (typeof recognizer.acceptWaveform === 'function') {
                        recognizer.acceptWaveform(audioBuffer);
                    }
                };

                /** ~0.25s at 16 kHz — small chunks keep the worker responsive; one huge buffer appears to hang. */
                const VOSK_FILE_CHUNK_SAMPLES = 4000;

                const transcribePcm = async (pcmFloat32, options = {}) => {
                    const normalized = downsampleTo16kMono(
                        pcmFloat32,
                        Number(options && options.inputSampleRate) || 16000,
                    );
                    if (!normalized || normalized.length === 0) return { text: '' };
                    const audioBuffer = createMonoAudioBuffer(normalized, 16000);
                    if (typeof recognizer.acceptWaveform === 'function') {
                        recognizer.acceptWaveform(audioBuffer);
                    } else {
                        throw new Error('Vosk recognizer missing acceptWaveform API');
                    }
                    await new Promise((resolve) => setTimeout(resolve, 50));
                    return { text: (latestText || '').trim() };
                };

                const transcribeFullFilePcm = async (pcmFloat32, inputSampleRate) => {
                    const normalized = downsampleTo16kMono(
                        pcmFloat32,
                        Number(inputSampleRate) || 16000,
                    );
                    if (!normalized || normalized.length === 0) return '';

                    const segmentTexts = [];
                    const onResult = (ev) => {
                        const d = ev && ev.detail;
                        if (!d || !d.result) return;
                        const trimmed = String(dResultText(d.result) || '').trim();
                        if (trimmed) segmentTexts.push(trimmed);
                    };
                    function dResultText(r) {
                        if (!r) return '';
                        if (typeof r.text === 'string') return r.text;
                        if (typeof r.result === 'string') return r.result;
                        return '';
                    }

                    recognizer.addEventListener('result', onResult);

                    try {
                        for (let i = 0; i < normalized.length; i += VOSK_FILE_CHUNK_SAMPLES) {
                            const end = Math.min(i + VOSK_FILE_CHUNK_SAMPLES, normalized.length);
                            const slice = normalized.subarray(i, end);
                            if (slice.length === 0) break;
                            const audioBuffer = createMonoAudioBuffer(slice, 16000);
                            recognizer.acceptWaveform(audioBuffer);
                            await new Promise((r) => setTimeout(r, 15));
                        }
                        if (typeof recognizer.retrieveFinalResult === 'function') {
                            recognizer.retrieveFinalResult();
                        }
                        const finalWaitMs = Math.min(
                            8000,
                            500 + Math.floor(normalized.length / 16000) * 120,
                        );
                        await new Promise((r) => setTimeout(r, finalWaitMs));
                    } finally {
                        recognizer.removeEventListener('result', onResult);
                    }

                    const fromSegments = segmentTexts.join(' ').replace(/\\s+/g, ' ').trim();
                    const fromLatest = (latestText || '').trim();
                    return fromSegments || fromLatest;
                };

                return {
                    transcribePcm: transcribePcm,
                    feedAudioBuffer: feedAudioBuffer,
                    transcribeFullFilePcm: transcribeFullFilePcm,
                    voskModelLabel: voskModelLabel,
                    voskModelArchiveUrl: loadedVoskModelArchiveUrl,
                };
            }

            async function ensureClientSideSystemStt() {
                if (!USE_CLIENT_SIDE_SYSTEM_STT) {
                    setSystemAudioSource('unavailable');
                    return null;
                }
                if (clientSideSystemSttUnavailable) return null;
                if (clientSideSystemSttFn) return clientSideSystemSttFn;
                if (clientSideSystemSttLoading) return clientSideSystemSttLoading;
                clientSideSystemSttLoading = (async () => {
                    try {
                        // Always load Vosk in the popup document so the model worker and transcript UI share one context.
                        const asr = await ensurePopupVoskRecognizer();
                        if (!asr || typeof asr.transcribePcm !== 'function') {
                            throw new Error('client STT handler invalid');
                        }
                        clientSideSystemSttFn = asr;
                        clientSideSystemSttWarned = false;
                        setSystemAudioSource('client');
                        console.log('🎤 [CLIENT-STT] System-audio recognizer ready');
                        return asr;
                    } catch (e) {
                        clientSideSystemSttUnavailable = true;
                        setSystemAudioSource('unavailable');
                        const msg = (e && e.message) ? e.message : String(e || '');
                        console.warn('🎤 [CLIENT-STT] Disabled for this popup session:', msg.slice(0, 260));
                        if (!clientSideSystemSttWarned) {
                            clientSideSystemSttWarned = true;
                            setSystemAudioUnavailableNotice(msg);
                            showWarning('System-audio local transcription is unavailable. Could not reach the Vosk service. Check REACT_APP_VOSK_SERVICE_ORIGIN or your network.');
                        }
                        return null;
                    } finally {
                        clientSideSystemSttLoading = null;
                    }
                })();
                return clientSideSystemSttLoading;
            }

            async function transcribeChunkWithClientSystemStt(chunkBlob) {
                const handler = await ensureClientSideSystemStt();
                if (!handler) return null;
                var transcribePcm = handler.transcribePcm || handler;
                if (typeof transcribePcm !== 'function') return null;
                const arr = await chunkBlob.arrayBuffer();
                if (!arr || arr.byteLength === 0) return '';
                const AC = window.AudioContext || window.webkitAudioContext;
                if (!AC) return null;
                const ac = new AC();
                try {
                    const decoded = await ac.decodeAudioData(arr.slice(0));
                    const pcm = decoded.getChannelData(0);
                    const out = await transcribePcm(pcm, {
                        chunk_length_s: LOCAL_SYSTEM_AUDIO_SEGMENT_SECONDS,
                        stride_length_s: 0,
                        return_timestamps: false,
                        inputSampleRate: decoded.sampleRate,
                    });
                    if (typeof out === 'string') return out.trim();
                    if (out && typeof out.text === 'string') return out.text.trim();
                    return '';
                } finally {
                    try { await ac.close(); } catch (_) {}
                }
            }
            
            // Aggregate (mic + system/meeting) audio cleanup - call on stop when include system audio was used
            let aggregateCleanup = null;
            
            // Initialize audio visualizer bars
            function initializeAudioBars() {
                audioBars = [
                    document.getElementById('audioBar1'),
                    document.getElementById('audioBar2'),
                    document.getElementById('audioBar3'),
                    document.getElementById('audioBar4'),
                    document.getElementById('audioBar5'),
                    document.getElementById('audioBar6'),
                    document.getElementById('audioBar7'),
                    document.getElementById('audioBar8')
                ];
            }
            
            // Start audio level monitoring
            function startAudioLevelMonitoring(stream) {
                try {
                    audioContext = new (window.AudioContext || window.webkitAudioContext)();
                    analyser = audioContext.createAnalyser();
                    microphone = audioContext.createMediaStreamSource(stream);
                    
                    analyser.fftSize = 256;
                    microphone.connect(analyser);
                    
                    // Start monitoring audio levels
                    audioLevelInterval = setInterval(() => {
                        const bufferLength = analyser.frequencyBinCount;
                        const dataArray = new Uint8Array(bufferLength);
                        analyser.getByteFrequencyData(dataArray);
                        
                        // Calculate average audio level
                        let sum = 0;
                        for (let i = 0; i < bufferLength; i++) {
                            sum += dataArray[i];
                        }
                        const average = sum / bufferLength;
                        currentAudioLevel = average;
                        
                        // Update visualizer bars
                        updateAudioVisualizer(average);
                    }, 100); // Update every 100ms
                    
                    console.log('🎵 Audio level monitoring started');
                } catch (error) {
                    console.warn('⚠️ Audio level monitoring failed:', error);
                }
            }

            function clearLiveWsFlushTimer() {
                if (liveWsFlushTimer) {
                    clearTimeout(liveWsFlushTimer);
                    liveWsFlushTimer = null;
                }
            }

            function enqueueLocalWhisperChunk(chunkBlob, chunkIndex) {
                if (!USE_BROWSER_ONLY_LIVE_TRANSCRIPTION) return Promise.resolve();
                if (!getLiveTranscriptionSetting()) return Promise.resolve();
                if (!includeSystemAudioForCurrentRecording) return Promise.resolve();
                if (!chunkBlob || !chunkBlob.size) return Promise.resolve();
                localWhisperFlushChain = localWhisperFlushChain
                    .then(() => transcribeChunkWithLocalWhisper(chunkBlob, chunkIndex))
                    .catch((e) => {
                        console.warn('🎤 [LOCAL-WHISPER] queue error:', (e && e.message) || e);
                    });
                return localWhisperFlushChain;
            }

            async function transcribeChunkWithLocalWhisper(chunkBlob, chunkIndex) {
                if (USE_CLIENT_SIDE_SYSTEM_STT) {
                    try {
                        const clientText = await transcribeChunkWithClientSystemStt(chunkBlob);
                        if (typeof clientText === 'string' && clientText.trim()) {
                            systemAudioTranscriptText = mergeSystemText(systemAudioTranscriptText, clientText);
                            pushSystemAudioTimedLine(clientText.trim());
                            systemAudioLivePartial = '';
                            transcriptText = [micTranscriptText, systemAudioTranscriptText].filter(Boolean).join(' ').trim();
                            setLiveTranscriptionOutput(buildMicLiveDisplayMultiline() || (micTranscriptText || '').trim() || 'Listening...');
                            console.log('🎤 [CLIENT-STT] System transcript:', clientText.slice(0, 80));
                            return;
                        }
                    } catch (clientErr) {
                        console.warn('🎤 [CLIENT-STT] Failed:', (clientErr && clientErr.message) || clientErr);
                    }
                }
                setSystemAudioSource('unavailable');
                // Backend fallback explicitly disabled by product decision.
                return;
            }

            /**
             * Send every MediaRecorder chunk we have not yet sent, each as one binary frame (matches useWebSocket sendAudioChunk).
             * Serialized so concurrent ondataavailable callbacks do not double-send or skip.
             */
            function flushMediaRecorderChunksToLiveWs(tag) {
                if (USE_BROWSER_ONLY_LIVE_TRANSCRIPTION) return Promise.resolve();
                liveWsFlushChain = liveWsFlushChain
                    .then(() => runFlushMediaRecorderChunksToLiveWs(tag))
                    .catch((e) => {
                        console.warn('🎤 [WS-TRANSCRIBE] flush failed:', (e && e.message) || e);
                    });
                return liveWsFlushChain;
            }

            async function runFlushMediaRecorderChunksToLiveWs(tag) {
                if (!getLiveTranscriptionSetting()) return;
                if (!audioChunks || audioChunks.length === 0) return;
                if (!liveWsConfigAccepted) return;
                await ensureLiveTranscriptionWebSocket();
                if (!liveWs || liveWs.readyState !== WebSocket.OPEN || !liveWsConfigAccepted) return;
                const start = lastLiveWsSentChunkIndex;
                const end = audioChunks.length;
                if (start >= end) return;
                for (let i = start; i < end; i++) {
                    if (!liveWs || liveWs.readyState !== WebSocket.OPEN || !liveWsConfigAccepted) break;
                    const chunk = audioChunks[i];
                    if (!chunk || !chunk.size) {
                        lastLiveWsSentChunkIndex = i + 1;
                        continue;
                    }
                    const ab = await chunk.arrayBuffer();
                    if (!liveWs || liveWs.readyState !== WebSocket.OPEN || !liveWsConfigAccepted) break;
                    liveWs.send(ab);
                    lastLiveWsSentChunkIndex = i + 1;
                    console.log('🎤 [WS-TRANSCRIBE] Sent recorder chunk', tag || '', {
                        index: i,
                        bytes: ab.byteLength,
                        totalChunks: audioChunks.length,
                    });
                }
            }
            
            // Stop audio level monitoring
            function stopAudioLevelMonitoring() {
                if (audioLevelInterval) {
                    clearInterval(audioLevelInterval);
                    audioLevelInterval = null;
                }
                if (audioContext) {
                    audioContext.close();
                    audioContext = null;
                }
                console.log('🎵 Audio level monitoring stopped');
            }
            
            // Backup: upload directly from popup when possible (avoids postMessage size limits on long recordings)
            async function runPeriodicBackup() {
                if (!(isRecording && !isPaused && audioChunks.length > 0)) return;
                if (backupUploadInProgress) {
                    console.warn('💾 Skipping backup tick — previous upload still in progress');
                    return;
                }
                backupUploadInProgress = true;
                try {
                    backupCount++;
                    console.log('💾 Creating backup #' + backupCount + ' at ' + timer.minutes + ':' + String(timer.seconds).padStart(2, '0'));
                    const backupBlob = new Blob(audioChunks, { type: currentMimeType });
                    if (!backupBlob || !backupBlob.size) {
                        console.warn('💾 Skipping backup — empty blob');
                        return;
                    }

                    const backupData = {
                        type: 'AUDIO_RECORDING_BACKUP',
                        patientId: patientId,
                        patientName: patientName,
                        ehrId: injectedEhrId,
                        duration: timer.minutes * 60 + timer.seconds,
                        timestamp: new Date().toISOString(),
                        backupNumber: backupCount,
                        audioNumber: audioNumber,
                        isBackup: true,
                        scheduledDate: scheduledDate,
                        patientScheduledDate: scheduledDate,
                        liveTranscription: getLiveTranscriptionSetting(),
                        liveTranscriptText: ((buildLiveTranscriptTextForSave() || '').trim() || transcriptText),
                        liveFactExtraction: false,
                        sessionId: recordingSessionId
                    };

                    if (EXTERNAL_SUPABASE_SERVICE_KEY && resolveSupabaseProjectUrl(EXTERNAL_SUPABASE_URL)) {
                        const directBackup = await uploadPeriodicBackupDirectly(backupBlob);
                        if (directBackup.success) {
                            if (window.opener) {
                                window.opener.postMessage({
                                    ...backupData,
                                    backupPreUploaded: true,
                                    backupUrl: directBackup.url,
                                    backupPath: directBackup.path,
                                    contentType: backupBlob.type || currentMimeType
                                }, '*');
                                console.log('💾 Backup #' + backupCount + ' uploaded directly; metadata sent to parent');
                            }
                            return;
                        }
                        console.warn('💾 Direct backup upload failed, trying postMessage fallback:', directBackup.error);
                    }

                    if (backupBlob.size > POST_MESSAGE_BLOB_MAX_BYTES) {
                        console.error('💾 Backup blob too large for postMessage (' + backupBlob.size + ' bytes). Enable direct Supabase upload.');
                        return;
                    }

                    if (window.opener) {
                        window.opener.postMessage({ ...backupData, audioBlob: backupBlob }, '*');
                        console.log('💾 Backup #' + backupCount + ' sent to parent window (blob)');
                    } else {
                        console.warn('💾 No parent window available for backup');
                    }
                } catch (backupErr) {
                    console.error('💾 Periodic backup failed:', backupErr);
                } finally {
                    backupUploadInProgress = false;
                }
            }

            function startBackupTimer() {
                if (backupTimerInterval) {
                    clearInterval(backupTimerInterval);
                    backupTimerInterval = null;
                }
                backupTimerInterval = setInterval(function() {
                    void runPeriodicBackup();
                }, BACKUP_INTERVAL_MS);
                console.log('💾 Backup timer started - will backup every ' + (BACKUP_INTERVAL_MS / 1000) + ' seconds');
            }
            
            function stopBackupTimer() {
                if (backupTimerInterval) {
                    clearInterval(backupTimerInterval);
                    backupTimerInterval = null;
                    console.log('💾 Backup timer stopped. Total backups: ' + backupCount);
                }
            }
            
            // Update audio visualizer bars
            function updateAudioVisualizer(audioLevel) {
                if (!audioBars.length) initializeAudioBars();
                
                const normalizedLevel = Math.min(audioLevel / 128, 1); // Normalize to 0-1
                
                audioBars.forEach((bar, index) => {
                    if (bar) {
                        const randomFactor = Math.random() * 0.5 + 0.5; // Add some randomness
                        const barHeight = Math.max(4, (normalizedLevel * 32 * randomFactor) + 4);
                        
                        bar.style.height = barHeight + 'px';
                        
                        if (audioLevel > 10) {
                            bar.classList.add('active');
                        } else {
                            bar.classList.remove('active');
                        }
                    }
                });
            }
            
            function getBrowserWarning() {
                // Brave is Chromium but blocks the speech recognition network calls
                var isBrave = (typeof navigator.brave !== 'undefined' && navigator.brave !== null);
                if (isBrave) {
                    return 'Live transcription does not work on Brave browser — Brave blocks the speech recognition network connection even though it is Chromium-based. Please use a Chromium-based browser like Chrome, Comet, or Edge.';
                }
                // Non-Chromium: no webkitSpeechRecognition at all
                var isChromium = !!(window.chrome || window.webkitSpeechRecognition || window.SpeechRecognition);
                if (!isChromium) {
                    return 'Live transcription only works on Chromium-based browsers. Please use Chrome, Comet, or Edge.';
                }
                return null;
            }

            function updateTranscriptionSetting() {
                const checkbox = document.getElementById('liveTranscriptionCheckbox');
                const label = checkbox.nextElementSibling;
                label.style.color = checkbox.checked ? '#38a169' : '#666';
                persistLiveSetting('external_live_transcription_enabled', checkbox.checked);

                if (checkbox.checked) {
                    var warning = getBrowserWarning();
                    if (warning) {
                        setLiveTranscriptionStatus('Unavailable');
                        resetStructuredTranscriptLines();
                        setLiveTranscriptionOutput(warning);
                        // Uncheck so user can't accidentally leave it on
                        checkbox.checked = false;
                        label.style.color = '#666';
                        persistLiveSetting('external_live_transcription_enabled', false);
                        syncSystemAudioCheckboxState();
                        return;
                    }
                }

                handleLiveTranscriptionToggle();
                syncSystemAudioCheckboxState();
            }
            
            function syncSystemAudioCheckboxState() {
                const liveCb = document.getElementById('liveTranscriptionCheckbox');
                const systemAudioCb = document.getElementById('includeSystemAudioCheckbox');
                const recapCb = document.getElementById('aiRecapCheckbox');
                const recapLabel = document.getElementById('aiRecapCheckboxLabel');
                if (!liveCb) return;
                const liveEnabled = liveCb.checked;
                if (systemAudioCb) {
                    systemAudioCb.disabled = !liveEnabled;
                    if (!liveEnabled) systemAudioCb.checked = false;
                }
                if (recapCb) {
                    // Keep AI recap locked (enabled + non-interactive) like live transcription.
                    recapCb.disabled = true;
                    recapCb.checked = true;
                    if (recapLabel) recapLabel.style.color = '#666';
                }
            }
            
            function updateSystemAudioSetting() {
                const checkbox = document.getElementById('includeSystemAudioCheckbox');
                if (!checkbox) return;
                // Persist to localStorage
                try {
                    localStorage.setItem('includeSystemAudio', JSON.stringify(checkbox.checked));
                } catch (err) {
                    console.warn('⚠️ Unable to persist system audio setting', err);
                }
                // Sync with live transcription checkbox state
                syncSystemAudioCheckboxState();
            }

            function persistLiveSetting(key, value) {
                try {
                    localStorage.setItem(key, JSON.stringify(value));
                } catch (err) {
                    console.warn('⚠️ Unable to persist setting', key, err);
                }
            }

            function initializeLiveSettingsFromStorage() {
                try {
                    const liveCheckbox = document.getElementById('liveTranscriptionCheckbox');
                    if (liveCheckbox) {
                        // Keep live transcription enabled, but locked in external window.
                        liveCheckbox.checked = true;
                        liveCheckbox.disabled = true;
                    }
                    persistLiveSetting('external_live_transcription_enabled', true);
                    updateTranscriptionSetting();

                    // Keep AI recap enabled, but locked in external window.
                    const recapCb = document.getElementById('aiRecapCheckbox');
                    if (recapCb) {
                        recapCb.checked = true;
                        recapCb.disabled = true;
                    }
                    persistLiveSetting('ai_recap_enabled', true);
                    
                    // Initialize system audio checkbox from localStorage
                    const systemAudioCb = document.getElementById('includeSystemAudioCheckbox');
                    if (systemAudioCb) {
                        const savedSystemAudio = localStorage.getItem('includeSystemAudio');
                        if (savedSystemAudio !== null) {
                            systemAudioCb.checked = JSON.parse(savedSystemAudio);
                        }
                        // Sync with live transcription state
                        syncSystemAudioCheckboxState();
                    }
                } catch (err) {
                    console.warn('⚠️ Unable to initialize live settings', err);
                }
            }

            function ensureSpeechRecognition() {
                if (recognition) return recognition;

                const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
                if (!SpeechRecognition) {
                    console.warn('⚠️ SpeechRecognition API not available in this browser.');
                    setLiveTranscriptionStatus('Unavailable');
                    resetStructuredTranscriptLines();
                    setLiveTranscriptionOutput('Live transcription only works on Chromium-based browsers. Please use Chrome, Comet, or Edge.');
                    return null;
                }

                let networkErrorCount = 0;
                const MAX_NETWORK_ERRORS = 3;

                recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = 'en-US';

                recognition.onstart = () => {
                    isRecognitionActive = true;
                    setLiveTranscriptionStatus('Listening');
                    console.log('🎤 [TRANSCRIPT] Started');
                };

                recognition.onerror = (event) => {
                    if (event.error === 'no-speech') {
                        // Non-fatal: happens frequently between utterances; let auto-restart continue.
                        console.log('🎤 [TRANSCRIPT] no-speech (ignored)');
                        return;
                    }
                    console.error('🎤 [TRANSCRIPT] Error:', event.error);
                    isRecognitionActive = false;
                    if (event.error === 'not-allowed') {
                        resetStructuredTranscriptLines();
                        setLiveTranscriptionOutput('Microphone permission denied for speech recognition.');
                    } else if (event.error === 'network') {
                        networkErrorCount++;
                        if (networkErrorCount >= MAX_NETWORK_ERRORS) {
                            var isBrave = (typeof navigator.brave !== 'undefined');
                            var braveMsg = 'Live transcription does not work on Brave browser — Brave blocks the speech recognition network connection even though it is Chromium-based. Please use Chrome, Comet, or Edge.';
                            var genericMsg = 'Live transcription is unavailable. Please use a Chromium-based browser like Chrome, Comet, or Edge.';
                            setLiveTranscriptionStatus('Unavailable');
                            resetStructuredTranscriptLines();
                            setLiveTranscriptionOutput(isBrave ? braveMsg : genericMsg);
                            recognition = null;
                        }
                    }
                };

                recognition.onend = () => {
                    isRecognitionActive = false;
                    console.log('🎤 [TRANSCRIPT] Ended — isRecording:', isRecording, 'isPaused:', isPaused, 'liveEnabled:', getLiveTranscriptionSetting());
                    // Chrome fires onend after every final result (even with continuous=true),
                    // leaving a gap where subsequent audio goes un-transcribed. Auto-restart
                    // eliminates that gap so screen/meeting audio after the user speaks is captured.
                    if (isRecording && !isPaused && getLiveTranscriptionSetting() && recognition) {
                        setTimeout(() => {
                            if (!isRecognitionActive && recognition) {
                                try {
                                    recognition.start();
                                    console.log('🎤 [TRANSCRIPT] Auto-restarted');
                                } catch (err) {
                                    console.warn('🎤 [TRANSCRIPT] Could not restart:', err.name, err.message);
                                }
                            }
                        }, 150);
                    } else {
                        setLiveTranscriptionStatus('Idle');
                    }
                };

                recognition.onresult = (event) => {
                    networkErrorCount = 0;
                    let interim = '';
                    for (let i = event.resultIndex; i < event.results.length; i++) {
                        const segment = event.results[i][0].transcript;
                        if (event.results[i].isFinal) {
                            const trimmed = segment.trim();
                            if (trimmed) {
                                pushMicFinalTimedLine(segment);
                                console.log('🎤 [TRANSCRIPT] Final segment:', trimmed);
                            }
                        } else {
                            interim += segment;
                        }
                    }
                    micLiveInterim = interim;
                    transcriptText = [micTranscriptText, systemAudioTranscriptText].filter(Boolean).join(' ').trim();
                    var displayMic = (buildMicLiveDisplayMultiline() || '').trim();
                    setLiveTranscriptionOutput(displayMic || 'Listening...');
                };

                return recognition;
            }

            function getBackendBaseUrl() {
                var explicit = (EXTERNAL_BACKEND_BASE_URL || '').trim();
                if (explicit) return explicit.replace(/\\/$/, '');
                var h = window.location.hostname || '';
                if (h === 'localhost' || h === '127.0.0.1') {
                    return 'http://127.0.0.1:8000';
                }
                return '';
            }

            function parseClinicalAudioSlotFromUrl(url) {
                if (!url) return null;
                try {
                    var leaf = String(url).split('?')[0].split('/').pop() || '';
                    var m = /_Audio(\\d+)_/i.exec(leaf);
                    return m ? parseInt(m[1], 10) : null;
                } catch (_) {
                    return null;
                }
            }

            function resolveCopilotAudioSlotForPersist() {
                if (lastSavedCopilotAudioSlot != null && lastSavedCopilotAudioSlot >= 1) {
                    return lastSavedCopilotAudioSlot;
                }
                var fromSaved = parseClinicalAudioSlotFromUrl(savedAudioUrl);
                return fromSaved != null ? fromSaved : audioNumber;
            }

            function rememberClinicalTabsFromLlm(c) {
                if (!c || typeof c !== 'object') return;
                lastClinicalTabsStructured = {
                    source: 'llm',
                    assessment: c.assessment || '',
                    plan: c.plan || '',
                    evidence: c.evidence != null ? c.evidence : '',
                    consider: c.consider || '',
                    scores: c.scores !== undefined ? c.scores : null,
                    report: c.report || '',
                    key_clinical_data: c.key_clinical_data || [],
                    orders: Array.isArray(c.orders) ? c.orders : [],
                    followups: Array.isArray(c.followups) ? c.followups : [],
                };
                var ta = document.getElementById('clinicalReportPlainField');
                if (ta && ta.value) {
                    lastClinicalTabsStructured.report_plain = ta.value;
                }
                scheduleCopilotPersistDebounced();
                renderAiKeyDataArray();
            }

            function rememberClinicalTabsFromCorti(results) {
                if (!results || typeof results !== 'object') return;
                var soap = results.soap_notes || {};
                lastClinicalTabsStructured = {
                    source: 'corti_soap',
                    facts: results.facts || [],
                    soap_notes: soap,
                };
                var ta = document.getElementById('clinicalReportPlainField');
                if (ta && ta.value) {
                    lastClinicalTabsStructured.report_plain = ta.value;
                }
                scheduleCopilotPersistDebounced();
                renderAiKeyDataArray();
            }

            function scheduleCopilotPersistDebounced() {
                if (!COPILOT_ENABLED) return;
                if (copilotPersistDebounceTimer !== null) {
                    clearTimeout(copilotPersistDebounceTimer);
                }
                copilotPersistDebounceTimer = setTimeout(function () {
                    copilotPersistDebounceTimer = null;
                    void persistCopilotTabsSnapshot('debounced');
                }, 1400);
            }

            async function persistCopilotTabsSnapshot(reason) {
                if (!COPILOT_ENABLED) return;
                if (!AUTO_COPIOLT_ENABLED) return;
                var baseUrl = getBackendBaseUrl();
                if (!baseUrl) return;
                var slot = resolveCopilotAudioSlotForPersist();
                var tabs = lastClinicalTabsStructured;
                if (!tabs || typeof tabs !== 'object' || tabs.pending === true) {
                    // P0 fix: refuse to persist a placeholder. The previous code substituted
                    // { pending: true } here, which the backend then wrote on top of the
                    // already-saved good clinical tabs — silently destroying Brett's A&P.
                    console.warn('[COPILOT-PERSIST] aborting: tabs not ready', reason);
                    return;
                }
                try {
                    var resp = await fetch(baseUrl + '/api/transcription-backup/save-copilot/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            patient_id: patientId,
                            session_id: recordingSessionId,
                            clinical_tabs: tabs,
                            timestamp: new Date().toISOString(),
                            schema_name: EXTERNAL_DB_SCHEMA || 'public',
                            year: copilotPersistFolderYear,
                            date: copilotPersistFolderDate,
                            patient_name: patientName,
                            ehr_id: injectedEhrId,
                            audio_number: slot,
                            metadata: { save_reason: reason || '', external_popup: true },
                        }),
                    });
                    var j = await resp.json().catch(function () { return {}; });
                    if (!resp.ok || !j.success) {
                        console.warn('[COPILOT-PERSIST]', reason, (j && j.error) ? j.error : resp.status);
                    }
                } catch (e) {
                    console.warn('[COPILOT-PERSIST] failed', e);
                }
            }

            var liveClinicalAiDebounceTimer = null;
            var liveClinicalAiInFlight = false;
            var liveClinicalAiQueuedFlush = false;
            var liveClinicalAiRequestGeneration = 0;
            var liveClinicalAiLastFingerprint = '';
            var LIVE_CLINICAL_AI_DEBOUNCE_MS = 1800;
            var LIVE_CLINICAL_AI_MIN_CHARS = 40;
            var liveClinicalAiLastTime = 0;
            var LIVE_CLINICAL_AI_THROTTLE_MS = 30000;
            var lastLiveClinicalAiTranscript = '';

            function clearLiveClinicalDebounceTimer() {
                if (liveClinicalAiDebounceTimer !== null) {
                    clearTimeout(liveClinicalAiDebounceTimer);
                    liveClinicalAiDebounceTimer = null;
                }
            }

            function bumpLiveClinicalAiGeneration() {
                liveClinicalAiRequestGeneration++;
            }

            function liveClinicalTranscriptFingerprint(txt) {
                var s = (txt || '').trim();
                if (!s) return '';
                return String(s.length) + ':' + s.slice(0, 64) + ':' + s.slice(-64);
            }

            function getTranscriptPayloadForLiveClinicalAi() {
                var t = (buildLiveTranscriptTextForSave() || '').trim();
                if (!t || t === LIVE_MERGED_PLACEHOLDER) return '';
                return t;
            }

            function scheduleDebouncedLiveClinicalTabsRefresh() {
                if (!COPILOT_ENABLED) return;
                if (!AUTO_COPIOLT_ENABLED) return;
                if (!isRecording || isPaused) return;
                var t = getTranscriptPayloadForLiveClinicalAi();
                if (!t || t.length < LIVE_CLINICAL_AI_MIN_CHARS) return;
                
                var fp = liveClinicalTranscriptFingerprint(t);
                if (fp === liveClinicalAiLastFingerprint) return;

                if (liveClinicalAiDebounceTimer !== null) return;

                var now = Date.now();
                var elapsed = now - liveClinicalAiLastTime;
                var delay = Math.max(0, LIVE_CLINICAL_AI_THROTTLE_MS - elapsed);

                liveClinicalAiDebounceTimer = setTimeout(function () {
                    liveClinicalAiDebounceTimer = null;
                    void flushLiveClinicalTabsHaikuFetch();
                }, delay);
            }

            function extractKeyClinicalTerms(assessmentText, considerText, planText) {
                var terms = [];
                
                function cleanTerm(text) {
                    if (!text) return '';
                    var cleaned = text.trim()
                        .replace(/^[-*•\\s\\d+\\.]+/g, '')
                        .replace(/^(r\\/o|rule out|possible|suspected|hx of|history of|patient presents with|presents with|diagnosed with|assessed for|evaluation of|eval of|exhibiting|complaining of|c\\/o)\\s+/i, '')
                        .trim();
                    if (cleaned) {
                        cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
                    }
                    return cleaned;
                }

                if (considerText && considerText !== 'Not discussed' && considerText !== 'Nothing returned for Consider.') {
                    var considerLines = considerText.split(/[\\n;•\\-\\*]/);
                    considerLines.forEach(function (line) {
                        var term = cleanTerm(line);
                        if (term && term.length > 2 && term.length < 50 && term.indexOf('.') === -1) {
                            if (terms.indexOf(term) === -1) {
                                terms.push(term);
                            }
                        }
                    });
                }

                if (assessmentText && assessmentText !== 'Not discussed' && assessmentText !== 'No assessment/plan/evidence returned.') {
                    var assessmentSegments = assessmentText.split(/[\\n;•\\-\\*\\.]/);
                    assessmentSegments.forEach(function (segment) {
                        var term = cleanTerm(segment);
                        if (term && term.length > 3 && term.length < 40) {
                            if (terms.indexOf(term) === -1) {
                                terms.push(term);
                            }
                        }
                    });
                }

                if (terms.length === 0) {
                    var words = (assessmentText + ' ' + considerText).split(/[\\s,]+/);
                    words.forEach(function (word) {
                        var cleanWord = word.replace(/[^a-zA-Z]/g, '');
                        if (cleanWord && cleanWord.length > 3 && cleanWord.charAt(0) === cleanWord.charAt(0).toUpperCase()) {
                            var stopWords = ['Patient', 'Clinician', 'Assessment', 'Plan', 'Consider', 'Evidence', 'Report', 'Encounter', 'Nothing', 'Discussed', 'None', 'Subjective', 'Objective', 'The', 'This', 'With', 'From'];
                            if (stopWords.indexOf(cleanWord) === -1 && terms.indexOf(cleanWord) === -1) {
                                terms.push(cleanWord);
                            }
                        }
                    });
                }

                return terms.slice(0, 10).filter(Boolean);
            }

            function buildExistingClinicalTabsContext() {
                var contextObj = {};
                var assessment = '';
                var plan = '';
                var consider = '';
                var evidence = '';
                var scores = '';
                var report = '';

                if (lastClinicalTabsStructured && typeof lastClinicalTabsStructured === 'object') {
                    assessment = lastClinicalTabsStructured.assessment || '';
                    plan = lastClinicalTabsStructured.plan || '';
                    consider = lastClinicalTabsStructured.consider || '';
                    evidence = lastClinicalTabsStructured.evidence || '';
                    scores = lastClinicalTabsStructured.scores || '';
                    report = lastClinicalTabsStructured.report || '';
                }

                if (!assessment) {
                    var el = document.getElementById('apTabContent');
                    if (el) assessment = el.innerText || '';
                }
                if (!consider) {
                    var el = document.getElementById('considerTabContent');
                    if (el) consider = el.innerText || '';
                }
                if (!scores) {
                    var el = document.getElementById('scoresTabContent');
                    if (el) scores = el.innerText || '';
                }

                contextObj.assessment = assessment;
                contextObj.plan = plan;
                contextObj.consider = consider;
                contextObj.evidence = evidence;
                contextObj.scores = scores;
                contextObj.report = report;
                contextObj.orders = (lastClinicalTabsStructured && Array.isArray(lastClinicalTabsStructured.orders)) ? lastClinicalTabsStructured.orders : [];
                contextObj.followups = (lastClinicalTabsStructured && Array.isArray(lastClinicalTabsStructured.followups)) ? lastClinicalTabsStructured.followups : [];

                var keyInfo = [];
                if (assessment && String(assessment).trim() && String(assessment).trim() !== 'Not discussed') {
                    keyInfo.push({ key: 'assessment', label: 'Assessment', value: String(assessment).trim() });
                }
                if (plan && String(plan).trim() && String(plan).trim() !== 'Not discussed') {
                    keyInfo.push({ key: 'plan', label: 'Plan', value: String(plan).trim() });
                }
                if (consider && String(consider).trim() && String(consider).trim() !== 'Not discussed') {
                    keyInfo.push({ key: 'consider', label: 'Consider', value: String(consider).trim() });
                }
                if (evidence) {
                    var evStr = Array.isArray(evidence) ? evidence.filter(Boolean).join('\\\\n') : String(evidence).trim();
                    if (evStr && evStr !== 'No evidence summary.') {
                        keyInfo.push({ key: 'evidence', label: 'Evidence', value: evStr });
                    }
                }
                if (scores && String(scores).trim() && String(scores).trim() !== 'None mentioned') {
                    keyInfo.push({ key: 'scores', label: 'Scores', value: String(scores).trim() });
                }
                if (report && String(report).trim() && String(report).trim() !== 'Not discussed') {
                    keyInfo.push({ key: 'report', label: 'Report', value: String(report).trim() });
                }

                contextObj.key_info_array = keyInfo;

                // Use LLM-classified clinical data if available; otherwise fallback to dynamic extraction
                var clinicalKeys = [];
                if (lastClinicalTabsStructured && lastClinicalTabsStructured.key_clinical_data && lastClinicalTabsStructured.key_clinical_data.length > 0) {
                    clinicalKeys = lastClinicalTabsStructured.key_clinical_data;
                } else {
                    clinicalKeys = extractKeyClinicalTerms(assessment, consider, plan);
                }

                contextObj.key_clinical_data = clinicalKeys;
                return contextObj;
            }

            function toggleAiKeyDataDetails() {
                var details = document.getElementById('aiKeyDataDetails');
                var btn = document.getElementById('toggleAiKeyDataBtn');
                if (!details || !btn) return;
                if (details.style.display === 'none') {
                    details.style.display = 'flex';
                    btn.textContent = 'Hide Details';
                } else {
                    details.style.display = 'none';
                    btn.textContent = 'Show Details';
                }
            }
            window.toggleAiKeyDataDetails = toggleAiKeyDataDetails;

            function renderAiKeyDataArray() {
                var container = document.getElementById('aiKeyDataContainer');
                if (!container) return;
                var context = buildExistingClinicalTabsContext();
                var arr = context.key_clinical_data || [];
                var tabsArr = context.key_info_array || [];
                if (arr.length === 0 && tabsArr.length === 0) {
                    container.style.display = 'none';
                    container.innerHTML = '';
                    return;
                }
                
                var headerHtml = '<div style="display:flex; justify-content:space-between; align-items:center; width:100%; border-bottom:1px solid #bee3f8; padding-bottom:0.2rem; margin-bottom:0.35rem;">' +
                    '<span style="font-weight:700; color:#2b6cb0;">Preserved Context</span>' +
                    '<button id="toggleAiKeyDataBtn" onclick="toggleAiKeyDataDetails()" style="background:#3182ce; color:#fff; border:none; padding:0.15rem 0.45rem; border-radius:4px; font-size:0.65rem; font-weight:600; cursor:pointer;">Show Details</button>' +
                    '</div>';
                
                var keyHtml = '';
                if (arr.length > 0) {
                    keyHtml += '<div style="display:flex; flex-wrap:wrap; gap:0.25rem; align-items:center; margin-bottom:0.15rem;">' +
                        '<span style="font-weight:600; color:#4a5568; margin-right:0.15rem;">Key Data:</span>';
                    arr.forEach(function (item) {
                        keyHtml += '<span style="background:#e2e8f0; color:#475569; padding:0.08rem 0.35rem; border-radius:4px; font-weight:600; font-size:0.65rem; border:1px solid #cbd5e0; display:inline-block;">' + escapeHtmlClinical(item) + '</span>';
                    });
                    keyHtml += '</div>';
                } else {
                    keyHtml += '<div style="font-size:0.68rem; color:#718096; font-style:italic;">No explicit medical conditions extracted yet.</div>';
                }
                
                var bodyHtml = '<div id="aiKeyDataDetails" style="display:none; width:100%; flex-direction:column; gap:0.4rem; padding-top:0.35rem;">';
                tabsArr.forEach(function (item) {
                    bodyHtml += '<div style="background:#fff; border:1px solid #dbe3ee; border-radius:5px; padding:0.4rem; box-shadow:0 1px 3px rgba(0,0,0,0.05);">' +
                        '<div style="font-weight:700; color:#2c5282; font-size:0.68rem; margin-bottom:0.15rem; border-bottom:1px dashed #edf2f7; padding-bottom:0.1rem;">' + escapeHtmlClinical(item.label) + '</div>' +
                        '<div style="white-space:pre-wrap; font-size:0.68rem; color:#4a5568; line-height:1.35;">' + escapeHtmlClinical(item.value) + '</div>' +
                        '</div>';
                });
                bodyHtml += '</div>';

                var isAlreadyShown = false;
                var existingDetails = document.getElementById('aiKeyDataDetails');
                if (existingDetails && existingDetails.style.display === 'flex') {
                    isAlreadyShown = true;
                }
                
                container.innerHTML = headerHtml + keyHtml + bodyHtml;
                container.style.display = 'flex';
                
                if (isAlreadyShown) {
                    var newDetails = document.getElementById('aiKeyDataDetails');
                    var newBtn = document.getElementById('toggleAiKeyDataBtn');
                    if (newDetails && newBtn) {
                        newDetails.style.display = 'flex';
                        newBtn.textContent = 'Hide Details';
                    }
                }
            }

            async function flushLiveClinicalTabsHaikuFetch() {
                if (!COPILOT_ENABLED) return;
                if (!AUTO_COPIOLT_ENABLED) return;
                if (!isRecording || isPaused) return;
                var t = getTranscriptPayloadForLiveClinicalAi();
                if (!t || t.length < LIVE_CLINICAL_AI_MIN_CHARS) return;
                var fp = liveClinicalTranscriptFingerprint(t);
                if (fp === liveClinicalAiLastFingerprint) return;
                
                var diff = t;
                if (lastLiveClinicalAiTranscript && t.indexOf(lastLiveClinicalAiTranscript) === 0) {
                    diff = t.slice(lastLiveClinicalAiTranscript.length).trim();
                }
                
                if (!diff || diff.length < 5) {
                    return;
                }

                var baseUrl = getBackendBaseUrl();
                if (!baseUrl) return;
                if (liveClinicalAiInFlight) {
                    return;
                }
                var genSnapshot = liveClinicalAiRequestGeneration;
                liveClinicalAiInFlight = true;
                liveClinicalAiLastTime = Date.now();
                
                var requestBody = {
                    transcript: diff,
                    mode: 'clinical_only',
                    model_choice: COPILOT_LIVE_CLINICAL_MODEL_CHOICE,
                    userId: EXTERNAL_USER_ID,
                    patientId: patientId,
                    sessionId: recordingSessionId,
                    schema: EXTERNAL_DB_SCHEMA,
                    userJwtToken: EXTERNAL_USER_JWT_TOKEN,
                };
                
                var existingTabs = buildExistingClinicalTabsContext();
                if (existingTabs && existingTabs.key_info_array && existingTabs.key_info_array.length > 0) {
                    requestBody.existing_clinical_tabs = existingTabs;
                }

                try {
                    setAiTabsStatus('AI: live clinical tabs (Haiku)…', '#2b6cb0');
                    var resp = await fetch(baseUrl + '/api/generate-ai-recap/', {
                         method: 'POST',
                         headers: { 'Content-Type': 'application/json' },
                         body: JSON.stringify(requestBody),
                    });
                    var payload = await resp.json();
                    if (genSnapshot !== liveClinicalAiRequestGeneration) return;
                    if (!resp.ok || payload.error) {
                        throw new Error((payload && payload.error) ? payload.error : 'AI request failed');
                    }
                    if (payload.clinical_tabs) {
                        renderClinicalTabsFromLlm(payload.clinical_tabs);
                        liveClinicalAiLastFingerprint = fp;
                        lastLiveClinicalAiTranscript = t;
                    }
                    setAiTabsStatus('AI: live tabs updated (Haiku)', '#2f855a');
                } catch (e) {
                    if (genSnapshot !== liveClinicalAiRequestGeneration) return;
                    console.warn('🎤 [AI-TABS LIVE] Haiku refresh failed:', e);
                    setAiTabsStatus(
                        'AI: live tabs error - ' + ((e && e.message) ? e.message : 'unknown'),
                        '#c53030',
                    );
                } finally {
                    liveClinicalAiInFlight = false;
                    scheduleDebouncedLiveClinicalTabsRefresh();
                }
            }

            async function ensureLiveTranscriptionWebSocket() {
                if (USE_BROWSER_ONLY_LIVE_TRANSCRIPTION) return null;
                if (liveWs || liveWsConnecting) return liveWs;
                if (!getLiveTranscriptionSetting()) return null;

                var baseUrl = getBackendBaseUrl();
                if (!baseUrl) {
                    console.warn('🎤 [WS-TRANSCRIBE] Missing backend URL for websocket init');
                    return null;
                }

                liveWsConnecting = true;
                try {
                    const resp = await fetch(baseUrl + '/corti/get-websocket-url/', {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' }
                    });
                    if (!resp.ok) throw new Error('HTTP ' + resp.status + ': ' + resp.statusText);
                    const payload = await resp.json();
                    if (!payload || payload.status !== 'success' || !payload.websocket_url) {
                        throw new Error((payload && payload.error) ? payload.error : 'Invalid websocket URL payload');
                    }

                    liveWs = new WebSocket(payload.websocket_url);
                    liveWs.binaryType = 'arraybuffer';
                    liveWsConfigAccepted = false;

                    liveWs.onopen = () => {
                        const config = {
                            type: 'config',
                            configuration: {
                                transcription: {
                                    primaryLanguage: 'en',
                                    isDiarization: false,
                                    isMultichannel: false,
                                    participants: [{ channel: 0, role: 'multiple' }],
                                    partialResultStabilityLevel: 0,
                                    interimResults: true,
                                },
                                mode: { type: 'transcription', outputLocale: 'en' },
                            },
                        };
                        liveWs.send(JSON.stringify(config));
                        console.log('🎤 [WS-TRANSCRIBE] WebSocket opened, config sent');
                    };

                    liveWs.onmessage = (event) => {
                        let msg = null;
                        try {
                            msg = JSON.parse(event.data);
                        } catch (_) {
                            console.log('🎤 [WS-TRANSCRIBE] Non-JSON WS frame received, ignoring');
                            return;
                        }
                        if (!msg) return;

                        var rawType = '';
                        if (msg.type != null && msg.type !== '') rawType = String(msg.type);
                        else if (msg.event != null && msg.event !== '') rawType = String(msg.event);
                        var typeUp = rawType.toUpperCase().replace(/\\s/g, '_');

                        if (typeUp === 'CONFIG_ACCEPTED') {
                            liveWsConfigAccepted = true;
                            console.log('🎤 [WS-TRANSCRIBE] CONFIG_ACCEPTED');
                            clearLiveWsFlushTimer();
                            lastLiveWsSentChunkIndex = 0;
                            flushMediaRecorderChunksToLiveWs('after-config');
                            return;
                        }

                        if (typeUp === 'CONFIG_DENIED' || typeUp === 'CONFIG_TIMEOUT' || typeUp === 'ERROR') {
                            try {
                                console.warn('🎤 [WS-TRANSCRIBE] WS config/runtime error:', JSON.stringify(msg));
                            } catch (_) {
                                console.warn('🎤 [WS-TRANSCRIBE] WS config/runtime error:', msg);
                            }
                            return;
                        }

                        if (typeUp === 'ENDED') {
                            console.log('🎤 [WS-TRANSCRIBE] Server ended transcription session');
                            if (liveWsCloseTimer) {
                                clearTimeout(liveWsCloseTimer);
                                liveWsCloseTimer = null;
                            }
                            liveWsCloseTimer = setTimeout(() => {
                                try {
                                    if (liveWs && liveWs.readyState === WebSocket.OPEN) {
                                        liveWs.close(1000, 'Server ended transcription session');
                                    }
                                } catch (_) { /* ignore */ }
                            }, 300);
                            return;
                        }

                        var isTranscriptPayload =
                            typeUp === 'TRANSCRIPT' ||
                            typeUp.indexOf('TRANSCRIPT') !== -1 ||
                            typeUp === 'PARTIAL' ||
                            typeUp.indexOf('PARTIAL') !== -1;

                        if (isTranscriptPayload) {
                            const candidates = [];
                            if (Array.isArray(msg.data)) candidates.push(...msg.data);
                            if (Array.isArray(msg.transcript)) candidates.push(...msg.transcript);
                            if (Array.isArray(msg.results)) candidates.push(...msg.results);
                            if (Array.isArray(msg.segments)) candidates.push(...msg.segments);
                            if (msg.payload && Array.isArray(msg.payload.segments)) candidates.push(...msg.payload.segments);

                            let segment = candidates
                                .map((item) => {
                                    if (!item) return '';
                                    if (typeof item === 'string') return item;
                                    return item.transcript || item.text || item.value || item.word || '';
                                })
                                .join(' ')
                                .trim();

                            if (!segment && typeof msg.text === 'string') {
                                segment = msg.text.trim();
                            }
                            if (!segment && typeof msg.transcript === 'string') {
                                segment = msg.transcript.trim();
                            }
                            if (!segment && msg.payload && typeof msg.payload.text === 'string') {
                                segment = msg.payload.text.trim();
                            }

                            if (!segment) {
                                try {
                                    console.log('🎤 [WS-TRANSCRIBE] Transcript-shaped message (no text):', JSON.stringify(msg).slice(0, 500));
                                } catch (_) {
                                    console.log('🎤 [WS-TRANSCRIBE] Transcript-shaped message (no text):', rawType);
                                }
                                return;
                            }
                            transcriptText = (transcriptText ? transcriptText + ' ' + segment : segment).trim();
                            micTranscriptText = transcriptText;
                            var segTrim = segment.trim();
                            if (segTrim) {
                                micTranscriptDisplayLines.push(formatRecordingTimestamp() + ' ' + segTrim);
                            }
                            micLiveInterim = '';
                            setLiveTranscriptionOutput((buildMicLiveDisplayMultiline() || '').trim() || transcriptText);
                            console.log('🎤 [WS-TRANSCRIBE] Transcript segment received:', segment.slice(0, 80));
                            return;
                        }

                        try {
                            var dbg = JSON.stringify(msg);
                            if (dbg.length > 800) dbg = dbg.slice(0, 800) + '…';
                            console.log('🎤 [WS-TRANSCRIBE] WS message:', typeUp || 'unknown', dbg);
                        } catch (_) {
                            console.log('🎤 [WS-TRANSCRIBE] WS message:', typeUp || 'unknown');
                        }
                    };

                    liveWs.onerror = (err) => {
                        console.warn('🎤 [WS-TRANSCRIBE] WebSocket error', err);
                    };

                    liveWs.onclose = (event) => {
                        console.log('🎤 [WS-TRANSCRIBE] WebSocket closed', event.code, event.reason);
                        if (liveWsCloseTimer) {
                            clearTimeout(liveWsCloseTimer);
                            liveWsCloseTimer = null;
                        }
                        clearLiveWsFlushTimer();
                        lastLiveWsSentChunkIndex = 0;
                        liveWsFlushChain = Promise.resolve();
                        liveWs = null;
                        liveWsConfigAccepted = false;
                    };
                } catch (err) {
                    console.warn('🎤 [WS-TRANSCRIBE] WebSocket init failed:', err.message || err);
                    liveWs = null;
                    liveWsConfigAccepted = false;
                } finally {
                    liveWsConnecting = false;
                }
                return liveWs;
            }

            function closeLiveTranscriptionWebSocket() {
                if (USE_BROWSER_ONLY_LIVE_TRANSCRIPTION) return;
                try {
                    clearLiveWsFlushTimer();
                    if (liveWsCloseTimer) {
                        clearTimeout(liveWsCloseTimer);
                        liveWsCloseTimer = null;
                    }
                    const finalize = () => {
                        try {
                            if (liveWs && liveWs.readyState === WebSocket.OPEN) {
                                liveWs.send(JSON.stringify({ type: 'end' }));
                                console.log('🎤 [WS-TRANSCRIBE] Sent end frame, waiting for final transcript frames');
                                liveWsCloseTimer = setTimeout(() => {
                                    try {
                                        if (liveWs && liveWs.readyState === WebSocket.OPEN) {
                                            liveWs.close(1000, 'Recording ended');
                                        }
                                    } catch (_) { /* ignore */ }
                                }, 2000);
                            }
                        } catch (_) { /* ignore */ }
                    };
                    if (liveWs && liveWs.readyState === WebSocket.OPEN) {
                        flushMediaRecorderChunksToLiveWs('final').finally(finalize);
                    } else {
                        finalize();
                    }
                } catch (_) { /* ignore */ }
            }

            function startLiveTranscriptionIfEnabled() {
                const enabled = getLiveTranscriptionSetting();
                if (!enabled) {
                    setLiveTranscriptionStatus('Disabled');
                    return;
                }
                if (!USE_BROWSER_ONLY_LIVE_TRANSCRIPTION) {
                    // Optional path: popup-owned WebSocket transcription.
                    ensureLiveTranscriptionWebSocket();
                } else {
                    console.log('🎤 [TRANSCRIPT] Browser-only mode active (Corti WebSocket disabled)');
                }
                if (USE_BROWSER_ONLY_LIVE_TRANSCRIPTION && includeSystemAudioForCurrentRecording) {
                    setLiveTranscriptionStatus('Listening (local)');
                    console.log('🎤 [LOCAL-VOSK] Using local Vosk chunk transcription for system audio');
                }
                const rec = ensureSpeechRecognition();
                if (!rec) return;
                // Only reset transcriptText if it's empty or undefined (new recording start)
                // Don't reset when resuming - preserve existing transcription from before pause
                if (!transcriptText || !transcriptText.trim()) {
                    transcriptText = '';
                    micTranscriptText = '';
                    systemAudioTranscriptText = '';
                    resetStructuredTranscriptLines();
                }
                if (!isRecognitionActive) {
                    try {
                        rec.start();
                    } catch (err) {
                        console.warn('🎤 [TRANSCRIPT] Could not start:', err.message);
                        setLiveTranscriptionStatus('Error');
                    }
                }
                if (USE_CLIENT_SIDE_SYSTEM_STT && includeSystemAudioForCurrentRecording && systemAudioSttStreamRef && getLiveTranscriptionSetting()) {
                    void startSystemAudioSttTap(systemAudioSttStreamRef);
                }
            }

            function stopLiveTranscription() {
                stopSystemAudioSttTap();
                if (recognition && isRecognitionActive) {
                    try { recognition.stop(); } catch (e) {}
                }
                isRecognitionActive = false;
                recognition = null;
                closeLiveTranscriptionWebSocket();
            }

            function setLiveTranscriptionStatus(text) {
                const node = document.getElementById('liveTranscriptionStatus');
                if (node) node.textContent = text;
            }

            function setLiveTranscriptionOutput(text) {
                const node = document.getElementById('liveTranscriptionOutput');
                if (node) node.textContent = text || 'Enable the checkbox to view live transcription.';
                const structuredSystem = (buildSystemLiveDisplayMultiline() || '').trim();
                const systemPlain = (systemAudioTranscriptText || '').trim();
                const systemFallbackPlain = systemPlain || 'No system audio transcript yet.';
                const systemNode = document.getElementById('systemAudioTranscriptionOutput');
                const systemShown = structuredSystem || systemFallbackPlain;
                if (systemNode) {
                    systemNode.textContent = systemShown;
                    if (systemShown.indexOf('System-audio local transcription unavailable') === 0) {
                        systemNode.style.color = '#c53030';
                    } else {
                        systemNode.style.color = '';
                    }
                }
                const normalizedText = (text || '').trim();
                const normalizedSystemText = systemPlain;
                const hasMicContent =
                    normalizedText &&
                    normalizedText !== 'Enable the checkbox to view live transcription.' &&
                    normalizedText !== 'Listening...';
                const hasMeaningfulTranscript =
                    hasMicContent || !!normalizedSystemText || !!structuredSystem;
                if (hasMeaningfulTranscript) {
                    latestLiveTranscriptText = [normalizedText, normalizedSystemText].filter(Boolean).join(' ').trim();
                }
                const tabNode = document.getElementById('liveTranscriptTabContent');
                if (tabNode && hasMeaningfulTranscript) {
                    tabNode.textContent = text;
                    tabNode.style.color = '#1a202c';
                }
                const tabSystemNode = document.getElementById('liveTranscriptSystemAudioContent');
                if (tabSystemNode) {
                    tabSystemNode.textContent = systemShown;
                    if (systemShown.indexOf('System-audio local transcription unavailable') === 0) {
                        tabSystemNode.style.color = '#c53030';
                    } else {
                        tabSystemNode.style.color = '';
                    }
                }
                const tabStatus = document.getElementById('liveTranscriptTabStatus');
                if (tabStatus && text) {
                    const hasLiveMic = normalizedText && normalizedText !== 'Listening...' && normalizedText !== 'Enable the checkbox to view live transcription.';
                    const hasLiveSystem = !!(normalizedSystemText || structuredSystem);
                    tabStatus.textContent = (hasLiveMic || hasLiveSystem) ? 'Live' : 'Idle';
                    tabStatus.style.color = (tabStatus.textContent === 'Live') ? '#38a169' : '#4a5568';
                }
                if (hasMeaningfulTranscript) {
                    try {
                        if (window.opener) {
                            // text = display version (mic lines); saveText = the same merged
                            // transcript persisted on stop/pause/backup, so the parent's
                            // periodic backup never writes a different format.
                            window.opener.postMessage({
                                type: 'LIVE_TRANSCRIPT_UPDATE',
                                text: text,
                                saveText: (buildLiveTranscriptTextForSave() || '').trim(),
                                sessionId: recordingSessionId,
                                patientId: patientId,
                                timestamp: new Date().toISOString()
                            }, '*');
                            console.log('[ExternalRecording] postMessage sent to opener, length:', text.length);
                        } else {
                            console.warn('[ExternalRecording] window.opener is null — postMessage skipped');
                        }
                    } catch(e) { console.error('[ExternalRecording] postMessage error:', e); }
                    try {
                        localStorage.setItem('liveTranscriptUpdate', JSON.stringify({ text: text, timestamp: Date.now() }));
                        console.log('[ExternalRecording] localStorage set, length:', text.length);
                    } catch(e) { console.error('[ExternalRecording] localStorage error:', e); }
                }
                updateLiveSystemMergedPanels();
            }

            function showAiRecapGenerating() {
                const section = document.getElementById('aiRecapSection');
                const spinner = document.getElementById('aiRecapSpinner');
                const generating = document.getElementById('aiRecapGenerating');
                const content = document.getElementById('aiRecapContent');
                const error = document.getElementById('aiRecapError');
                if (!section) return;
                section.style.display = 'block';
                if (spinner) spinner.style.display = 'inline-block';
                if (generating) generating.style.display = 'block';
                if (content) content.style.display = 'none';
                if (error) error.style.display = 'none';
                switchTab('liveTranscript');
            }

            function showAiRecap(recap) {
                const section = document.getElementById('aiRecapSection');
                const spinner = document.getElementById('aiRecapSpinner');
                const generating = document.getElementById('aiRecapGenerating');
                const content = document.getElementById('aiRecapContent');
                const error = document.getElementById('aiRecapError');
                if (!section || !recap) return;
                section.style.display = 'block';
                if (spinner) spinner.style.display = 'none';
                if (generating) generating.style.display = 'none';
                if (error) error.style.display = 'none';
                if (content) content.style.display = 'block';

                const summary = document.getElementById('aiRecapSummary');
                if (summary) summary.textContent = recap.summary || '';

                const takeawaysList = document.getElementById('aiRecapTakeaways');
                const takeawaysBox = document.getElementById('aiRecapTakeawaysBox');
                if (takeawaysList && takeawaysBox) {
                    if (recap.key_takeaways && recap.key_takeaways.length > 0) {
                        takeawaysList.innerHTML = recap.key_takeaways.map(item => '<li>' + escapeHtmlClinical(formatAiRecapListItem(item)) + '</li>').join('');
                        takeawaysBox.style.display = 'block';
                    } else {
                        takeawaysBox.style.display = 'none';
                    }
                }

                const actionsList = document.getElementById('aiRecapActions');
                const actionsBox = document.getElementById('aiRecapActionsBox');
                if (actionsList && actionsBox) {
                    if (recap.action_items && recap.action_items.length > 0) {
                        actionsList.innerHTML = recap.action_items.map(item => '<li>' + escapeHtmlClinical(formatAiRecapListItem(item)) + '</li>').join('');
                        actionsBox.style.display = 'block';
                    } else {
                        actionsBox.style.display = 'none';
                    }
                }
                switchTab('liveTranscript');
            }

            function showAiRecapError(msg) {
                const section = document.getElementById('aiRecapSection');
                const spinner = document.getElementById('aiRecapSpinner');
                const generating = document.getElementById('aiRecapGenerating');
                const content = document.getElementById('aiRecapContent');
                const error = document.getElementById('aiRecapError');
                if (!section) return;
                section.style.display = 'block';
                if (spinner) spinner.style.display = 'none';
                if (generating) generating.style.display = 'none';
                if (content) content.style.display = 'none';
                if (error) { error.style.display = 'block'; error.textContent = '❌ ' + msg; }
            }

            function handleLiveTranscriptionToggle() {
                const enabled = getLiveTranscriptionSetting();
                if (enabled && isRecording) {
                    startLiveTranscriptionIfEnabled();
                } else {
                    stopLiveTranscription();
                    setLiveTranscriptionStatus(enabled ? 'Idle' : 'Disabled');
                }
            }
            
            function getLiveTranscriptionSetting() {
                return document.getElementById('liveTranscriptionCheckbox')?.checked || false;
            }

            function getAiRecapSetting() {
                return document.getElementById('aiRecapCheckbox')?.checked || false;
            }
            
            function startTimer() {
                timerInterval = setInterval(() => {
                    if (timer.seconds >= 59) {
                        timer.minutes++;
                        timer.seconds = 0;
                    } else {
                        timer.seconds++;
                    }
                    updateTimerDisplay();
                }, 1000);
            }
            
            function stopTimer() {
                if (timerInterval) {
                    clearInterval(timerInterval);
                    timerInterval = null;
                }
            }
            
            function resetTimer() {
                stopTimer();
                stopBackupTimer(); // Also reset backup timer
                timer = { minutes: 0, seconds: 0 };
                totalRecordedBytes = 0;
                autoSaveTriggered = false;
                const banner = document.getElementById('sizeWarningBanner');
                if (banner) banner.classList.add('hidden');
                updateTimerDisplay();
                updateSizeDisplay();
            }
            
            function formatRecorderByteLabel(bytes) {
                var gb = 1024 * 1024 * 1024;
                var mb = 1024 * 1024;
                if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
                if (bytes >= gb) return (bytes / gb).toFixed(2) + ' GB';
                if (bytes >= mb) return (bytes / mb).toFixed(1) + ' MB';
                if (bytes >= 1024) return (bytes / 1024).toFixed(0) + ' KB';
                return String(bytes) + ' B';
            }

            function updateTimerDisplay() {
                const mins = String(timer.minutes).padStart(2, '0');
                const secs = String(timer.seconds).padStart(2, '0');
                document.getElementById('timer').textContent = mins + ':' + secs;
            }

            function updateSizeDisplay() {
                const tracker = document.getElementById('sizeTracker');
                const banner = document.getElementById('sizeWarningBanner');
                if (!tracker) return;
                tracker.textContent =
                    formatRecorderByteLabel(totalRecordedBytes) +
                    ' / ' +
                    formatRecorderByteLabel(MAX_UPLOAD_BYTES);
                tracker.classList.remove('warning', 'danger');
                if (totalRecordedBytes > MAX_UPLOAD_BYTES - SIZE_DANGER_NEAR_BYTES) {
                    tracker.classList.add('danger');
                } else if (totalRecordedBytes > MAX_UPLOAD_BYTES - SIZE_WARN_NEAR_BYTES) {
                    tracker.classList.add('warning');
                }
                if (banner) {
                    if (
                        !autoSaveTriggered &&
                        totalRecordedBytes > MAX_UPLOAD_BYTES - SIZE_WARN_NEAR_BYTES &&
                        isRecording
                    ) {
                        banner.classList.remove('hidden');
                    } else {
                        banner.classList.add('hidden');
                    }
                }
                if (
                    MAX_UPLOAD_BYTES > 0 &&
                    !autoSaveTriggered &&
                    AUTO_SAVE_BYTES > 0 &&
                    totalRecordedBytes >= AUTO_SAVE_BYTES &&
                    isRecording
                ) {
                    autoSaveTriggered = true;
                    if (banner) banner.classList.add('hidden');
                    console.log('⚠️ [AUTO-SAVE] Size limit approaching — save & continue recording');
                    saveAndContinueRecording();
                }
            }
            
            async function startRecording() {
                console.log('🎤 [DEBUG] startRecording() called');
                try {
                    showLoading();
                    // Fresh recording => always clear prior transcript/AI state.
                    resetStructuredTranscriptLines();
                    transcriptText = '';
                    micTranscriptText = '';
                    systemAudioTranscriptText = '';
                    lastCapturedTranscript = '';
                    latestLiveTranscriptText = '';
                    liveClinicalAiLastTime = 0;
                    lastLiveClinicalAiTranscript = '';
                    liveClinicalAiLastFingerprint = '';
                    setLiveTranscriptionOutput('');
                    const startTabNode = document.getElementById('liveTranscriptTabContent');
                    if (startTabNode) {
                        startTabNode.textContent = 'Listening...';
                        startTabNode.style.color = '#1a202c';
                    }
                    const startSystemNode = document.getElementById('liveTranscriptSystemAudioContent');
                    if (startSystemNode) {
                        startSystemNode.textContent = 'No system audio transcript yet.';
                    }
                    const startTabStatus = document.getElementById('liveTranscriptTabStatus');
                    if (startTabStatus) {
                        startTabStatus.textContent = 'Idle';
                        startTabStatus.style.color = '#4a5568';
                    }
                    setAiTabsStatus('AI: idle', '#4a5568');
                    clearClinicalTabPanels('Content appears here after transcription completes.');
                    aggregateCleanup = null;
                    systemAudioRecorder = null;
                    systemAudioChunkIndex = 0;
                    stopSystemAudioSttTap();
                    systemAudioSttStreamRef = null;
                    liveTranscriptAutoSaved = false;
                    // Every new recording attempt in the same popup must get a fresh session id.
                    // Otherwise transcript dedupe (by session_id) can skip valid saves.
                    recordingSessionId = generateSessionId();
                    // Fresh session: periodic backup numbering and any stray timer reset.
                    backupCount = 0;
                    stopBackupTimer();
                    lastLiveWsSentChunkIndex = 0;
                    liveWsFlushChain = Promise.resolve();
                    localWhisperFlushChain = Promise.resolve();
                    console.log('🎤 [DEBUG] New recording session id:', recordingSessionId);
                    const includeSystemAudio = document.getElementById('includeSystemAudioCheckbox') && document.getElementById('includeSystemAudioCheckbox').checked;
                    includeSystemAudioForCurrentRecording = !!includeSystemAudio;
                    console.log('🎤 [DEBUG] Include system/meeting audio:', includeSystemAudio);
                    console.log('🎤 [DEBUG] Requesting microphone access...');
                    
                    // Same constraints as Test Microphone and the "Mic:" preview
                    const audioConstraints = buildMicConstraints(includeSystemAudio);

                    stream = null;
                    let systemAudioSourceStream = null;
                    let micInUseLabel = '';
                    if (includeSystemAudio) {
                        try {
                            const micStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
                            micInUseLabel = micStream.getAudioTracks()[0]?.label || '';
                            const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
                            
                            // FIX: Capture and send the display surface info to parent window for UI display
                            // This provides the actual URL/tab being shared instead of showing "about:blank"
                            let displayInfo = null;
                            const videoTracks = displayStream.getVideoTracks();
                            if (videoTracks && videoTracks.length > 0) {
                                const videoTrack = videoTracks[0];
                                // Get the label (e.g., "Tab 1", "Window 1", or the actual URL)
                                // The track.label contains the display surface identifier
                                displayInfo = {
                                    label: videoTrack.label,
                                    // The displaySurface property tells us what type of content is being shared
                                    // Values: "monitor", "window", "browser", "application", "tab", or undefined
                                    displaySurface: videoTrack.getSettings?.().displaySurface || null,
                                    // Attempt to get the shared URL from the label (works for tabs)
                                    sharedUrl: videoTrack.label || null
                                };
                                console.log('🎤 [DEBUG] Display surface info captured:', displayInfo);
                            }
                            
                            // Send display info to parent window so it can show "Shared to: [tab name]" in UI
                            if (window.opener && displayInfo) {
                                window.opener.postMessage({
                                    type: 'SHARE_INFO',
                                    displayInfo: displayInfo,
                                    patientId: patientId,
                                    patientName: patientName
                                }, '*');
                                console.log('🎤 [DEBUG] SHARE_INFO sent to parent window:', displayInfo);
                            }
                            
                            if (!displayStream.getAudioTracks().length) {
                                displayStream.getTracks().forEach(t => t.stop());
                                micStream.getTracks().forEach(t => t.stop());
                                throw { code: 'TAB_AUDIO_REQUIRED' };
                            }
                            // Dedicated stream for system-audio transcription only (no mic mixed in).
                            systemAudioSourceStream = new MediaStream(displayStream.getAudioTracks());
                            const mixContext = new (window.AudioContext || window.webkitAudioContext)();
                            const destination = mixContext.createMediaStreamDestination();
                            const micSource = mixContext.createMediaStreamSource(micStream);
                            let displaySource;
                            try {
                                displaySource = mixContext.createMediaStreamSource(displayStream);
                            } catch (sourceErr) {
                                displayStream.getTracks().forEach(t => t.stop());
                                micStream.getTracks().forEach(t => t.stop());
                                mixContext.close();
                                if (sourceErr.name === 'InvalidStateError' || (sourceErr.message && sourceErr.message.includes('no audio track'))) {
                                    throw { code: 'TAB_AUDIO_REQUIRED' };
                                }
                                throw sourceErr;
                            }
                            micSource.connect(destination);
                            displaySource.connect(destination);
                            stream = destination.stream;
                            aggregateCleanup = () => {
                                displayStream.getTracks().forEach(t => t.stop());
                                micStream.getTracks().forEach(t => t.stop());
                                mixContext.close();
                            };
                            console.log('🎤 [DEBUG] Aggregate stream (mic + system/tab audio) created');
                            systemAudioSttStreamRef = systemAudioSourceStream;
                            // Show system audio status indicator
                            const systemAudioStatus = document.getElementById('systemAudioStatus');
                            if (systemAudioStatus) {
                                systemAudioStatus.textContent = 'System audio: ON';
                                systemAudioStatus.classList.remove('hidden');
                                systemAudioStatus.classList.add('active');
                            }
                        } catch (displayErr) {
                            const isTabAudioRequired = displayErr && (displayErr.code === 'TAB_AUDIO_REQUIRED' || (displayErr.message && displayErr.message.includes('TAB_AUDIO_REQUIRED')));
                            const isNoAudioTrack = displayErr && (displayErr.name === 'InvalidStateError' || (displayErr.message && displayErr.message.includes('no audio track')));
                            if (displayErr.name === 'NotAllowedError') {
                                showError('Tab/screen share was cancelled. Recording with microphone only.');
                                systemAudioSttStreamRef = null;
                                stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
                            } else if (isTabAudioRequired || isNoAudioTrack) {
                                hideLoading();
                                showError('No tab audio was shared. Check "Also share tab audio" in the share dialog so we can capture meeting audio. Recording did not start. Click Start Recording again and turn on "Also share tab audio" when prompted.');
                                updateUI('error');
                                return;
                            } else {
                                throw displayErr;
                            }
                        }
                    } else {
                        systemAudioSttStreamRef = null;
                        stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
                    }
                    
                    console.log('🎤 [DEBUG] Microphone access granted, stream:', stream);
                    // Show the mic actually recording (with system audio, stream is the mix)
                    setMicInUseStatus(micInUseLabel || stream.getAudioTracks()[0]?.label || '');
                    
                    // Initialize audio visualizer
                    initializeAudioBars();
                    
                    // Start audio level monitoring
                    startAudioLevelMonitoring(stream);
                    
                    if (!window.MediaRecorder) {
                        throw new Error('MediaRecorder API not supported in this browser');
                    }
                    
                    // Safari-specific audio format detection
                    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    
                    // Determine the best supported MIME type for the browser
                    let mimeType = 'audio/webm;codecs=opus';
                    
                    if (isSafari) {
                        // Safari prefers MP4/M4A format
                        if (MediaRecorder.isTypeSupported('audio/mp4')) {
                            mimeType = 'audio/mp4';
                        } else if (MediaRecorder.isTypeSupported('audio/mp4a-latm')) {
                            mimeType = 'audio/mp4a-latm';
                        } else if (MediaRecorder.isTypeSupported('audio/aac')) {
                            mimeType = 'audio/aac';
                        }
                        console.log('🎤 [DEBUG] Safari detected, using MIME type:', mimeType);
                    } else {
                        // For other browsers, try webm first, then fallback
                        if (!MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                            if (MediaRecorder.isTypeSupported('audio/webm')) {
                                mimeType = 'audio/webm';
                            } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
                                mimeType = 'audio/ogg;codecs=opus';
                            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                                mimeType = 'audio/mp4';
                            }
                        }
                        console.log('🎤 [DEBUG] Using MIME type:', mimeType);
                    }
                    
                    // Store the detected MIME type for later use (Safari compatibility)
                    currentMimeType = mimeType;
                    console.log('🎤 [DEBUG] Stored MIME type:', currentMimeType);
                    
                    mediaRecorder = new MediaRecorder(stream, {
                        mimeType: mimeType
                    });
                    audioChunks = [];
                    recordedAudioBlob = null;
                    hasFinalSaveBeenSent = false; // Reset flag for new recording
                    
                    console.log('🎤 [DEBUG] MediaRecorder created:', mediaRecorder);
                    if (includeSystemAudio && systemAudioSourceStream && systemAudioSourceStream.getAudioTracks().length > 0) {
                        try {
                            systemAudioRecorder = new MediaRecorder(systemAudioSourceStream, { mimeType: mimeType });
                        } catch (_) {
                            systemAudioRecorder = new MediaRecorder(systemAudioSourceStream);
                        }
                        systemAudioRecorder.ondataavailable = (event) => {
                            if (event.data && event.data.size > 0) {
                                // MediaRecorder WebM chunks are not reliably decodable with decodeAudioData; system STT uses PCM tap instead.
                                if (!USE_CLIENT_SIDE_SYSTEM_STT) {
                                    enqueueLocalWhisperChunk(event.data, systemAudioChunkIndex);
                                }
                                systemAudioChunkIndex += 1;
                            }
                        };
                        systemAudioRecorder.onerror = (event) => {
                            console.warn('🎤 [LOCAL-WHISPER] system-audio recorder error:', event && event.error ? event.error : event);
                        };
                        console.log('🎤 [LOCAL-WHISPER] System-audio-only recorder created');
                    }
                    
                    mediaRecorder.ondataavailable = (event) => {
                        console.log('🎤 [DEBUG] Audio chunk received:', {
                            size: event.data.size,
                            type: event.data.type,
                            chunks: audioChunks.length
                        });
                        
                        if (event.data.size > 0) {
                            audioChunks.push(event.data);
                            totalRecordedBytes += event.data.size;
                            updateSizeDisplay();
                            console.log('🎤 [DEBUG] Audio chunk added. Total chunks:', audioChunks.length);
                            flushMediaRecorderChunksToLiveWs('chunk');
                            if (!includeSystemAudioForCurrentRecording) {
                                enqueueLocalWhisperChunk(event.data, audioChunks.length - 1);
                            }
                        } else {
                            console.log('🎤 [DEBUG] Empty audio chunk received, skipping');
                        }
                    };
                    
                    mediaRecorder.onstop = () => {
                        console.log('🎤 [DEBUG] MediaRecorder stopped. Processing chunks:', audioChunks.length);
                        stopSystemAudioSttTap();
                        systemAudioSttStreamRef = null;
                        if (systemAudioRecorder && systemAudioRecorder.state !== 'inactive') {
                            try { systemAudioRecorder.stop(); } catch (_) {}
                        }
                        systemAudioRecorder = null;
                        
                        // Release aggregate (system/meeting) audio tracks and mix context if used
                        if (aggregateCleanup) {
                            try { aggregateCleanup(); } catch (e) { console.warn('aggregateCleanup error', e); }
                            aggregateCleanup = null;
                        }
                        includeSystemAudioForCurrentRecording = false;
                        
                        // Hide system audio status indicator
                        const systemAudioStatus = document.getElementById('systemAudioStatus');
                        if (systemAudioStatus) {
                            systemAudioStatus.classList.add('hidden');
                            systemAudioStatus.classList.remove('active');
                        }
                        
                        // Stop audio level monitoring
                        stopAudioLevelMonitoring();
                        
                        try {
                            if (audioChunks.length === 0) {
                                throw new Error('No audio chunks were recorded');
                            }
                            
                            // Use the detected MIME type for blob creation (Safari compatibility)
                            recordedAudioBlob = new Blob(audioChunks, {
                                type: currentMimeType
                            });
                            
                            console.log('🎤 [DEBUG] Audio blob created successfully:', {
                                size: recordedAudioBlob.size,
                                type: recordedAudioBlob.type
                            });
                            
                            if (recordedAudioBlob.size === 0) {
                                throw new Error('Generated audio blob is empty');
                            }
                            
                            // Don't show "Recording complete" message or change UI here
                            // The stopAndSaveRecording function will handle the UI and save
                            // Only log that blob is ready - don't change UI
                            const durationText = timer.minutes + ':' + String(timer.seconds).padStart(2, '0');
                            console.log('🎤 [DEBUG] Recording blob ready. Duration: ' + durationText);
                            // UI will be updated by stopAndSaveRecording function
                        } catch (error) {
                            console.error('🎤 [ERROR] Error processing audio chunks:', error);
                            showError('Recording processing error: ' + error.message);
                            updateUI('error');
                            // Reset flag on error so user can retry
                            hasFinalSaveBeenSent = false;
                        }
                    };
                    
                    mediaRecorder.onerror = (event) => {
                        console.error('🎤 [ERROR] MediaRecorder error:', event.error);
                        showError('Recording error: ' + event.error.message);
                        updateUI('error');
                    };
                    
                    console.log('🎤 [DEBUG] Starting MediaRecorder...');
                    mediaRecorder.start(RECORDING_TIMESLICE_MS);
                    if (systemAudioRecorder) {
                        systemAudioRecorder.start(RECORDING_TIMESLICE_MS);
                    }
                    
                    setTimeout(() => {
                        if (mediaRecorder.state !== 'recording') {
                            console.error('🎤 [ERROR] MediaRecorder failed to start. State:', mediaRecorder.state);
                            showError('Failed to start recording. Please try again.');
                            updateUI('error');
                        } else {
                            console.log('🎤 [DEBUG] MediaRecorder started successfully. State:', mediaRecorder.state);
                        }
                    }, 100);
                    
                    isRecording = true;
                    isPaused = false;
                    externalRecordingState = 'record';
                    liveClinicalAiLastFingerprint = '';
                    clearLiveClinicalDebounceTimer();
                    startTimer();

                    startBackupTimer(); // Start periodic backups
                    hideLoading();
                    updateUI('record');
                    setIsSaving(false); // Re-enable button for new recording
                    startLiveTranscriptionIfEnabled();

                    // Parent backup: send after UI locks live-transcription on so flags match transcript behavior
                    if (window.opener) {
                        const startData = {
                            type: 'RECORDING_STARTED',
                            patientId: patientId,
                            patientName: patientName,
                            ehrId: injectedEhrId,
                            sessionId: recordingSessionId,
                            timestamp: new Date().toISOString(),
                            scheduledDate: scheduledDate,
                            audioNumber: audioNumber,
                            liveTranscription: getLiveTranscriptionSetting(),
                        };
                        window.opener.postMessage(startData, '*');
                        console.log('💾 [EXTERNAL] RECORDING_STARTED sent to parent window');
                    }
                    
                } catch (error) {
                    console.error('🎤 [ERROR] Error starting recording:', error);
                    hideLoading();
                    
                    let errorMessage = 'Failed to access microphone. ';
                    if (error.name === 'NotAllowedError') {
                        errorMessage += 'Please allow microphone permissions in your browser.';
                    } else if (error.name === 'NotFoundError') {
                        errorMessage += 'No microphone found. Please connect a microphone and try again.';
                    } else if (error.name === 'NotReadableError') {
                        errorMessage += 'Microphone is being used by another application.';
                    } else {
                        errorMessage += 'Error: ' + error.message;
                    }
                    
                    showError(errorMessage);
                    updateUI('error');
                }
            }
            
            function pauseRecording() {
                if (mediaRecorder && isRecording) {
                    mediaRecorder.pause();
                    if (systemAudioRecorder && systemAudioRecorder.state === 'recording') {
                        try { systemAudioRecorder.pause(); } catch (_) {}
                    }
                    isPaused = true;
                    externalRecordingState = 'paused';
                    stopTimer();
                    stopBackupTimer(); // Pause backups during recording pause
                    stopLiveTranscription();
                    updateUI('paused');

                    clearLiveClinicalDebounceTimer();

                    // Send RECORDING_PAUSED message to parent window to save transcription backup
                    if (window.opener) {
                        const pauseData = {
                            type: 'RECORDING_PAUSED',
                            patientId: patientId,
                            patientName: patientName,
                            ehrId: injectedEhrId,
                            scheduledDate: scheduledDate,
                            audioNumber: audioNumber,
                            sessionId: recordingSessionId,
                            timestamp: new Date().toISOString(),
                            transcriptText: ((buildLiveTranscriptTextForSave() || '').trim() || transcriptText || ''),
                            duration: timer.minutes * 60 + timer.seconds,
                        };
                        window.opener.postMessage(pauseData, '*');
                        console.log('💾 [EXTERNAL] RECORDING_PAUSED sent to parent window');
                    }
                }
            }
            
            function resumeRecording() {
                if (mediaRecorder && isRecording && isPaused) {
                    mediaRecorder.resume();
                    if (systemAudioRecorder && systemAudioRecorder.state === 'paused') {
                        try { systemAudioRecorder.resume(); } catch (_) {}
                    }
                    isPaused = false;
                    externalRecordingState = 'record';
                    startTimer();
                    startBackupTimer(); // Resume backups
                    startLiveTranscriptionIfEnabled();
                    updateUI('record');
                    
                    // Request latest transcription backup from parent to restore if needed
                    if (window.opener) {
                        const resumeData = {
                            type: 'RECORDING_RESUMED',
                            patientId: patientId,
                            patientName: patientName,
                            sessionId: recordingSessionId,
                            timestamp: new Date().toISOString(),
                            transcriptText: ((buildLiveTranscriptTextForSave() || '').trim() || transcriptText || ''),
                        };
                        window.opener.postMessage(resumeData, '*');
                        console.log('💾 [EXTERNAL] RECORDING_RESUMED sent to parent window');
                    }
                }
            }
            
            async function saveAndContinueRecording() {
                console.log('🎤 [DEBUG] saveAndContinueRecording() called');
                if (hasFinalSaveBeenSent || !isRecording) return;

                // Hide the warning banner immediately
                const banner = document.getElementById('sizeWarningBanner');
                if (banner) banner.classList.add('hidden');

                // Capture everything we need from the current recording before resetting state
                const oldRecorder = mediaRecorder;
                const oldChunks = audioChunks;
                const oldMimeType = currentMimeType;
                const oldSessionId = recordingSessionId;
                const oldDuration = timer.minutes * 60 + timer.seconds;
                const oldTranscriptPlain = typeof transcriptText !== 'undefined' ? transcriptText.trim() : '';
                const oldTranscriptText =
                    (buildLiveTranscriptTextForSave() || '').trim() || oldTranscriptPlain;
                const oldBackupCount = backupCount;
                console.log('[SAVE-CONTINUE] Captured old state — duration:', oldDuration, 's, transcript length:', oldTranscriptText.length, 'chars, chunks:', oldChunks.length);

                // Start the new recording immediately on the same stream (zero gap)
                // Bug fix 1: stop the running timer/backup-timer intervals before resetting
                stopTimer();
                stopBackupTimer();
                // Bug fix 2: stop live transcription and reset transcript so new recording
                // starts with empty transcript - must stop recognition BEFORE clearing variable
                stopLiveTranscription();
                if (typeof transcriptText !== 'undefined') transcriptText = '';
                micTranscriptText = '';
                systemAudioTranscriptText = '';
                resetStructuredTranscriptLines();
                audioChunks = [];
                totalRecordedBytes = 0;
                autoSaveTriggered = false;
                hasFinalSaveBeenSent = false;
                recordedAudioBlob = null;
                recordingSessionId = generateSessionId();
                backupCount = 0;
                timer = { minutes: 0, seconds: 0 };

                const newRecorder = new MediaRecorder(stream, { mimeType: oldMimeType });
                mediaRecorder = newRecorder;

                newRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                        audioChunks.push(event.data);
                        totalRecordedBytes += event.data.size;
                        updateSizeDisplay();
                        flushMediaRecorderChunksToLiveWs('chunk');
                        if (!includeSystemAudioForCurrentRecording) {
                            enqueueLocalWhisperChunk(event.data, audioChunks.length - 1);
                        }
                    }
                };
                newRecorder.onstop = () => {
                    // Stop audio level monitoring only if really stopping (not save & continue)
                    stopAudioLevelMonitoring();
                    try {
                        if (audioChunks.length === 0) throw new Error('No audio chunks were recorded');
                        recordedAudioBlob = new Blob(audioChunks, { type: currentMimeType });
                        if (recordedAudioBlob.size === 0) throw new Error('Generated audio blob is empty');
                    } catch (error) {
                        console.error('🎤 [ERROR] Error processing audio chunks:', error);
                        showError('Recording processing error: ' + error.message);
                        updateUI('error');
                        hasFinalSaveBeenSent = false;
                    }
                };
                newRecorder.onerror = (event) => {
                    console.error('🎤 [ERROR] MediaRecorder error:', event.error);
                    showError('Recording error: ' + event.error.message);
                    updateUI('error');
                };

                newRecorder.start(RECORDING_TIMESLICE_MS);
                startTimer();
                startBackupTimer();
                updateTimerDisplay();
                updateSizeDisplay();
                updateUI('record');
                showSuccess('Saved! New recording started automatically.');

                // The parent keys its transcript backup on sessionId; without this
                // the new segment's text kept landing in the previous segment's file.
                if (window.opener) {
                    try {
                        window.opener.postMessage({
                            type: 'RECORDING_STARTED',
                            continuation: true,
                            patientId: patientId,
                            patientName: patientName,
                            ehrId: injectedEhrId,
                            sessionId: recordingSessionId,
                            timestamp: new Date().toISOString(),
                            scheduledDate: scheduledDate,
                            audioNumber: audioNumber,
                            liveTranscription: getLiveTranscriptionSetting(),
                        }, '*');
                    } catch (e) { console.warn('[SAVE-CONTINUE] RECORDING_STARTED notify failed:', e); }
                }

                // Now stop the old recorder — override onstop to skip stream cleanup
                // (the stream is still being used by the new recorder)
                // Bug fix 3: redirect old recorder's final ondataavailable to oldChunks so the
                // last WebM segment doesn't contaminate the new recording's audioChunks array
                oldRecorder.ondataavailable = (event) => {
                    if (event.data && event.data.size > 0) {
                        oldChunks.push(event.data);
                        console.log('[SAVE-CONTINUE] Old recorder final chunk captured: ' + event.data.size + ' bytes (total old chunks: ' + oldChunks.length + ')');
                    }
                };
                oldRecorder.onstop = () => {
                    void (async function() {
                        try {
                            console.log('[SAVE-CONTINUE] Old recorder stopped — chunks:', oldChunks.length, 'mimeType:', oldMimeType);
                            if (oldChunks.length === 0) {
                                console.warn('⚠️ [SAVE-CONTINUE] No chunks in old recording, skipping send');
                                return;
                            }
                            const blob = new Blob(oldChunks, { type: oldMimeType });
                            console.log('[SAVE-CONTINUE] Old recording blob size:', blob.size, 'bytes');
                            if (blob.size === 0) return;

                            const segmentResult = await uploadCompletedSegmentAndNotifyParent(blob, {
                                duration: oldDuration,
                                transcriptText: oldTranscriptText,
                                sessionId: oldSessionId,
                                totalBackups: oldBackupCount,
                                timestamp: new Date().toISOString()
                            });
                            if (segmentResult.success) {
                                console.log('🎤 [SAVE-CONTINUE] Old recording segment saved');
                            } else {
                                console.error('🎤 [SAVE-CONTINUE] Segment save failed:', segmentResult.error);
                                showError('Failed to save recording segment: ' + (segmentResult.error || 'Unknown error'));
                            }
                        } catch (err) {
                            console.error('🎤 [SAVE-CONTINUE] Failed to send old recording:', err);
                        }
                    })();
                };
                if (oldRecorder.state === 'recording' || oldRecorder.state === 'paused') {
                    try {
                        if (oldRecorder.state === 'recording' && typeof oldRecorder.requestData === 'function') {
                            oldRecorder.requestData();
                        }
                    } catch (e) { /* ignore */ }
                    oldRecorder.stop();
                }
            }

            async function stopAndSaveRecording() {
                console.log('🎤 [DEBUG] stopAndSaveRecording() called');
                console.log('🎤 [DEBUG] Current patientName:', patientName, ', patientId:', patientId, ', injectedEhrId:', injectedEhrId);
                console.log('🎤 [DEBUG] EXTERNAL_DB_SCHEMA:', EXTERNAL_DB_SCHEMA);
                
                // Prevent duplicate saves - check FIRST before doing anything
                if (hasFinalSaveBeenSent) {
                    console.log('🚫 [DEBUG] Final save already sent, ignoring duplicate call');
                    return;
                }
                
                // Mark as saving immediately to prevent duplicate calls
                hasFinalSaveBeenSent = true;
                
                // Disable button immediately to prevent multiple clicks
                setIsSaving(true);
                
                // Stop timer immediately - don't wait for anything
                stopTimer();
                stopBackupTimer();
                // FIX: Clear transcriptText BEFORE stopping recognition so any pending
                // onresult callbacks from the old recognition write to the discarded string
                const tabNodeAtStop = document.getElementById('liveTranscriptTabContent');
                const tabTranscriptText = tabNodeAtStop ? (tabNodeAtStop.textContent || '').trim() : '';
                const finalTranscript = (transcriptText || '').trim()
                    || (latestLiveTranscriptText || '').trim()
                    || tabTranscriptText;
                const outboundLiveText =
                    (buildLiveTranscriptTextForSave() || '').trim() || finalTranscript.trim();
                lastCapturedTranscript = outboundLiveText.trim() || finalTranscript.trim();
                stopLiveTranscription();
                isRecording = false;
                isPaused = false;
                clearLiveClinicalDebounceTimer();
                bumpLiveClinicalAiGeneration();

                // Send RECORDING_STOPPED message to parent window to end transcription backup session
                if (window.opener) {
                    const stopData = {
                        type: 'RECORDING_STOPPED',
                        patientId: patientId,
                        patientName: patientName,
                        ehrId: injectedEhrId,
                        scheduledDate: scheduledDate,
                        audioNumber: audioNumber,
                        sessionId: recordingSessionId,
                        timestamp: new Date().toISOString(),
                        duration: timer.minutes * 60 + timer.seconds,
                        finalTranscript: outboundLiveText.trim() || finalTranscript.trim(),
                    };
                    window.opener.postMessage(stopData, '*');
                    console.log('💾 [EXTERNAL] RECORDING_STOPPED sent to parent window');
                }
                
                // Always stop the MediaRecorder if it's recording, regardless of save status
                let wasRecording = false;
                if (mediaRecorder && (mediaRecorder.state === 'recording' || mediaRecorder.state === 'paused')) {
                    console.log('🎤 [DEBUG] Stopping MediaRecorder (state: ' + mediaRecorder.state + ')...');
                    wasRecording = true;
                    
                    if (mediaRecorder.state === 'recording' || mediaRecorder.state === 'paused') {
                        try {
                            if (mediaRecorder.state === 'recording' && typeof mediaRecorder.requestData === 'function') {
                                mediaRecorder.requestData();
                            }
                        } catch (e) { /* ignore */ }
                        mediaRecorder.stop();
                        console.log('🎤 [DEBUG] MediaRecorder.stop() called, waiting for onstop event...');
                    }
                }
                
                try {
                    showLoading('Finalizing recording...');
                    
                    if (wasRecording) {
                        await waitForRecordedBlobAfterStop();
                    }
                    
                    console.log('🎤 [DEBUG] Recording finalized, checking for audio data...');
                    
                    if (!recordedAudioBlob) {
                        console.error('🎤 [ERROR] recordedAudioBlob is still null after waiting');
                        throw new Error('No audio data recorded. Please try recording again.');
                    }
                    
                    // Validate blob is not empty before upload
                    if (recordedAudioBlob.size === 0) {
                        console.error('🎤 [ERROR] recordedAudioBlob is empty (0 bytes)');
                        throw new Error('No audio data recorded. The recording may have failed. Please try again.');
                    }
                    
                    console.log('🎤 [DEBUG] Audio blob found, size:', recordedAudioBlob.size, 'bytes');

                    if (recordedAudioBlob.size > MAX_UPLOAD_BYTES && !(EXTERNAL_SUPABASE_SERVICE_KEY && EXTERNAL_AUDIO_BUCKET_NAME)) {
                        throw new Error(
                            'Recording exceeds maximum size (' +
                            '${injectedMaxUploadLabelText}' +
                            '). Configure direct upload, or split with "Save & continue recording".'
                        );
                    }
                    
                    showLoading('Saving audio to patient record...');
                    
                    // Capture timer values before they might change
                    const finalDuration = timer.minutes * 60 + timer.seconds;
                    
                    const audioData = {
                        type: 'AUDIO_RECORDING_COMPLETE',
                        patientId: patientId,
                        patientName: patientName,
                        ehrId: injectedEhrId,
                        audioBlob: recordedAudioBlob,
                        duration: finalDuration,
                        timestamp: new Date().toISOString(),
                        patientScheduledDate: scheduledDate,
                        scheduledDate: scheduledDate,
                        liveTranscription: getLiveTranscriptionSetting(),
                        liveTranscriptText: outboundLiveText,
                        liveFactExtraction: false,
                        generateAiRecap: getAiRecapSetting(),
                        totalBackups: backupCount,
                        sessionId: recordingSessionId
                    };
                    
                    console.log('🎤 [DEBUG] Sending audio data to parent window');
                    
                    // =========================================================
                    // INDEPENDENT SAVE: Try direct upload first, then fallback
                    // =========================================================
                    let directUploadSuccess = false;
                    let finalAudioUrl = null;
                    
                    // Attempt direct upload if we have Supabase credentials
                    if (EXTERNAL_SUPABASE_SERVICE_KEY && EXTERNAL_AUDIO_BUCKET_NAME) {
                        console.log('🎤 [DIRECT-UPLOAD] Attempting direct upload to Supabase...');
                        showLoading('Uploading audio directly to storage...');
                        // Settle the row's schema first: it names the storage
                        // folder below as well as the table the save PATCHes.
                        await ensureRowSchema();

                        // Get the actual next audio index by checking existing files in storage
                        // This MUST succeed - throw error if storage listing fails
                        const year = scheduledDate ? scheduledDate.split('-')[0] : new Date().getFullYear().toString();
                        const date = scheduledDate || new Date().toISOString().split('T')[0];
                        let nextAudioIndex;
                        try {
                            nextAudioIndex = await getNextAudioIndexFromStorage(
                                EXTERNAL_AUDIO_BUCKET_NAME,
                                EXTERNAL_DB_SCHEMA || 'public',
                                year,
                                date,
                                patientName
                            );
                        } catch (indexError) {
                            console.error('🎤 [AUDIO-INDEX] FATAL: Could not determine audio index from storage:', indexError.message);
                            throw new Error('Failed to determine audio file index. Storage listing failed: ' + indexError.message);
                        }
                        
                        console.log('🎤 [DEBUG] Before generateAudioFilename - patientName:', patientName, ', injectedEhrId:', injectedEhrId, ', resolved audioNumber:', nextAudioIndex);
                        const filename = generateAudioFilename(
                            patientName,
                            injectedEhrId,
                            nextAudioIndex,
                            getAudioFileExtensionForBlob(recordedAudioBlob),
                        );
                        console.log('🎤 [DEBUG] generateAudioFilename called with patientName:', patientName, ', ehrId:', injectedEhrId, ', audioNumber:', nextAudioIndex);
                        const metadata = {
                            schemaName: EXTERNAL_DB_SCHEMA || 'public',
                            year: year,
                            date: date,
                            patientId: patientId,
                            patientName: patientName,
                            ehrId: injectedEhrId,
                            duration: finalDuration
                        };
                        
                        // Fast path: the rolling backup already holds the recording up
                        // to the last backup tick, so promote it via server-side copy
                        // (near-instant) and let the save flow complete immediately.
                        // The full blob then upserts over the same file in the
                        // background to restore the final seconds after the last tick.
                        let uploadResult = { success: false };
                        if (backupCount > 0) {
                            uploadResult = await promoteLastBackupToAudioBucket(
                                filename,
                                EXTERNAL_DB_SCHEMA || 'public',
                                year,
                                date
                            );
                            if (uploadResult.success) {
                                console.log('🎤 [DIRECT-UPLOAD] Promoted last backup checkpoint via server-side copy:', uploadResult.url);
                                uploadAudioDirectly(
                                    recordedAudioBlob,
                                    filename,
                                    EXTERNAL_AUDIO_BUCKET_NAME,
                                    null,
                                    EXTERNAL_SUPABASE_SERVICE_KEY,
                                    metadata
                                ).then(function (fullUpload) {
                                    if (fullUpload.success) {
                                        console.log('🎤 [DIRECT-UPLOAD] Background full-blob upsert complete (final seconds restored):', fullUpload.url);
                                    } else {
                                        console.warn('🎤 [DIRECT-UPLOAD] Background full-blob upsert failed; keeping promoted backup copy:', fullUpload.error);
                                    }
                                }).catch(function (bgErr) {
                                    console.warn('🎤 [DIRECT-UPLOAD] Background full-blob upsert error; keeping promoted backup copy:', bgErr);
                                });
                            }
                        }

                        // No backup yet (short recording) or the copy failed: upload
                        // the full blob synchronously, exactly as before.
                        if (!uploadResult.success) {
                            uploadResult = await uploadAudioDirectly(
                                recordedAudioBlob,
                                filename,
                                EXTERNAL_AUDIO_BUCKET_NAME,
                                null, // Let the function determine the URL
                                EXTERNAL_SUPABASE_SERVICE_KEY,
                                metadata
                            );
                        }

                        if (uploadResult.success) {
                            directUploadSuccess = true;
                            finalAudioUrl = uploadResult.url;
                            lastSavedCopilotAudioSlot = nextAudioIndex;
                            copilotPersistFolderYear = year;
                            copilotPersistFolderDate = date;
                            console.log('🎤 [DIRECT-UPLOAD] Direct upload succeeded:', finalAudioUrl);
                        } else {
                            console.warn('🎤 [DIRECT-UPLOAD] Direct upload failed:', uploadResult.error);
                            console.log('🎤 [DIRECT-UPLOAD] Falling back to parent window upload');
                        }
                    } else {
                        console.log('🎤 [DIRECT-UPLOAD] No Supabase credentials available, using parent window');
                    }
                    
                    // Prepare success message for parent
                    const successMessage = {
                        type: 'AUDIO_SAVED_SUCCESS',
                        patientId: patientId,
                        patientName: patientName,
                        audioUrl: finalAudioUrl,
                        finalAudioUrl: finalAudioUrl,
                        duration: finalDuration,
                        timestamp: new Date().toISOString(),
                        sessionId: recordingSessionId,
                        wasDirectUpload: directUploadSuccess,
                        liveTranscription: getLiveTranscriptionSetting(),
                        liveTranscriptText: outboundLiveText || '',
                        liveFactExtraction: false,
                        scheduledDate: scheduledDate,
                        patientScheduledDate: scheduledDate,
                        generateAiRecap: getAiRecapSetting(),
                        databaseUpdatedDirectly: directUploadSuccess,
                        clinicalAudioSlot: lastSavedCopilotAudioSlot,
                        storageFolderYear: copilotPersistFolderYear,
                        storageFolderDate: copilotPersistFolderDate,
                        storageSchemaName: EXTERNAL_DB_SCHEMA || 'public'
                    };
                    
                    if (directUploadSuccess) {
                        const savedAudioLabel = getExternalAudioLabel();
                        showLoading('Updating patient record...');
                        const patientAudioSave = await saveAudioToPatientRecordDirectly(
                            finalAudioUrl,
                            finalDuration,
                            recordedAudioBlob && recordedAudioBlob.type,
                            savedAudioLabel
                        );
                        if (!patientAudioSave.success) {
                            throw new Error(patientAudioSave.error || 'Audio uploaded but patient record update failed');
                        }

                        let transcriptMessage = '';
                        if (getLiveTranscriptionSetting()) {
                            const transcriptSave = await saveLiveTranscriptDirectly(
                                finalAudioUrl,
                                (outboundLiveText || '').trim() || (finalTranscript || ''),
                                finalDuration,
                                recordingSessionId,
                                savedAudioLabel
                            );
                            // Tell the parent so it can append the row itself when this failed.
                            successMessage.liveTranscriptSaved = !!transcriptSave.success;
                            if (!transcriptSave.success) {
                                console.warn('🎤 [DIRECT-UPLOAD] Live transcript direct save failed:', transcriptSave.error);
                                transcriptMessage = ' Live transcript was not saved: ' + (transcriptSave.error || 'Unknown error') + '.';
                            } else {
                                transcriptMessage = ' Live transcript saved.';
                                liveTranscriptAutoSaved = true;
                                void persistCopilotTabsSnapshot('after_transcript_save');
                            }
                        }

                        // Direct upload + DB update succeeded - show success and notify parent
                        console.log('🎤 [DIRECT-UPLOAD] Audio saved directly, notifying parent window');
                        savedAudioUrl = finalAudioUrl;
                        const transcriptForAi = (outboundLiveText || '').trim() || (finalTranscript || '').trim();
                        if (isTenantUser) {
                            showSuccess('Audio saved for ' + (patientName || 'this patient') + '.' + transcriptMessage);
                        } else {
                            showTranscriptionPrompt();
                        }
                        updateUI('saved');
                        setIsSaving(true);
                        // v1 "I'm in a hurry": fire AI immediately on the live transcript for ALL users
                        // (tenant + clinical) right after direct save, so SOAP renders without dialog gate.
                        if (transcriptForAi && (isTenantUser || COPILOT_ENABLED)) {
                            setAiTabsStatus('AI: triggered after direct save', '#2b6cb0');
                            runAiFromSavedTranscript(transcriptForAi);
                        } else if (!transcriptForAi) {
                            console.warn('🎤 [AI-TABS] No transcript text available right after direct save');
                            setAiTabsStatus('AI: skipped after direct save (no transcript)', '#b7791f');
                        }
                        
                        // Keep transcript buffers after save so AI recap/clinical tabs can consume
                        // the just-recorded text without racing a UI clear.
                        const tabStatus = document.getElementById('liveTranscriptTabStatus');
                        if (tabStatus) {
                            tabStatus.textContent = 'Idle';
                            tabStatus.style.color = '#4a5568';
                        }
                        
                        // Notify parent window if it still exists so it can refresh UI.
                        if (window.opener) {
                            window.opener.postMessage(successMessage, '*');
                            console.log('🎤 [DEBUG] Direct upload success notified to parent');
                        }
                    } else if (window.opener) {
                        if (recordedAudioBlob.size > POST_MESSAGE_BLOB_MAX_BYTES) {
                            throw new Error(
                                'Recording is too large to send to the main window (' +
                                formatRecorderByteLabel(recordedAudioBlob.size) +
                                '). Direct upload failed — enable direct upload to storage, or use Save & continue so each segment stays under ' +
                                formatRecorderByteLabel(POST_MESSAGE_BLOB_MAX_BYTES) + '.'
                            );
                        }
                        // Fall back to parent window upload
                        console.log('🎤 [DEBUG] Falling back to parent window upload');
                        window.opener.postMessage(audioData, '*');
                        console.log('🎤 [DEBUG] Audio data sent to parent window for processing');
                        showSuccess('Audio saved to ${saveTargetName.toLowerCase()} record successfully!');
                        updateUI('saved');
                        setIsSaving(true);
                        const transcriptForAi = (outboundLiveText || '').trim() || (finalTranscript || '').trim();
                        if (isTenantUser) {
                            if (transcriptForAi) {
                                setAiTabsStatus('AI: triggered after fallback save', '#2b6cb0');
                                runAiFromSavedTranscript(transcriptForAi);
                            } else {
                                console.warn('🎤 [AI-TABS] No transcript text available after fallback save');
                                setAiTabsStatus('AI: skipped after fallback save (no transcript)', '#b7791f');
                            }
                        } else if (COPILOT_ENABLED && transcriptForAi) {
                            setAiTabsStatus('AI: runs after transcription completes (or if you skip)', '#718096');
                        } else if (!transcriptForAi) {
                            console.warn('🎤 [AI-TABS] No transcript text available after fallback save');
                            setAiTabsStatus('AI: skipped after fallback save (no transcript)', '#b7791f');
                        }
                        // Keep transcript buffers after save so AI recap/clinical tabs can consume
                        // the just-recorded text without racing a UI clear.
                        const tabStatus = document.getElementById('liveTranscriptTabStatus');
                        if (tabStatus) {
                            tabStatus.textContent = 'Idle';
                            tabStatus.style.color = '#4a5568';
                        }
                    } else {
                        throw new Error('Parent window not available and direct upload failed');
                    }
                    
                } catch (error) {
                    console.error('🎤 [ERROR] Error in stopAndSaveRecording:', error);
                    showError('Failed to save recording: ' + error.message);
                    updateUI('download');
                    // Re-enable button on error so user can retry
                    setIsSaving(false);
                    hasFinalSaveBeenSent = false; // Reset flag on error to allow retry
                }
            }
            
            function closeWindow() {
                if (isRecording && !recordedAudioBlob) {
                    if (confirm('Recording is in progress. Are you sure you want to close?')) {
                        window.close();
                    }
                } else {
                    window.close();
                }
            }
            
            function exportToPDF() {
                console.log('📄 Exporting to PDF...');
                
                if (!recordedAudioBlob) {
                    showError('No recording available to export. Please record and save audio first.');
                    return;
                }
                
                // Send message to parent window to handle PDF export
                const exportData = {
                    type: 'EXPORT_TO_PDF',
                    patientId: patientId,
                    patientName: patientName,
                    audioBlob: recordedAudioBlob,
                    duration: timer.minutes * 60 + timer.seconds,
                    timestamp: new Date().toISOString(),
                    scheduledDate: scheduledDate,
                    liveTranscription: getLiveTranscriptionSetting(),
                    liveFactExtraction: false,
                    sessionId: recordingSessionId // Add session ID
                };
                
                console.log('📤 Sending export data:', {
                    patientId: exportData.patientId,
                    patientName: exportData.patientName,
                    duration: exportData.duration
                });
                
                if (window.opener) {
                    window.opener.postMessage(exportData, '*');
                    showSuccess('Generating PDF report...');
                } else {
                    showError('Unable to communicate with main application for PDF export.');
                }
            }
            
            function updateUI(state) {
                const startBtn = document.getElementById('startBtn');
                const pauseBtn = document.getElementById('pauseBtn');
                const resumeBtn = document.getElementById('resumeBtn');
                const stopBtn = document.getElementById('stopBtn');
                const status = document.getElementById('status');
                const recordGif = document.getElementById('recordGif');
                const liveTranscriptionCheckbox = document.getElementById('liveTranscriptionCheckbox');
                const includeSystemAudioCheckbox = document.getElementById('includeSystemAudioCheckbox');

                startBtn.classList.add('hidden');
                pauseBtn.classList.add('hidden');
                resumeBtn.classList.add('hidden');
                stopBtn.classList.add('hidden');

                const isActiveRecording = state === 'record' || state === 'paused';
                if (liveTranscriptionCheckbox) {
                    liveTranscriptionCheckbox.disabled = true;
                    liveTranscriptionCheckbox.checked = true;
                    liveTranscriptionCheckbox.style.cursor = 'not-allowed';
                    liveTranscriptionCheckbox.parentElement.style.opacity = '0.6';
                }
                if (includeSystemAudioCheckbox) {
                    includeSystemAudioCheckbox.disabled = isActiveRecording;
                    includeSystemAudioCheckbox.style.cursor = isActiveRecording ? 'not-allowed' : 'pointer';
                    includeSystemAudioCheckbox.parentElement.style.opacity = isActiveRecording ? '0.6' : '1';
                }
                const aiRecapCheckboxEl = document.getElementById('aiRecapCheckbox');
                if (aiRecapCheckboxEl) {
                    aiRecapCheckboxEl.disabled = true;
                    aiRecapCheckboxEl.checked = true;
                    aiRecapCheckboxEl.style.cursor = 'not-allowed';
                    aiRecapCheckboxEl.parentElement.style.opacity = '0.6';
                }
                
                switch (state) {
                    case 'prompt':
                        startBtn.classList.remove('hidden');
                        status.textContent = 'Ready to Record';
                        document.getElementById('sizeTracker').classList.add('hidden');
                        recordGif.style.opacity = '1';
                        recordGif.style.transform = 'scale(1)';
                        externalRecordingState = 'opened';
                        // Send state change to parent window
                        if (window.opener) {
                            window.opener.postMessage({
                                type: 'RECORDING_STATE_CHANGE',
                                state: 'opened'
                            }, '*');
                        }
                        break;
                        
                    case 'record':
                        pauseBtn.classList.remove('hidden');
                        stopBtn.classList.remove('hidden');
                        status.textContent = 'Recording...';
                        document.getElementById('sizeTracker').classList.remove('hidden');
                        recordGif.style.opacity = '1';
                        recordGif.style.transform = 'scale(1)';
                        externalRecordingState = 'recording';
                        // Send state change to parent window
                        if (window.opener) {
                            window.opener.postMessage({
                                type: 'RECORDING_STATE_CHANGE',
                                state: 'recording'
                            }, '*');
                        }
                        break;
                        
                    case 'paused':
                        resumeBtn.classList.remove('hidden');
                        stopBtn.classList.remove('hidden');
                        status.textContent = 'Paused';
                        document.getElementById('sizeTracker').classList.remove('hidden');
                        recordGif.style.opacity = '0.6';
                        recordGif.style.transform = 'scale(0.9)';
                        externalRecordingState = 'paused';
                        // Send state change to parent window
                        if (window.opener) {
                            window.opener.postMessage({
                                type: 'RECORDING_STATE_CHANGE',
                                state: 'paused'
                            }, '*');
                        }
                        break;
                        
                    case 'download':
                        startBtn.classList.remove('hidden');
                        stopBtn.classList.remove('hidden');
                        status.textContent = 'Recording Complete';
                        recordGif.style.opacity = '0.3';
                        recordGif.style.transform = 'scale(0.8)';
                        externalRecordingState = 'opened';
                        // Send state change to parent window
                        if (window.opener) {
                            window.opener.postMessage({
                                type: 'RECORDING_STATE_CHANGE',
                                state: 'opened'
                            }, '*');
                        }
                        break;
                        
                    case 'saved':
                        startBtn.classList.remove('hidden');
                        status.textContent = 'Saved to Patient';
                        document.getElementById('sizeTracker').classList.add('hidden');
                        recordGif.style.opacity = '0.3';
                        recordGif.style.transform = 'scale(0.8)';
                        externalRecordingState = 'opened';
                        // Send state change to parent window
                        if (window.opener) {
                            window.opener.postMessage({
                                type: 'RECORDING_STATE_CHANGE',
                                state: 'opened'
                            }, '*');
                        }
                        resetTimer();
                        break;
                        
                    case 'error':
                        startBtn.classList.remove('hidden');
                        status.textContent = 'Error';
                        document.getElementById('sizeTracker').classList.add('hidden');
                        recordGif.style.opacity = '0.3';
                        recordGif.style.transform = 'scale(0.8)';
                        externalRecordingState = 'opened';
                        // Send state change to parent window
                        if (window.opener) {
                            window.opener.postMessage({
                                type: 'RECORDING_STATE_CHANGE',
                                state: 'opened'
                            }, '*');
                        }
                        resetTimer();
                        break;
                }
            }
            
            function showLoading(message = 'Loading...') {
                const messages = document.getElementById('messages');
                messages.innerHTML = '<div class="loading"></div>' + message;
            }
            
            function hideLoading() {
                const messages = document.getElementById('messages');
                messages.innerHTML = '';
            }
            
            function showError(message) {
                const messages = document.getElementById('messages');
                messages.innerHTML = '<div class="error-message">' + message + '</div>';
            }
            
            function showSuccess(message) {
                const messages = document.getElementById('messages');
                messages.innerHTML = '<div class="success-message">' + message + '</div>';
            }
            
            function showWarning(message) {
                const messages = document.getElementById('messages');
                messages.innerHTML = '<div class="warning-message">' + message + '</div>';
            }
            
            function setIsSaving(isSaving) {
                const stopBtn = document.getElementById('stopBtn');
                if (stopBtn) {
                    if (isSaving) {
                        stopBtn.disabled = true;
                        stopBtn.innerHTML = '<div class="loading"></div>Saving...';
                    } else {
                        stopBtn.disabled = false;
                        stopBtn.innerHTML = 'Stop & Save to ${saveTargetName}';
                    }
                }
            }
            
            function showTranscriptionPrompt() {
                const prompt = document.getElementById('transcriptionPrompt');
                if (!prompt) return;

                const transcriptForPrompt = (
                    lastCapturedTranscript ||
                    transcriptText ||
                    latestLiveTranscriptText ||
                    ''
                ).trim();
                const hasLiveTranscript = getLiveTranscriptionSetting() && transcriptForPrompt.length > 0;
                const savedTitle = '<h3 style="margin-bottom: 0.5rem; color: #2c5282;">\\u2728 Audio saved for ' + (patientName || 'this patient') + '</h3>';

                // Tenant users (student, socialworker, other) don't get Corti — only live transcript
                if (isTenantUser) {
                    if (hasLiveTranscript && !liveTranscriptAutoSaved) {
                        prompt.innerHTML = savedTitle
                            + '<p style="color: #4a5568; margin-bottom: 0.5rem;">Live transcript captured. Save it to the patient record?</p>'
                            + '<div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">'
                            + '<button class="btn btn-success" onclick="saveLiveTranscript()" style="min-width: 160px;">Save Live Transcript</button>'
                            + '<button class="btn btn-light" onclick="skipTranscription()" style="min-width: 120px;">Close</button>'
                            + '</div>';
                    } else {
                        // Live transcript was auto-saved or not available - just show success
                        prompt.innerHTML = savedTitle
                            + '<p style="color: #4a5568; margin-bottom: 1.5rem;">'
                            + (liveTranscriptAutoSaved ? '\\u2713 Live transcript saved.' : 'Live transcript was not saved because no transcript text was captured.')
                            + '</p>'
                            + '<div style="display: flex; gap: 1rem; justify-content: center;">'
                            + '<button class="btn btn-light" onclick="skipTranscription()" style="min-width: 120px;">Close</button>'
                            + '</div>';
                    }
                } else {
                    // Clinical / non-tenant: Vosk in-popup (no Corti)
                    if (hasLiveTranscript && !liveTranscriptAutoSaved) {
                        prompt.innerHTML = savedTitle
                            + '<p style="color: #4a5568; margin-bottom: 0.5rem;">Live transcript captured. Save it to the patient record?</p>'
                            + '<p style="color: #718096; margin-bottom: 1.5rem; font-size: 0.9rem;">You can save the live transcript as-is, or run Vosk on the saved audio for a fresh on-device transcript (clinical tabs can use that text when copilot is on).</p>'
                            + '<div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">'
                            + '<button class="btn btn-success" onclick="saveLiveTranscript()" style="min-width: 160px;">Save Live Transcript</button>'
                            + '<button class="btn btn-info" onclick="requestTranscription()" style="min-width: 160px;">Transcribe with Vosk</button>'
                            + '</div>';
                    } else if (hasLiveTranscript && liveTranscriptAutoSaved) {
                        prompt.innerHTML = savedTitle
                            + '<p style="color: #4a5568; margin-bottom: 0.5rem;">\\u2713 Live transcript saved.</p>'
                            + '<p style="color: #718096; margin-bottom: 1.5rem; font-size: 0.9rem;">You can also run Vosk on the saved recording for an on-device transcript.</p>'
                            + '<div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">'
                            + '<button class="btn btn-info" onclick="requestTranscription()" style="min-width: 160px;">Transcribe with Vosk</button>'
                            + '<button class="btn btn-light" onclick="skipTranscription()" style="min-width: 120px;">Close</button>'
                            + '</div>';
                    } else {
                        prompt.innerHTML = savedTitle
                            + '<p style="color: #4a5568; margin-bottom: 1.5rem;">No live transcript was captured. Transcribe this audio on device with Vosk?</p>'
                            + '<div style="display: flex; gap: 1rem; justify-content: center;">'
                            + '<button class="btn btn-info" onclick="requestTranscription()" style="min-width: 120px;">Yes, Transcribe</button>'
                            + '<button class="btn btn-light" onclick="skipTranscription()" style="min-width: 120px;">No, Skip</button>'
                            + '</div>';
                    }
                }

                prompt.classList.remove('hidden');
                switchTab('liveTranscript');
            }
            
            function hideTranscriptionPrompt() {
                const prompt = document.getElementById('transcriptionPrompt');
                if (prompt) {
                    prompt.classList.add('hidden');
                }
            }
            
            function guessExtensionFromBlobMime(mime) {
                const m = String(mime || '').toLowerCase();
                if (m.indexOf('webm') >= 0) return 'webm';
                if (m.indexOf('wav') >= 0) return 'wav';
                if (m.indexOf('mpeg') >= 0 || m.indexOf('mp3') >= 0) return 'mp3';
                if (m.indexOf('mp4') >= 0 || m.indexOf('m4a') >= 0) return 'm4a';
                if (m.indexOf('ogg') >= 0) return 'ogg';
                return 'webm';
            }

            function requestTranscription() {
                console.log('🎤 [DEBUG] User requested Vosk transcription');
                hideTranscriptionPrompt();
                clearClinicalTabPanels('Transcribing…');

                const durationSeconds = timer.minutes * 60 + timer.seconds;
                const label = (typeof getExternalAudioLabel === 'function' ? getExternalAudioLabel() : '') || 'Recording';
                const audioUrl = savedAudioUrl || null;

                if (recordedAudioBlob && recordedAudioBlob.size > 0) {
                    const ext = guessExtensionFromBlobMime(recordedAudioBlob.type);
                    const file = new File([recordedAudioBlob], 'recording.' + ext, {
                        type: recordedAudioBlob.type || ('audio/' + ext),
                    });
                    const urlForMeta = audioUrl || URL.createObjectURL(recordedAudioBlob);
                    runTenantUploadTranscription(urlForMeta, file, durationSeconds, label).catch(function (err) {
                        console.error('🎤 [VOSK] requestTranscription failed:', err);
                    });
                    return;
                }

                if (audioUrl) {
                    requestTranscriptionForUrl(audioUrl, durationSeconds, label);
                    return;
                }

                showError('No audio available for transcription. Please try saving again.');
            }
            
            function skipTranscription() {
                console.log('🎤 [DEBUG] User skipped transcription');
                hideTranscriptionPrompt();
                showSuccess('Audio saved. Transcription skipped.');
                if (!isTenantUser && COPILOT_ENABLED) {
                    var tSkip = (buildCombinedLiveTranscriptForAi() || '').trim()
                        || (lastCapturedTranscript || '').trim()
                        || (transcriptText || '').trim()
                        || (latestLiveTranscriptText || '').trim();
                    if (tSkip) {
                        runAiFromSavedTranscript(tSkip);
                    }
                }
            }

            function saveLiveTranscript() {
                console.log('🎤 [DEBUG] User chose to save live transcript');
                hideTranscriptionPrompt();

                document.getElementById('processingState').classList.remove('hidden');
                document.getElementById('processingMessage').textContent = 'Saving live transcript...';

                (async function () {
                    const transcriptDuration = timer.minutes * 60 + timer.seconds;
                    const transcriptToSave = (
                        (buildLiveTranscriptTextForSave() || '').trim() ||
                        lastCapturedTranscript ||
                        transcriptText ||
                        latestLiveTranscriptText ||
                        ''
                    ).trim();
                    const directResult = await saveLiveTranscriptDirectly(
                        savedAudioUrl,
                        transcriptToSave,
                        transcriptDuration,
                        recordingSessionId
                    );

                    if (directResult.success) {
                        document.getElementById('processingState').classList.add('hidden');
                        liveTranscriptAutoSaved = true;
                        showSuccess(directResult.skipped ? 'Live transcript was already saved.' : 'Live transcript saved.');
                        if (window.opener) {
                            // alreadySaved: the parent only refreshes its UI —
                            // without it the parent appended a duplicate row.
                            window.opener.postMessage({
                                type: 'SAVE_LIVE_TRANSCRIPT',
                                alreadySaved: true,
                                patientId: patientId,
                                patientName: patientName,
                                audioUrl: savedAudioUrl,
                                transcriptText: transcriptToSave,
                                duration: transcriptDuration,
                                timestamp: new Date().toISOString(),
                                sessionId: recordingSessionId
                            }, '*');
                        }
                        return;
                    }

                    if (window.opener) {
                        window.opener.postMessage({
                            type: 'SAVE_LIVE_TRANSCRIPT',
                            patientId: patientId,
                            patientName: patientName,
                            audioUrl: savedAudioUrl,
                            transcriptText: transcriptToSave,
                            duration: transcriptDuration,
                            timestamp: new Date().toISOString(),
                            sessionId: recordingSessionId
                        }, '*');
                        console.log('🎤 [DEBUG] Direct transcript save failed, sent request to parent window');
                    } else {
                        showError('Failed to save live transcript: ' + (directResult.error || 'unknown error'));
                        document.getElementById('processingState').classList.add('hidden');
                    }
                })();
            }

            document.addEventListener('keydown', (event) => {
                if (event.ctrlKey || event.metaKey) {
                    switch (event.key) {
                        case ' ':
                            event.preventDefault();
                            if (isRecording && !isPaused) {
                                pauseRecording();
                            } else if (isRecording && isPaused) {
                                resumeRecording();
                            }
                            break;
                        case 's':
                            event.preventDefault();
                            if (recordedAudioBlob) {
                                stopAndSaveRecording();
                            }
                            break;
                        case 'r':
                            event.preventDefault();
                            if (!isRecording) {
                                startRecording();
                            }
                            break;
                    }
                }
            });
            
            updateUI('prompt');
            initializeLiveSettingsFromStorage();
            syncSystemAudioCheckboxState();
            showPickedMic();
            setLiveTranscriptionStatus(getLiveTranscriptionSetting() ? 'Idle' : 'Disabled');
            if (COPILOT_ENABLED) {
                setInterval(updateAiStatusDisplay, 1000);
            }
            window.__externalRecorderInitialized = true;

            // Find the chart row's schema now, so the rolling backups taken while
            // recording already sit in the folder Stop & Save promotes from.
            // Backups never wait on it; the save paths do.
            ensureRowSchema();

            // Warm up client-side STT as soon as popup bootstraps so first-record latency is lower.
            // This is intentionally non-blocking and only runs when live transcription is enabled.
            if (USE_CLIENT_SIDE_SYSTEM_STT && getLiveTranscriptionSetting()) {
                const scheduleWarmup =
                    (typeof window.requestIdleCallback === 'function')
                        ? window.requestIdleCallback
                        : (cb) => setTimeout(cb, 0);
                scheduleWarmup(() => {
                    ensureClientSideSystemStt().catch((err) => {
                        console.warn(
                            '🎤 [CLIENT-STT] Warmup failed:',
                            (err && err.message) ? err.message : err,
                        );
                    });
                });
            }
            
            // Periodically sync recording state with the parent window (every 5 seconds)
            setInterval(() => {
                if (window.opener) {
                    try {
                        window.opener.postMessage({
                            type: 'RECORDING_STATE_CHANGE',
                            state: externalRecordingState,
                            patientId: patientId,
                            sessionId: recordingSessionId
                        }, '*');
                    } catch (e) {
                        console.warn('🔄 [SYNC] Failed to send periodic state check:', e);
                    }
                }
            }, 5000);
            
            window.addEventListener('beforeunload', (event) => {
                // Clean up aggregate audio resources first
                if (aggregateCleanup) {
                    try { aggregateCleanup(); } catch (e) {}
                    aggregateCleanup = null;
                }
                
                // If recording is in progress, send emergency backup message to parent window
                // This ensures transcription is saved even if the window closes abruptly
                if (isRecording && window.opener) {
                    // Calculate duration from timer
                    const durationSeconds = (timer?.minutes || 0) * 60 + (timer?.seconds || 0);
                    
                    // Send emergency backup message to parent window
                    const emergencyData = {
                        type: 'RECORDING_ABRUPTLY_CLOSED',
                        patientId: patientId,
                        patientName: patientName,
                        ehrId: injectedEhrId || 'unknown_ehr',
                        sessionId: recordingSessionId,
                        timestamp: new Date().toISOString(),
                        scheduledDate: scheduledDate,
                        transcriptText: ((buildLiveTranscriptTextForSave() || '').trim() || transcriptText || ''),
                        duration: durationSeconds,
                        liveTranscription: getLiveTranscriptionSetting(),
                    };
                    
                    // Use try/catch to ensure we don't block the unload
                    try {
                        window.opener.postMessage(emergencyData, '*');
                        console.log('💾 [EXTERNAL] RECORDING_ABRUPTLY_CLOSED sent to parent window');
                    } catch (e) {
                        console.warn('💾 [EXTERNAL] Failed to send emergency backup message:', e);
                    }
                }
                
                // Show confirmation if recording without saved audio
                if (isRecording && !recordedAudioBlob) {
                    event.preventDefault();
                    event.returnValue = 'Recording in progress. Are you sure you want to leave?';
                }
            });
        </script>
    </body>
    </html>
  `;
  };

  const getDynamicWindowSize = () => {
    // More responsive window size calculation with aggressive minimums + 200px height increase
    const screenWidth = window.screen.width || window.innerWidth || 800;
    const screenHeight = window.screen.height || window.innerHeight || 600;

    // Account for browser UI and available space
    const availableWidth = Math.min(screenWidth - 100, screenWidth * 0.95);
    const availableHeight = Math.min(screenHeight - 100, screenHeight * 0.9);

    // Responsive sizing based on screen size (added 200px to all heights)
    let targetWidth, targetHeight;

    if (screenWidth < 480) {
      // Very small screens
      targetWidth = Math.max(280, Math.min(availableWidth, 320));
      targetHeight = Math.max(520, Math.min(availableHeight, 680)); // +200px
    } else if (screenWidth < 768) {
      // Mobile/tablet portrait
      targetWidth = Math.max(320, Math.min(availableWidth, 380));
      targetHeight = Math.max(580, Math.min(availableHeight, 750)); // +200px
    } else if (screenWidth < 1024) {
      // Tablet landscape
      targetWidth = Math.max(380, Math.min(availableWidth, 450));
      targetHeight = Math.max(620, Math.min(availableHeight, 800)); // +200px
    } else {
      // Desktop
      targetWidth = Math.max(420, Math.min(availableWidth, 520));
      targetHeight = Math.max(680, Math.min(availableHeight, 850)); // +200px
    }

    // Ensure minimum sizes for functionality with hard limits
    const minWidth = 280;
    const minHeight = 520; // +200px
    const maxWidth = Math.min(600, screenWidth - 40);
    const maxHeight = Math.min(900, screenHeight - 40); // Increased max height

    return {
      width: Math.max(minWidth, Math.min(targetWidth, maxWidth)),
      height: Math.max(minHeight, Math.min(targetHeight, maxHeight)),
    };
  };

  return {
    recordingState,
    isRecording,
    isPaused,
    timer,
    isLoading,
    isSaving,
    generateExternalHTML,
    getDynamicWindowSize,
  };
};
